// AI Photo Measure button.
//
// Mirrors the HOVER import button visually + behaviorally so contractors
// have a familiar workflow:
//   click → upload 2-8 phone photos (and optional reference dim) →
//   preview Claude's diff → Apply.
//
// The backend (/api/measure/ai-measure) returns the same `measurements`
// shape as HOVER, so we hand it to the same `onApply` callback the page
// already uses for HOVER.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, X, Check, Loader2, AlertTriangle, Camera, Upload, Ruler, RotateCcw, FileText, Printer, Bug, Lightbulb, ScanSearch, HelpCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import writeThrough from "@/lib/write_through";
import PhotoTakeoffEditor from "@/components/estimate/PhotoTakeoffEditor";
import {
  inchesPerPx as gableInchesPerPx, gableNetArea, crossCheckRidges, dormerNetArea,
} from "@/lib/gableMath";
// Iter 78z — Profile annotator: lets contractor draw boxes tagged
// Shake / B&B / etc. so the AI worker treats those regions as
// authoritative accent material. Both AI Measure + Blueprint share it.
import ProfileAnnotator from "@/components/estimate/ProfileAnnotator";
import AnnotatorErrorBoundary from "@/components/estimate/AnnotatorErrorBoundary";
import { renderAnnotated, describeAnnotations } from "@/lib/photoAnnotate";
// Iter 78s — HOVER-style elevation drawings, generated from the AI Measure
// raw_ai output.
// Iter 79j.12 — ElevationDrawing / Elevation3DPreview imports removed
// with the Elevation Drawings preview block. `buildElevationsFromAIMeasure`
// is still used further down for the report PDF sheet layout.
import { buildElevationsFromAIMeasure } from "@/lib/elevationBuilder";
// Iter 78z (P1.3) — Per-Elevation Breakdown card + "+ Add Accent" override
import PerElevationBreakdownCard from "@/components/estimate/PerElevationBreakdownCard";
import RunReadinessChecklist from "@/components/estimate/RunReadinessChecklist";
import { printTakeoff } from "@/lib/printTakeoff";
import { useAuth } from "@/lib/auth";

// SEND-213 (Howard ruled 2026-08-14) — "Keep the aerial code. Hide the
// button and the address hint from the contractor for now. Do not delete the
// feature." ONE CONSTANT, one place, and the feature is intact behind it:
// `fetchSatellite`, its route and its address rule are untouched and still
// tested. Flip this to show the button again.
const AERIAL_SHOWN = false;

// SEND-214 (Howard ruled 2026-08-14) — "Same rule as aerial. The contractor
// does not need that checklist on this screen. The checks stay in the code."
// ONE CONSTANT hides the RUN READINESS block and its 2/7 list. The component,
// all seven checks and the calibration / tape / wall-ref / profile logic are
// NOT deleted — flip this to show the block again.
const READINESS_SHOWN = false;

// SEND-246 (Howard ruled 2026-08-14) — "On the Photo Measure dialog, hide
// everything from Reference Marker Coverage down. This is hide, not delete.
// The code stays." SAME RULE AS AERIAL AND READINESS, ONE CONSTANT: it hides
// the reference-marker coverage tile and its four elevation cards, the resume
// / restore banner, the yellow "estimate, not a survey" banner, the whole
// results area, and the footer row — Powered by, Calibrate Window Sizing, Tag
// Profiles, Run AI Measure, Close and Start Over.
// WHAT STAYS ON SCREEN: the photo grid, Add Photo, AI May Read, Hand Only,
// Clear, Tips, and the X in the corner, which still closes the dialog.
// NOTHING IS DELETED: every block, every handler, `runMeasure`, `startOver`,
// `applyMeasurements`, the calibration inputs and the profile tagger are
// untouched on disk and still tested. Flip this to show the tail again.
const MEASURE_TAIL_SHOWN = false;

// SEND-247 (Howard ruled 2026-08-14) — "Hide the orange SAVED WORK FOUND
// banner. Do not delete the code." It sits ABOVE the SEND-246 cutoff, so 246
// left it on screen, and it only draws when a leftover AI run exists — but
// **START FRESH IS DESTRUCTIVE, AND THAT IS WHY IT COMES OFF**. Same pattern
// as 246: ONE FLAG, hide not delete. The header, RESUME and START FRESH go
// together; `resumeSession`, the start-fresh confirm and the session-recovery
// plumbing are untouched on disk and still tested. Flip this and it is back.
const RESUME_BANNER_SHOWN = false;

// SEND-251 (Howard ruled 2026-08-14) — HIDE THE BOTTOM BLOCK ON THE PHOTO
// MEASURE PICKER. The three lines he named — "NO PHOTO IS ON THIS RUN YET …",
// "The choice is recorded against the photo, not against the job …" and
// "STEP-BY-STEP WALK MOVED TO PHOTO TAKEOFF …" — all live in ONE block under
// the photo tiles, so one flag takes the block. The read-scope variant of the
// first line ("a read would look at 2 of 3 on this run") goes with it, by his
// word: with Run AI Measure hidden there is no run to scope.
// KEPT: the tiles, ADD PHOTO, AI MAY READ, HAND ONLY, CLEAR, TIPS, X.
// HIDE, NOT DELETE: the block, its testids and every word stay on disk, and
// the per-photo AI choice itself is not touched. One flag brings it back.
const PICKER_NOTE_SHOWN = false;

// Iter 111 — anchor integrity: the validated production configuration.
// Non-admin runs are clamped to this set server-side; the picker below
// mirrors the same policy so the UI never promises what the backend
// will refuse.
const VALIDATED_MODEL_KEY = "claude-fable-5";
const MODEL_PICKER_LABELS = {
  "claude-opus-4-5": "Claude Opus 4.5",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
  "claude-fable-5": "Claude Fable 5",
  "gemini-3.5-flash": "Gemini 3.5 Flash",
  "gemini-3.1-pro": "Gemini 3.1 Pro",
  "gpt-5.5": "GPT-5.5",
  "gpt-5.4": "GPT-5.4",
};
const ADMIN_ROLES = new Set(["owner", "supplier_admin", "admin"]);
// Iter 79j.22 — 3D House Model view (parametric Three.js render from raw_ai)
import HouseModel3D from "@/components/estimate/HouseModel3D";
import FieldVerifyCard from "@/components/estimate/FieldVerifyCard";
import { RENDER_3D_ENABLED } from "@/lib/featureFlags";
// Iter 79j.36 — Debug view: per-photo raw observations + reconciled
// house JSON with provenance. Behind Advanced.
import AIExtractionDebugModal from "@/components/estimate/AIExtractionDebugModal";

const ELEVATION_OPTIONS = [
  { key: "",            label: "Untagged" },
  { key: "front",       label: "Front" },
  { key: "front-left",  label: "Front-Left corner" },
  { key: "left",        label: "Left" },
  { key: "rear-left",   label: "Rear-Left corner" },
  { key: "back",        label: "Back" },
  { key: "rear-right",  label: "Rear-Right corner" },
  { key: "right",       label: "Right" },
  { key: "front-right", label: "Front-Right corner" },
  { key: "aerial",      label: "Aerial (satellite)" },
  { key: "detail",      label: "Detail" },
];
const annotEmpty = (a) =>
  !a || (!a.reference && !a.windowReference && (!a.zones || a.zones.length === 0) && (!a.elevation || a.elevation === "") && !a.targetPin && (!a.gables || a.gables.length === 0) && (!a.dormers || a.dormers.length === 0));

const KEY_LABELS = {
  siding_sqft: "Siding",
  siding_with_openings_sqft: "Siding (+openings)",
  opening_sqft: "Openings (ft²)",
  eaves_lf: "Eaves",
  rakes_lf: "Rakes",
  opening_count: "Openings",
  window_count: "Windows",
  entry_door_count: "Entry doors",
  patio_door_count: "Patio doors",
  garage_door_count: "Garage doors",
  opening_perimeter_lf: "Opening perimeter",
};
const fmt = (n) => Number(n || 0).toLocaleString();
const unitOf = (k) =>
  k.endsWith("_sqft") ? "ft²" : k.endsWith("_lf") ? "LF" : "";

export default function AIMeasureButton({ kind, onApply, address, overhangIn, estimateId, estimate, onRederived = null }) {
  const t = useT();
  const fileRef = useRef();
  // `files` is the locally-selected file objects (used for previews until
  // upload completes); `photoUrls` is the canonical server-side list that
  // survives across sessions. Once a file finishes uploading, the URL is
  // appended to photoUrls and the local File is discarded.
  const [files, setFiles] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]); // ["/api/uploads/<uuid>.jpg", …]
  // Iter 56: per-photo pre-AI annotations. Keyed by photo filename
  // (matches the value stored in photoUrls). Each entry holds:
  //   { elevation: "front"|"back"|"left"|"right"|"detail"|"",
  //     reference: { p1, p2, inches } | null,
  //     zones: Array<{ kind, category, points }> }
  // Annotations are burned into the photo via Canvas in runMeasure()
  // before sending to Claude, and described as text alongside.
  const [photoAnnotations, setPhotoAnnotations] = useState({});
  // SEND-131A / SEND-139 — the photo takeoff editor (marks ON the photo)
  // is the ONE drawing door on this screen. The annotator's own state
  // (annotateOpenFor / annotateGuided / refineOpen) went out with its
  // doors; the annotator is an import source and the Guided Capture
  // step, never a drawing UI here.
  const [takeoffOpenFor, setTakeoffOpenFor] = useState(null);
  // SEND-151 (Howard ruled 2026-08-30) — MEASURE WITHOUT AI. A photo on the
  // estimate is enough to draw on: this door opens the SAME editor with no
  // AI call, no annotate step and no proposals. Run AI Measure stays and
  // stays optional. `noAiPhotos` is the server's own list (nothing invented).
  const [noAiPhotos, setNoAiPhotos] = useState([]);
  // SEND-179 (Howard ruled 2026-08-14) — THE UNGATE. The server has always
  // written the reason there is nothing to draw on ("no photo is on this
  // estimate yet — upload one and this door opens"); the editor threw it
  // away and hid the button, which is the one thing this app does not do.
  // The sentence is the SERVER'S, verbatim — none is composed here.
  const [noAiNote, setNoAiNote] = useState("");
  const [noAiPickerOpen, setNoAiPickerOpen] = useState(false);
  const [takeoffNoAi, setTakeoffNoAi] = useState(false);
  // SEND-151 — the photos this estimate can be drawn on, straight from the
  // server. It calls nothing and spends nothing; an empty list hides the door.
  const loadNoAiPhotos = async () => {
    if (!estimateId) return;
    try {
      const { data } = await api.get(`/estimates/${estimateId}/photo-takeoff/photos`);
      setNoAiPhotos(data?.photos || []);
      setNoAiNote(data?.note || "");
    } catch { /* no door rather than a wrong door */ }
  };
  // SEND-188 ITEM 2 (Howard ruled 2026-08-14) — ONE DOOR, AND THE AI CHOICE
  // BELONGS TO THE PHOTO, NOT TO THE BUTTON HE HAPPENED TO PRESS.
  // `photo_takeoff_photo_prefs` was built at SEND-183 with no writer, by
  // instruction, and registered as such. THIS IS ITS WRITER.
  // ABSENCE IS A THIRD STATE AND STAYS ONE: a photo missing from this map
  // has NO CHOICE RECORDED — not hand-only, not AI-allowed — so the map is
  // never padded with a default here either. A default is a decision he
  // never made.
  const [photoPrefs, setPhotoPrefs] = useState({});
  const [prefBusy, setPrefBusy] = useState("");
  const loadPhotoPrefs = async () => {
    if (!estimateId) return;
    try {
      const { data } = await api.get(`/estimates/${estimateId}/photo-takeoff/photo-prefs`);
      setPhotoPrefs(data?.prefs || {});
    } catch { /* no map rather than an invented one */ }
  };
  const setPhotoAiPref = async (photoKey, allowed) => {
    if (!estimateId || !photoKey) return;
    setPrefBusy(photoKey);
    try {
      const { data } = await api.put(
        `/estimates/${estimateId}/photo-takeoff/photo-prefs`,
        { photo_key: photoKey, ai_allowed: allowed },
      );
      setPhotoPrefs((prev) => {
        const next = { ...prev };
        if (data?.pref) next[photoKey] = data.pref;
        else delete next[photoKey];
        return next;
      });
      // THE SERVER'S OWN SENTENCE, never one composed here.
      if (data?.note) toast.success(data.note);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That choice did not save — nothing was changed");
    } finally {
      setPrefBusy("");
    }
  };
  // SEND-265 (Howard ruled 2026-08-14) — THE TWO CONTROLS ON THIS DIALOG.
  // Each is ONE server call and the screen prints THE SERVER'S OWN SENTENCE,
  // including the named skips. Nothing here composes a count of its own.
  const [bulkBusy, setBulkBusy] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSkipped, setBulkSkipped] = useState([]);
  // ITEM 1 — HAND ONLY on every photo. It sets no photo to AI MAY READ (the
  // route takes no parameter to set one with), it runs NO read and it touches
  // no mark.
  const handOnlyAllPhotos = async () => {
    if (!estimateId) return;
    setBulkBusy("hand");
    setBulkSkipped([]);
    try {
      const { data } = await api.post(
        `/estimates/${estimateId}/photo-takeoff/hand-only-all`, {},
      );
      setBulkNote(data?.note || "");
      if (data?.note) toast.success(data.note);
      await loadNoAiPhotos();
      await loadPhotoPrefs();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || "that did not save — no photo was changed";
      setBulkNote(msg);
      toast.error(msg);
    } finally {
      setBulkBusy("");
    }
  };
  // ITEM 2 — APPLY ALL PHOTOS TO CATALOG. The ready photos land; a photo with
  // no scale or no confirmed marks is SKIPPED AND NAMED, in the server's
  // words, and the quantities are recomputed rather than added twice.
  const applyAllPhotosToCatalog = async () => {
    if (!estimateId) return;
    setBulkBusy("apply");
    try {
      const { data } = await api.post(
        `/estimates/${estimateId}/photo-takeoff/apply-all`, {},
      );
      setBulkNote(data?.note || "");
      setBulkSkipped(data?.skipped || []);
      if (data?.note) toast.success(data.note);
      // the estimate page re-reads its own catalog — the same callback the
      // photo editor's writes use, so one path refreshes the screen.
      if (onRederived) onRederived();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || "nothing was applied — the catalog was left as it was";
      setBulkNote(msg);
      setBulkSkipped([]);
      toast.error(msg);
    } finally {
      setBulkBusy("");
    }
  };
  // THE ONE WAY IN. Every entry to the editor goes through here, so `noAi`
  // stops being a function of WHICH BUTTON was pressed and becomes the
  // choice recorded against this photo — including on NEXT/PREV, where
  // walking onto a hand-only photo must not quietly re-open the AI side.
  // A photo with no choice recorded opens exactly as it does today.
  const openTakeoff = (photoKey) => {
    setNoAiPickerOpen(false);
    setTakeoffNoAi(photoPrefs[photoKey]?.ai_allowed === false);
    setTakeoffOpenFor(photoKey);
  };
  // SEND-144 (Howard ruled 2026-08-28) — THE PHOTOS AS READ. An
  // annotated photo is a DIFFERENT FILE from the one attached to the
  // estimate, and the read's findings — and now its starting zones —
  // live on the file the read actually read. These are those files, so
  // the zones are always reachable without restoring anything.
  const [readPhotos, setReadPhotos] = useState([]);
  // Amber nudge — annotations edited after the last run only fold into
  // the measurements on Re-run. Cleared on run success / Start Over.
  const [annotDirtySinceRun, setAnnotDirtySinceRun] = useState(false);
  // Iter 78z — Profile annotator modal (box-tag Shake / B&B regions).
  const [profileAnnotatorOpen, setProfileAnnotatorOpen] = useState(false);
  const [savedProfileAnnotations, setSavedProfileAnnotations] = useState({});
  // Iter 79j.74 — 3D snapshot → Customer Quote PDF. Tracks a save done
  // this session so the auto-capture doesn't refire after the first one.
  const [modelSnapshotSaved, setModelSnapshotSaved] = useState(false);
  const saveModelSnapshot = async (blob, meta) => {
    const fd = new FormData();
    fd.append("file", blob, "model3d.png");
    const { data } = await api.post("/uploads", fd);
    await api.put(`/estimates/${estimateId}/model3d-snapshot`, {
      url: data.url,
      unverified: !!meta?.unverified,
      fit_low: !!meta?.fit_low,
    });
    setModelSnapshotSaved(true);
    toast.success("3D model snapshot saved — it will appear on the Customer Quote PDF");
  };
  // Iter 56c — free aerial fetch via Esri World Imagery.
  const [satBusy, setSatBusy] = useState(false);
  const [resumePrompt, setResumePrompt] = useState(false); // shows banner
  // Iter 79j.64 — Fix 2: specifics for the loud recovery banner
  // (photo count / result / marker counts pulled from the server
  // session at detection time).
  const [pendingSessionMeta, setPendingSessionMeta] = useState(null);
  // Iter 79j.64 — Fix 3: destructive-action confirm state.
  // null | { kind: "start_over" | "start_fresh" }
  const [destructiveConfirm, setDestructiveConfirm] = useState(null);
  const [refDim, setRefDim] = useState("");
  const [wallHeight, setWallHeight] = useState("");
  const [sidingPct, setSidingPct] = useState("");
  // Iter 57g — optional course-counting calibration. If the contractor
  // tells us the brick course size or the siding exposure, Claude can
  // size windows by counting visible rows in the photo — far more
  // accurate than estimating pixel ratios. Defaults:
  //   • Brick: 8 in (standard 3-bricks-per-8" course w/ 3/8" mortar)
  //   • Siding D5: 5 in, D6: 6 in, Cedar Impressions: 7 in (default 5)
  // Blank = "don't pass to Claude"; user can also disable each
  // independently by emptying the field.
  const [brickCourse, setBrickCourse] = useState("");
  const [sidingExposure, setSidingExposure] = useState("");
  // Iter 79j.44 — Deep Dormer Scan removed. Two-phase Phase A/B
  // owns dormer detection end-to-end. Flag no longer sent to the
  // backend; toggle UI + state variable are gone.
  // Iter 79j.15 — A/B model dropdown. Persisted so a contractor's
  // last choice survives modal close/reopen. Keys must match the
  // backend `_MODEL_CHOICES` registry.
  const [modelChoice, setModelChoice] = useState(() => {
    try {
      return localStorage.getItem("aiMeasureModelChoice") || VALIDATED_MODEL_KEY;
    } catch {
      return VALIDATED_MODEL_KEY;
    }
  });
  const { user: authUser } = useAuth();
  const isModelAdmin = ADMIN_ROLES.has((authUser?.role || "").toLowerCase());
  useEffect(() => {
    // non-admins are clamped server-side anyway; keep the UI honest
    if (authUser && !isModelAdmin && modelChoice !== VALIDATED_MODEL_KEY) {
      setModelChoice(VALIDATED_MODEL_KEY);
    }
  }, [authUser, isModelAdmin, modelChoice]);
  useEffect(() => {
    try { localStorage.setItem("aiMeasureModelChoice", modelChoice); } catch { /* ignore */ }
  }, [modelChoice]);
  // SEND-151 — load the no-AI photo list on mount and after an upload or a
  // takeoff session, so the door appears the moment a photo exists.
  useEffect(() => { loadNoAiPhotos(); loadPhotoPrefs(); }, [estimateId, photoUrls.length, takeoffOpenFor]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Iter 79j.16 — Model Comparison history state moved below `open`
  // and `preview` state declarations to avoid TDZ (state hooks
  // referenced by the effect must exist first).
  // Iter 57h — popover state for the inline "📐 Calibrate window sizing"
  // mini-panel that hangs next to the Run AI Measure button.
  const [calibOpen, setCalibOpen] = useState(false);
  // Iter 79j.11 — pre-Guided-Capture calibration prompt. Fires when
  // the contractor taps "Guided Capture" so they set the CURRENT
  // siding exposure BEFORE the first photo (contractor is at the
  // house, has line of sight to the wall — ideal moment). Does NOT
  // override the Wall / Window Reference lines the contractor draws
  // in-photo; it's an additional prompt hint to Claude on walls
  // where a red/blue reference line isn't reachable per opening.
  const [busy, setBusy] = useState(false);
  // Iter 57q — when AI Measure is running, show the worker's current
  // stage ("claude" → "dormer_scan" → "aggregating" → "mapping") in the
  // Run button so the contractor knows progress is happening. Empty
  // string when idle.
  const [busyStage, setBusyStage] = useState("");
  // Iter 79j.60 — Live per-photo progress from the two-phase pipeline.
  // Populated by every /status/{run_id} poll from `phase_a_progress`.
  // Rendered as a contractor-plain HUD (photo dots + plain-english
  // status line — no wave/proxy/phase jargon per Howard 2026-07-07).
  // `null` = pipeline hasn't started or is single-call.
  const [photoProgress, setPhotoProgress] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null); // {measurements, raw_ai}
  // Iter 79j.22 — Preview / 3D Model tab toggle inside the results block.
  // Auto-resets to "preview" whenever a new run lands so contractors see
  // the numbers first, then choose to switch to 3D for structural review.
  const [previewTab, setPreviewTab] = useState("preview");
  useEffect(() => {
    if (preview) setPreviewTab("preview");
  }, [preview?.run_id, preview?.session_id, preview?.model]);
  // Iter 79j.16 — Model Comparison history. Refetched whenever the
  // preview flips to a new run OR the modal opens. Empty until at
  // least one "done" run exists for this estimate. Declared here so
  // both `open` and `preview` are in scope (avoids TDZ).
  const [modelHistory, setModelHistory] = useState([]);
  // SEND-162 — the panel STATES ITS WINDOW (server-authored). A working
  // surface that silently loses rows as they age is the defect.
  const [modelHistoryWindow, setModelHistoryWindow] = useState("");
  useEffect(() => {
    if (!estimateId || !open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/measure/ai-measure/history/${estimateId}?limit=5`);
        if (!cancelled) {
          setModelHistory(Array.isArray(data?.runs) ? data.runs : []);
          setModelHistoryWindow(data?.window_label || "");
        }
      } catch {
        if (!cancelled) setModelHistory([]);
      }
    })();
    return () => { cancelled = true; };
    // Re-fetch every time a fresh preview lands (new run completed).
  }, [estimateId, open, preview?.session_id]);
  // Iter 57r — Resume support. When the modal opens we ask the
  // backend for the most recent AI Measure run for this estimate
  // (regardless of status). If it's still "running" or finished within
  // the last 30 minutes, we surface a small banner that lets the
  // contractor pick up where they left off after a page reload or
  // screen lock — no re-uploading photos, no re-running Claude.
  const [lastRun, setLastRun] = useState(null);  // { run_id, status, stage, age_seconds, photo_paths, result }
  // Iter 78z (Cross-Check) — track the active run's ID so the
  // PerElevationBreakdownCard can fire the cross-check endpoint.
  // Populated by both the fresh-run path and the resume path.
  const [currentRunId, setCurrentRunId] = useState(null);
  // RULING B (2026-07-26): "quote gables/dormers as shake" are a thin
  // alias over the per-elevation breakdown — the toggle sets the
  // surfaces' profile to shake and re-derives lines via /measure/map
  // (the conventions path). The Iter 51/52 flat swap math is retired.
  const [quoteGablesAsShake, setQuoteGablesAsShake] = useState(false);
  const [quoteDormersAsShake, setQuoteDormersAsShake] = useState(false);
  // Iter 47: contractor can override Claude's wall geometry inline.
  // Tracks whether walls were edited so apply() refreshes lines via
  // /measure/map (otherwise the pre-rolled lines are reused).
  const [wallsDirty, setWallsDirty] = useState(false);
  // Iter 57: hide Refine on Photo behind an Advanced Tools toggle.
  // Now that pre-AI annotations (Iter 56) cover most use cases, Refine
  // on Photo is the rare-case escape hatch — keep it accessible but out
  // of the primary flow.
  // Iter 79j.36 — Debug modal open state. Advanced-only. Shows per-
  // photo raw observations + the reconciled house JSON side-by-side,
  // so contractors can diagnose whether variance between runs is a
  // detection problem (photos disagree) or a reconciliation problem
  // (photos agree but the merge drifts).
  const [debugOpen, setDebugOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try {
      return localStorage.getItem("aiMeasureShowAdvanced") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("aiMeasureShowAdvanced", showAdvanced ? "1" : "0");
    } catch { /* ignore */ }
  }, [showAdvanced]);

  // Iter 79j.57c — First-run onboarding checklist. Explains the marker
  // SOP so contractors don't ship 8-photo runs that only mark corners
  // (which the reconciler treats as low-confidence). localStorage
  // remembers the dismissal so it doesn't nag every session, but the
  // in-modal Tips button re-opens it on demand.
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      return localStorage.getItem("aiMeasureOnboardingSeen") !== "1";
    } catch {
      return true;
    }
  });
  const dismissOnboarding = () => {
    try { localStorage.setItem("aiMeasureOnboardingSeen", "1"); } catch { /* ignore */ }
    try { localStorage.setItem("aiMeasureUnanchoredNudgeSeen", "1"); } catch { /* ignore */ }
    setShowOnboarding(false);
    setUnanchoredNudgeActive(false);
  };

  // Iter 79j.61 — Contextual accuracy nudge. Howard 2026-07-07: once
  // scale-refs plumbing is live, auto-trigger the checklist the FIRST
  // time a contractor uploads photos WITHOUT any `_scale_refs`, so
  // the warning lands at the exact moment the accuracy risk exists.
  // Uses a SEPARATE localStorage key from the first-run nudge so
  // dismissing "show once" doesn't also dismiss "you have photos but
  // no markers".
  const hasAnyScaleRef = useMemo(() => {
    const refs = photoAnnotations?._scale_refs;
    return refs && typeof refs === "object" && Object.keys(refs).length > 0;
  }, [photoAnnotations]);
  const [unanchoredNudgeActive, setUnanchoredNudgeActive] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (photoUrls.length === 0) return;      // no photos → nothing to nudge
    if (hasAnyScaleRef) return;               // markers present → healthy
    let seen = false;
    try { seen = localStorage.getItem("aiMeasureUnanchoredNudgeSeen") === "1"; }
    catch { seen = false; }
    if (seen) return;
    setShowOnboarding(true);
    setUnanchoredNudgeActive(true);
  }, [open, photoUrls.length, hasAnyScaleRef]);

  // Iter 79j.57d — Re-run confirmation. Howard buried a graduating run
  // once with a stray click; a confirmation dialog now guards ONLY the
  // done+reconciled case (dismissing on failed runs would just train
  // click-through). The dialog is intentionally SPECIFIC — "N dormers,
  // N sqft" instead of "are you sure?" — because generic warnings get
  // reflexively dismissed.
  const [rerunConfirm, setRerunConfirm] = useState(false);
  const attemptRerun = () => {
    const ra = preview?.raw_ai || null;
    const hasGoodReconciled = !!(ra
      && !ra._reconciliation_error
      && (Array.isArray(ra.walls) ? ra.walls.length : 0) > 0);
    if (hasGoodReconciled) {
      setRerunConfirm(true);
      return;
    }
    runMeasure();
  };

  // Iter 57d — Window styles dropdown. Kept in display order grouped
  // by category so the contractor can scan it quickly. Empty option
  // at the top means "not a window / not known yet".
  const WINDOW_STYLES = [
    { value: "", label: "— Select / N/A —" },
    { value: "Double Hung", label: "Double Hung" },
    { value: "Single Hung", label: "Single Hung" },
    { value: "Casement", label: "Casement" },
    { value: "Twin Casement", label: "Twin Casement" },
    { value: "Awning", label: "Awning" },
    { value: "Hopper", label: "Hopper" },
    { value: "2-Lite Slider", label: "2-Lite Slider (XO)" },
    { value: "3-Lite Slider", label: "3-Lite Slider (XOX)" },
    { value: "Picture", label: "Picture / Fixed" },
    { value: "Twin Double Hung", label: "Twin Double Hung" },
    { value: "Twin Single Hung", label: "Twin Single Hung" },
    { value: "Triple Double Hung", label: "Triple Double Hung" },
    { value: "Bay Window", label: "Bay Window" },
    { value: "Bow Window", label: "Bow Window" },
    { value: "Half-Round", label: "Half-Round" },
    { value: "Quarter-Round", label: "Quarter-Round" },
    { value: "Arch", label: "Arch / Eyebrow" },
    { value: "Octagon", label: "Octagon" },
    { value: "Hexagon", label: "Hexagon" },
    { value: "Garden Window", label: "Garden Window" },
    { value: "Other Shape", label: "Other / Custom Shape" },
  ];

  // Update the AI-detected style for one opening_schedule row. We mutate
  // the saved preview.measurements._ai_openings_schedule so the change
  // sticks across re-renders + the autosave hook pushes it back to the
  // ai_measure_sessions doc. Also propagates to preview.raw_ai.openings
  // (best-effort match by wall+size+type) so the Apply Measurements
  // step uses the corrected style when populating Vero rows.
  const updateOpeningStyle = (elev, type, sizeLabel, w, h, newStyle) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const m = prev.measurements || {};
      const sched = m._ai_openings_schedule || [];
      const nextSched = sched.map((row) => {
        if (
          (row.elevation || "").toLowerCase() === (elev || "").toLowerCase() &&
          (row.type || "").toLowerCase() === (type || "").toLowerCase() &&
          (row.size_label || "") === (sizeLabel || "") &&
          Math.round(Number(row.width_in) || 0) === Math.round(Number(w) || 0) &&
          Math.round(Number(row.height_in) || 0) === Math.round(Number(h) || 0)
        ) {
          return { ...row, style: newStyle };
        }
        return row;
      });
      // Also propagate to raw_ai.openings so Apply uses the new style.
      const raw = prev.raw_ai || {};
      const rawOps = (raw.openings || []).map((op) => {
        const sameWall = (op.wall || "").toLowerCase() === (elev || "").toLowerCase();
        const sameType = (op.type || "").toLowerCase() === (type || "").toLowerCase();
        const sameW = Math.round(Number(op.width_in) || 0) === Math.round(Number(w) || 0);
        const sameH = Math.round(Number(op.height_in) || 0) === Math.round(Number(h) || 0);
        if (sameWall && sameType && sameW && sameH) {
          return { ...op, style: newStyle };
        }
        return op;
      });
      return {
        ...prev,
        measurements: { ...m, _ai_openings_schedule: nextSched },
        raw_ai: { ...raw, openings: rawOps },
      };
    });
  };


  // Hits /api/measure/report-pdf with the current estimate_id; backend
  // reads the saved session and renders a 1–2 page report with photos,
  // confidence chips, openings schedule, and notes.
  const [reportBusy, setReportBusy] = useState(false);
  // Iter 79j.12 — show3DPreview state removed with the Elevation
  // Drawings preview block.
  const downloadReportPdf = async () => {
    if (!estimateId) {
      toast.error("Save the estimate first — the report needs an estimate ID");
      return;
    }
    setReportBusy(true);
    try {
      const res = await api.post(
        "/measure/report-pdf",
        { estimate_id: estimateId },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${estimateId.slice(0, 8)}-measurement.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Measurement report downloaded");
    } catch (err) {
      // Blob responses make the JSON error invisible — decode manually
      let detail = err?.response?.data?.detail || err?.message || "Report failed";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text)?.detail || text || detail;
        } catch { /* ignore */ }
      }
      toast.error(detail);
    } finally {
      setReportBusy(false);
    }
  };

  // Apply Howard's geometry math to the edited wall list and update
  // siding_sqft / gable / dormer totals on the preview in-place. Mirrors
  // backend `_aggregate_to_hover_shape` so the headline number tracks
  // every keystroke without a round-trip.
  const recomputeFromWalls = (walls) => {
    let sidingSqft = 0;
    let gableSqft = 0;
    let dormerSqft = 0;
    for (const w of walls) {
      // BLOCK A — NO PHOTO, NO WALL (Howard ruled 2026-08-27, SEND-136).
      // A face the backend refused for want of a photo contributes
      // NOTHING here either. This live recompute is a second money
      // surface: if it kept summing the mirrored faces, the headline
      // would put back exactly the ft² the read refused.
      if (w._refused) continue;
      const width = Number(w.width_ft) || 0;
      const eave = Number(w.height_ft) || 0;
      const gross = width * eave;
      let pct = Number(w.siding_pct_this_wall);
      if (!pct || pct <= 0) pct = 100;
      if (pct > 100) pct = 100;
      sidingSqft += gross * (pct / 100);
      const gableH = Number(w.gable_triangle_height_ft) || 0;
      // SEND-137 — THE GABLE RULING (Howard, 2026-08-27). ½ × width × rise
      // IS the gable wall. This panel always used ½; the backend used a
      // 0.70 field factor, so the number on screen and the number in the
      // database disagreed by ~40% of every gable. The 0.70 factor is
      // RETIRED: both surfaces now measure the triangle, and where the
      // triangle is not measured NEITHER writes an area (an untraced gable
      // refuses — the backend names the refusal, this panel adds nothing).
      if (gableH > 0 && width > 0) gableSqft += 0.5 * width * gableH;
      dormerSqft += Number(w.dormer_face_sqft) || 0;
    }
    sidingSqft += gableSqft + dormerSqft;
    return {
      siding_sqft: Math.round(sidingSqft * 10) / 10,
      _ai_gable_sqft: Math.round(gableSqft * 10) / 10,
      _ai_dormer_sqft: Math.round(dormerSqft * 10) / 10,
    };
  };

  // Edit one cell on one wall and recompute totals so the headline
  // sqft figure on the preview shifts immediately.
  const setWall = (idx, key, val) => {
    setPreview((p) => {
      if (!p?.raw_ai?.walls) return p;
      const walls = p.raw_ai.walls.map((w, i) =>
        i === idx ? { ...w, [key]: val === "" ? 0 : Number(val) } : w
      );
      const totals = recomputeFromWalls(walls);
      return {
        ...p,
        raw_ai: { ...p.raw_ai, walls },
        measurements: {
          ...p.measurements,
          siding_sqft: totals.siding_sqft,
          siding_with_openings_sqft: totals.siding_sqft,
          _ai_gable_sqft: totals._ai_gable_sqft,
          _ai_dormer_sqft: totals._ai_dormer_sqft,
        },
      };
    });
    setWallsDirty(true);
  };

  // Iter 79j.66 — Edit a dormer's WIDTH or KNEE height straight from the
  // Wall Breakdown and have the wall's dormer face ft² recompute itself.
  // Correcting a dormer used to mean knowing the face-area formula by
  // heart (face ft² includes cheeks/shed-rise beyond w×knee); instead we
  // RESCALE the AI's ft² by the ratio of the w×knee products, which
  // preserves the AI's geometry factor while making the correction
  // formula-free. Editing here mutates `raw_ai.dormers` — the same
  // object the 3D sidebar reads — so the model updates in lock-step.
  const setDormerDims = (dormerIdx, wallIdx, key, val) => {
    setPreview((p) => {
      if (!p?.raw_ai?.dormers?.[dormerIdx] || !p?.raw_ai?.walls?.[wallIdx]) return p;
      const num = val === "" ? 0 : Number(val);
      const wallLabel = ((p.raw_ai.walls[wallIdx].label || "")).toLowerCase();
      const product = (d) =>
        (Number(d.width_ft) || 0) * (Number(d.knee_wall_height_ft) || 0);
      const matched = p.raw_ai.dormers
        .map((d, j) => ({ d, j }))
        .filter(({ d }) => (d.face || "").toLowerCase() === wallLabel);
      const before = matched.reduce((a, { d }) => a + product(d), 0);
      const dormers = p.raw_ai.dormers.map((d, j) =>
        j === dormerIdx ? { ...d, [key]: num } : d
      );
      const after = matched.reduce(
        (a, { j }) => a + product(dormers[j]), 0
      );
      const walls = p.raw_ai.walls.map((w, i) => {
        if (i !== wallIdx) return w;
        const sqft = Number(w.dormer_face_sqft) || 0;
        const next = before > 0 && sqft > 0
          ? sqft * (after / before)   // preserve AI's cheek/rise factor
          : after;                    // no basis — fall back to Σ w×knee
        return { ...w, dormer_face_sqft: Math.round(next * 10) / 10 };
      });
      const totals = recomputeFromWalls(walls);
      return {
        ...p,
        raw_ai: { ...p.raw_ai, walls, dormers },
        measurements: {
          ...p.measurements,
          siding_sqft: totals.siding_sqft,
          siding_with_openings_sqft: totals.siding_sqft,
          _ai_gable_sqft: totals._ai_gable_sqft,
          _ai_dormer_sqft: totals._ai_dormer_sqft,
        },
      };
    });
    setWallsDirty(true);
  };

  // Edit any of the linear-measurement fields (eaves, rakes, starter,
  // corners, opening perimeter) inline. ISS soffit/gutter/etc. and the
  // siding-flow soffit/J-channel rows all derive their qty from these,
  // so a one-line override here propagates everywhere through the
  // /measure/map refresh on Apply.
  const setMeasurementField = (key, val) => {
    setPreview((p) => {
      if (!p?.measurements) return p;
      return {
        ...p,
        measurements: {
          ...p.measurements,
          [key]: val === "" ? 0 : Number(val),
        },
      };
    });
    setWallsDirty(true);
  };


  // ------------------------------------------------------------------
  // Server-side session persistence (Iter 50).
  // ------------------------------------------------------------------
  // On first modal open for this estimate, check for an existing session
  // and offer the contractor a Resume / Start Over choice. Without an
  // estimateId we just skip persistence entirely (e.g. ISS new-quote
  // flow before the doc has been saved).
  const [sessionChecked, setSessionChecked] = useState(false);
  // STEP 4 confidence gate — contractor acknowledgment (resets per run).
  const [gateAck, setGateAck] = useState(false);
  useEffect(() => {
    if (!estimateId || !open || sessionChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/measure/sessions/${estimateId}`);
        if (cancelled) return;
        // If the modal already has fresh state (user just selected files
        // before we finished the GET), don't clobber. Otherwise prompt.
        const hasFreshState = photoUrls.length > 0 || preview != null;
        if (!hasFreshState && (data.photo_urls?.length || data.preview)) {
          setResumePrompt(true);
          // Iter 79j.64 — Fix 2: capture WHAT the server holds so the
          // recovery banner can be specific instead of generic.
          const pa = data.photo_annotations || {};
          setPendingSessionMeta({
            photos: data.photo_urls?.length || 0,
            hasResult: !!data.preview,
            annotated: Object.keys(pa).filter(
              (k) => !k.startsWith("_") && pa[k] && Object.keys(pa[k]).length > 0
            ).length,
          });
          // Stash for the Resume button to consume.
          window.__aiMeasurePendingSession = data;
        }
      } catch {
        // 404 — no session, normal first run.
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, open, sessionChecked, photoUrls.length, preview]);

  // Iter 78z — Load saved profile annotations for this estimate.
  useEffect(() => {
    if (!estimateId || !open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/estimates/${estimateId}/profile-annotations`);
        if (!cancelled) setSavedProfileAnnotations(data?.annotations || {});
      } catch {
        // No annotations yet — that's fine
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId, open]);

  // Iter 57r — Resume support. On modal open, fetch the most recent
  // AI Measure run for this estimate. If it's still "running" or
  // finished within the last 30 min, set `lastRun` so the banner
  // surfaces. The actual Resume / Restore button click is wired below.
  useEffect(() => {
    if (!estimateId || !open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/measure/ai-measure/latest-for-estimate/${estimateId}`);
        if (cancelled) return;
        const r = data?.run || null;
        if (!r) { setLastRun(null); return; }
        // Done runs never expire — a paid AI read stays restorable for
        // the life of the estimate. Running runs always surface; only
        // errored runs stay fresh (< 30 min) so old failures don't nag.
        if (r.status === "done" || r.status === "running" || (r.age_seconds || 0) < 30 * 60) {
          setLastRun(r);
          if (r.status === "done" && r.photo_paths) {
            setReadPhotos(String(r.photo_paths).split(",").map((x) => x.trim()).filter(Boolean));
          }
        } else {
          setLastRun(null);
        }
      } catch {
        setLastRun(null);
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId, open]);

  // Iter 57r — handler: resume polling an in-flight run.
  const _applyAIResult = (data, status) => {
    // Resume path: load the preview directly. The full per-wall
    // recompute + auto-elevation-tagging that the fresh-run path
    // performs assumes the contractor was watching the run live; on
    // resume we trust whatever Claude returned and let them re-edit.
    // Iter 79j.37 — Thread the poll `status` payload's per-photo
    // extractions + pipeline label into the preview object so the
    // Debug view can show ACTUAL Phase A data.
    // Iter 79j.52 — Stamp run_id into the preview so the session
    // persistence flow saves it, and so a resumed failed session
    // can wire the Retry Reconciliation button back to the correct
    // run doc.
    const runIdForPreview = status?.run_id || currentRunId || data?.run_id || null;
    const enriched = status
      ? { ...data, raw_per_photo: status.raw_per_photo, pipeline: status.pipeline, run_id: runIdForPreview }
      : { ...data, run_id: runIdForPreview };
    setPreview(enriched);
  };

  // Iter 79j.51 — Reconcile-only retry. When Phase A succeeded but
  // Phase B (LLM reconciliation) 502'd on the proxy, we can re-run
  // JUST Phase B against the saved raw_per_photo — costing pennies
  // instead of paying for vision extraction again. Polls the same
  // run_id (backend re-uses the run doc, flips status back to
  // running / stage=reconciling).
  const retryReconcileOnly = async (runId) => {
    if (!runId) {
      toast.error("No run to reconcile — try Retry Run instead");
      return;
    }
    setBusy(true);
    setBusyStage("reconciling");
    setRunError(null);
    setRunErrorMeta(null);
    const t0 = Date.now();
    try {
      const launch = await api.post(`/measure/ai-measure/reconcile-only/${runId}`);
      if (!launch?.data?.run_id) throw new Error("Backend didn't accept the reconcile-only retry");
      let result = null;
      let finalStatus = null;
      // Ceiling policy alignment (ruled 2026-07-17): the server's Phase B
      // ceiling is 900s (AI_MEASURE_RECONCILE_CEILING) — the canonical
      // complex-house reconcile took 327s and 48k-token runs go longer.
      // The old 240s client budget expired before the server finished.
      for (let i = 0; i < 320; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        let statusResp;
        try {
          statusResp = await api.get(`/measure/ai-measure/status/${runId}`);
        } catch (e) {
          if (i >= 5) console.warn("reconcile-only status poll failed", e?.message);
          continue;
        }
        const s = statusResp?.data || {};
        if (s.stage && s.stage !== busyStage) setBusyStage(s.stage);
        if (s.phase_a_progress) setPhotoProgress(s.phase_a_progress);
        if (s.status === "error") throw new Error(s.error || "Reconciliation retry failed");
        if (s.status === "done") { result = s.result; finalStatus = s; break; }
      }
      if (!result) throw new Error("Reconciliation retry timed out — the server may still be finishing in the background");
      _applyAIResult(result, finalStatus);
      toast.success("Reconciliation retry complete · measurements applied");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Reconciliation retry failed";
      setRunError(String(msg));
      setRunErrorMeta({
        stage: "reconciling",
        elapsedMs: Date.now() - t0,
        kind: e?.response?.status === 502 ? "BadGateway" : "ReconcileRetryFailed",
      });
    } finally {
      setBusy(false);
      setBusyStage("");
    }
  };

  // Iter 78z+ — Re-fire AI Measure using cached photo bytes server-side
  // (no re-upload). Triggered from ProfileAnnotator's "Save & Re-run".
  // Mirrors `resumeRunPolling` but kicks off a fresh worker first.
  const rerunWithAnnotations = async () => {
    if (!currentRunId) {
      toast.error("No previous AI Measure run to re-fire — upload photos first");
      return;
    }
    setBusy(true);
    setBusyStage("starting");
    try {
      // Iter 79j.35 — Pass the current "Powered by" dropdown selection
      // so Re-Run honors the model the contractor picked. Prior version
      // silently reused the original run's model, defeating A/B testing.
      // Iter 79j.67(a) — also pass the CURRENT calibration values.
      // Legacy run docs predate calibration persistence, and the
      // contractor may have updated the Calibrate popover between runs —
      // body values win over the previous run's stored ones.
      const rerunBody = {
        ...(modelChoice ? { model_choice: modelChoice } : {}),
        ...(brickCourse && parseFloat(brickCourse) > 0
          ? { brick_course_in: parseFloat(brickCourse) } : {}),
        ...(sidingExposure && parseFloat(sidingExposure) > 0
          ? { siding_exposure_in: parseFloat(sidingExposure) } : {}),
      };
      const launch = await api.post(
        `/measure/ai-measure/rerun/${currentRunId}`,
        rerunBody,
      );
      const newRunId = launch?.data?.run_id;
      if (!newRunId) throw new Error("Backend didn't return a new run_id");
      setCurrentRunId(newRunId);
      setBusyStage(launch?.data?.stage || "starting");
      // Poll the new run to completion. Iter 79j.48 — bumped 100→200
      // iterations (300s→600s) so client budget exceeds server worst
      // case (Phase A 300s + drain 5s + Phase B 180s ≈ 485s).
      let result = null;
      let finalStatus = null;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        let statusResp;
        try {
          statusResp = await api.get(`/measure/ai-measure/status/${newRunId}`);
        } catch (e) {
          if (i >= 5) console.warn("ai-measure rerun status poll failed", e?.message);
          continue;
        }
        const s = statusResp?.data || {};
        if (s.stage && s.stage !== busyStage) setBusyStage(s.stage);
        if (s.phase_a_progress) setPhotoProgress(s.phase_a_progress);
        if (s.status === "error") throw new Error(s.error || "AI measure re-run failed");
        if (s.status === "done") { result = s.result; finalStatus = s; break; }
      }
      if (!result) throw new Error("Re-run did not complete within 10 minutes — the server may still be finishing in the background");
      _applyAIResult(result, finalStatus);
      toast.success("Re-run complete · annotations applied to materials list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Re-run failed");
    } finally {
      setBusy(false);
      setBusyStage("");
    }
  };

  const resumeRunPolling = async () => {
    if (!lastRun || !lastRun.run_id) return;
    setBusy(true);
    setBusyStage(lastRun.stage || "running");
    setCurrentRunId(lastRun.run_id);
    // Restore the photo grid from the saved photo_paths so the UI
    // matches the run the worker is processing.
    if (lastRun.photo_paths) {
      const paths = String(lastRun.photo_paths).split(",").map((s) => s.trim()).filter(Boolean);
      if (paths.length) setPhotoUrls(paths);
      if (paths.length) setReadPhotos(paths);
    }
    setLastRun(null);
    try {
      let result = null;
      let finalStatus = null;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        let statusResp;
        try {
          statusResp = await api.get(`/measure/ai-measure/status/${lastRun.run_id}`);
        } catch {
          continue;
        }
        const s = statusResp?.data || {};
        if (s.stage && s.stage !== busyStage) setBusyStage(s.stage);
        if (s.phase_a_progress) setPhotoProgress(s.phase_a_progress);
        if (s.status === "error") throw new Error(s.error || "AI measure failed");
        if (s.status === "done") { result = s.result; finalStatus = s; break; }
      }
      if (!result) throw new Error("Resume timed out — the server may still be finishing in the background");
      // Mimic the same downstream flow as a normal run completion.
      _applyAIResult(result, finalStatus);
      toast.success("AI Measure resumed — preview loaded");
    } catch (e) {
      toast.error(e?.message || "Resume failed");
    } finally {
      setBusy(false);
      setBusyStage("");
    }
  };

  // Iter 57r — handler: restore the preview from a finished run.
  const restoreLastRun = () => {
    if (!lastRun || !lastRun.result) return;
    if (lastRun.photo_paths) {
      const paths = String(lastRun.photo_paths).split(",").map((s) => s.trim()).filter(Boolean);
      if (paths.length) setPhotoUrls(paths);
      if (paths.length) setReadPhotos(paths);
    }
    setCurrentRunId(lastRun.run_id);
    _applyAIResult(lastRun.result, lastRun);
    setLastRun(null);
    toast.success("Last AI run restored — preview loaded");
  };

  // Debounced autosave: any time the persisted state changes, push to
  // /measure/sessions. 1 second debounce keeps wall-edit keystrokes from
  // hammering the backend.
  useEffect(() => {
    if (!estimateId || !open || !sessionChecked) return;
    // Skip empty initial state to avoid creating an empty session doc.
    if (!photoUrls.length && !preview) return;
    // Iter 79j.29 — Do NOT clobber a session that already has photos
    // with an empty photo_urls array when a preview exists. This was
    // the root cause of the "Re-Run does nothing" bug — an empty
    // photo_urls + non-null preview mismatch poisoned the session so
    // Resume rehydrated 0 photos and Re-Run silently bailed.
    if (!photoUrls.length && preview) return;
    // Iter 79j.53 — HARD GUARD: never persist a preview that carries a
    // `_reconciliation_error`. A failed reconcile attempt would
    // otherwise silently clobber the last-good preview on the next
    // debounced autosave and bury the working reconciled house. The
    // run doc (ai_measure_runs) is authoritative for the retry
    // target; the session's job is to remember successful state
    // only. If the current preview is a failure state, skip the
    // autosave entirely — the last-good session preview stays put.
    if (preview && preview?.raw_ai?._reconciliation_error) {
      return;
    }
    const t = setTimeout(() => {
      api
        .put(`/measure/sessions/${estimateId}`, {
          estimate_id: estimateId,
          photo_urls: photoUrls,
          reference_dim: refDim,
          brick_course_in: brickCourse && parseFloat(brickCourse) > 0 ? parseFloat(brickCourse) : null,
          siding_exposure_in: sidingExposure && parseFloat(sidingExposure) > 0 ? parseFloat(sidingExposure) : null,
          wall_height: wallHeight,
          siding_pct: sidingPct,
          overhang_in: Number(overhangIn ?? 12),
          preview,
          // Iter 56f: persist per-photo annotations so they survive
          // page navigation / refresh too. Previously these were
          // dropped on close because they weren't in the payload —
          // contractors lost all their pin / scale / mask work.
          photo_annotations: photoAnnotations,
        })
        .catch(() => {
          // Non-fatal: autosave failures are silent so they don't
          // interrupt the contractor's flow. Local state is still good.
        });
    }, 1000);
    return () => clearTimeout(t);
  }, [estimateId, open, sessionChecked, photoUrls, refDim, wallHeight, sidingPct, overhangIn, preview, photoAnnotations]);


  const resumeSession = async () => {
    const data = window.__aiMeasurePendingSession;
    if (!data) {
      setResumePrompt(false);
      return;
    }
    // Iter 79j.29 — if the session's photo_urls is empty but a preview
    // exists, the session was clobbered by an earlier autosave bug.
    // Fall back to the last completed run doc's photo_paths so the
    // contractor's grid + Re-Run still work.
    let urls = data.photo_urls || [];
    if ((!urls || urls.length === 0) && data.preview) {
      try {
        const r = await api.get(`/measure/ai-measure/history/${estimateId}`, { params: { limit: 1 } });
        const last = r.data?.runs?.[0];
        const paths = (last?.photo_paths || "").split(",").map((s) => s.trim()).filter(Boolean);
        if (paths.length) {
          urls = paths;
          toast.success(`Recovered ${paths.length} photos from the last run`);
        }
      } catch {
        // Non-fatal — the loud banner below handles the "no photos" case.
      }
    }
    setPhotoUrls(urls);
    setRefDim(data.reference_dim || "");
    // Iter 79j.67(a) — restore contractor calibration on session resume;
    // without this a recovered session re-runs with no exposure and
    // course counting never fires.
    if (data.brick_course_in != null) setBrickCourse(String(data.brick_course_in));
    if (data.siding_exposure_in != null) setSidingExposure(String(data.siding_exposure_in));
    setWallHeight(data.wall_height || "");
    setSidingPct(data.siding_pct || "");
    if (data.preview) setPreview(data.preview);
    if (data.photo_annotations) setPhotoAnnotations(data.photo_annotations);
    // Iter 79j.52 — If the resumed preview carries a stale
    // reconciliation failure, hoist it into `runError` so the
    // top-level failure banner (with the Retry Reconciliation
    // button) surfaces, the 3D tab suppresses its placeholder
    // render, and the Apply button disables. Without this the UI
    // silently restored a preview whose raw_ai._reconciliation_error
    // was set but ONLY the run-time (fresh-run) error path could
    // render the banner.
    // Iter 79j.53 — Mark the error as HISTORIC (origin="resume") so
    // the banner reads "Prior reconciliation failed" and does NOT
    // display "Elapsed: 0s" as if a fresh call just returned. This
    // was misleading contractors into thinking Resume auto-fired a
    // retry. Resume is read-only — it never calls Phase B; retries
    // are always explicit user clicks.
    const reconErr = data?.preview?.raw_ai?._reconciliation_error;
    const resumedRunId = data?.preview?.run_id || null;
    // Iter 79j.52b — Session self-heal on Resume. If the persisted
    // preview says reconciliation failed, but the LATEST run doc on
    // the server has a successful reconciliation (typically because a
    // retry ran after the session was persisted), replace the stale
    // failure with the fresh good result AND overwrite the session
    // doc so subsequent resumes stay healed. Falls back to the
    // existing "Prior reconciliation failed" banner if no fresher
    // good run exists.
    let didSelfHeal = false;
    if (reconErr) {
      try {
        const { data: freshData } = await api.get(
          `/measure/ai-measure/latest-for-estimate/${estimateId}`,
        );
        const freshRun = freshData?.run;
        const freshResult = freshRun?.result;
        const freshHasError = !!freshResult?.raw_ai?._reconciliation_error;
        const freshOk = freshRun?.status === "done"
          && freshResult
          && freshResult.raw_ai
          && !freshHasError;
        if (freshOk) {
          const healedPreview = { ...freshResult, run_id: freshRun.run_id };
          setPreview(healedPreview);
          setCurrentRunId(freshRun.run_id);
          setRunError(null);
          setRunErrorMeta(null);
          // Persist the self-heal so a page navigation + Resume later
          // doesn't re-summon the stale failure banner.
          try {
            await api.put(`/measure/sessions/${estimateId}`, {
              estimate_id: estimateId,
              photo_urls: urls,
              reference_dim: data.reference_dim || "",
              brick_course_in: data.brick_course_in ?? null,
              siding_exposure_in: data.siding_exposure_in ?? null,
              wall_height: data.wall_height || "",
              siding_pct: data.siding_pct || "",
              overhang_in: Number(data.overhang_in ?? 12),
              preview: healedPreview,
              photo_annotations: data.photo_annotations || {},
            });
          } catch {
            // Non-fatal — next debounced autosave will retry.
          }
          toast.success(
            "Resumed — session self-healed from a newer successful reconciliation",
          );
          didSelfHeal = true;
        }
      } catch {
        // Latest-run lookup failed (network / 404). Fall through to
        // the "Prior reconciliation failed" banner path.
      }
      if (!didSelfHeal) {
        setRunError(String(reconErr));
        setRunErrorMeta({
          stage: "reconciling",
          elapsedMs: null,
          kind: "PriorFailure",
          origin: "resume",
        });
        if (resumedRunId) setCurrentRunId(resumedRunId);
      }
    } else if (resumedRunId) {
      // Non-failure resume: still remember the run id so a manual
      // Retry Reconciliation (if the user opens it later) has the
      // right target.
      setCurrentRunId(resumedRunId);
    }
    setResumePrompt(false);
    delete window.__aiMeasurePendingSession;
    if (urls.length > 0 && !didSelfHeal) {
      toast.success("Resumed your last AI Measure session");
    }
  };

  const startOver = async () => {
    // 2026-08-11 send-4: local session state is cleared optimistically
    // so the UI resets fast. If the DELETE refuses, the session in
    // the DB survives and the local state must too — otherwise the
    // contractor sees a blank slate while a session ghost lives on.
    const prev = {
      resumePrompt,
      pendingSessionMeta,
      preview,
      photoUrls,
      files,
      refDim,
      wallHeight,
      sidingPct,
      wallsDirty,
      photoAnnotations,
      annotDirtySinceRun,
      windowGhost: window.__aiMeasurePendingSession,
    };
    if (!estimateId) {
      setResumePrompt(false);
      setPendingSessionMeta(null);
      setPreview(null);
      setPhotoUrls([]);
      setFiles([]);
      setRefDim("");
      setWallHeight("");
      setSidingPct("");
      setWallsDirty(false);
      setPhotoAnnotations({});
      setAnnotDirtySinceRun(false);
      delete window.__aiMeasurePendingSession;
      return;
    }
    await writeThrough({
      applyOptimistic: () => {
        setResumePrompt(false);
        setPendingSessionMeta(null);
        setPreview(null);
        setPhotoUrls([]);
        setFiles([]);
        setRefDim("");
        setWallHeight("");
        setSidingPct("");
        setWallsDirty(false);
        setPhotoAnnotations({});
        setAnnotDirtySinceRun(false);
        delete window.__aiMeasurePendingSession;
      },
      rollback: () => {
        setResumePrompt(prev.resumePrompt);
        setPendingSessionMeta(prev.pendingSessionMeta);
        setPreview(prev.preview);
        setPhotoUrls(prev.photoUrls);
        setFiles(prev.files);
        setRefDim(prev.refDim);
        setWallHeight(prev.wallHeight);
        setSidingPct(prev.sidingPct);
        setWallsDirty(prev.wallsDirty);
        setPhotoAnnotations(prev.photoAnnotations);
        setAnnotDirtySinceRun(prev.annotDirtySinceRun);
        if (prev.windowGhost !== undefined) {
          window.__aiMeasurePendingSession = prev.windowGhost;
        }
      },
      apiCall: async () => {
        await api.delete(`/measure/sessions/${estimateId}`);
        return { data: null, status: 200 };
      },
      refusalTestid: "ai-measure-session-refusal-chip",
    });
  };

  // Iter 79j.52a — Dismiss handler for the run-error banner. Behavior
  // depends on WHAT kind of error is on screen:
  //   • Reconciliation-failure preview (either resumed or fresh):
  //     downgrade the session to PHOTOS-ONLY. That means:
  //       - clear the failed preview from local state,
  //       - clear currentRunId (per Howard 2026-07-06: dismiss = clean
  //         slate; Debug View still holds the run history server-side),
  //       - IMMEDIATELY PUT the session with preview=null so the next
  //         Resume doesn't restore the failed banner (the debounced
  //         autosave alone would leave a race window on navigation).
  //     Photos, ref dim, wall height, siding % and annotations are
  //     preserved so the contractor can just click Run AI Measure again
  //     without re-uploading a thing.
  //   • Any other error kind (budget, generic fresh-run failure):
  //     just clear the local banner state — nothing on the session
  //     needs to change because those errors never wrote a failed
  //     preview (the autosave guard on line 703 prevented it).
  const dismissRunError = async () => {
    const hasReconcileFailure = !!(preview && preview?.raw_ai?._reconciliation_error);
    setRunError(null);
    setRunErrorMeta(null);
    if (!hasReconcileFailure) return;   // budget / fresh-error: nothing to persist
    setPreview(null);
    setCurrentRunId(null);
    if (!estimateId) return;
    try {
      await api.put(`/measure/sessions/${estimateId}`, {
        estimate_id: estimateId,
        photo_urls: photoUrls,
        reference_dim: refDim,
        brick_course_in: brickCourse && parseFloat(brickCourse) > 0 ? parseFloat(brickCourse) : null,
        siding_exposure_in: sidingExposure && parseFloat(sidingExposure) > 0 ? parseFloat(sidingExposure) : null,
        wall_height: wallHeight,
        siding_pct: sidingPct,
        overhang_in: Number(overhangIn ?? 12),
        preview: null,
        photo_annotations: photoAnnotations,
      });
    } catch {
      // Non-fatal — local state is already clean; the next debounced
      // autosave will retry the persistence.
    }
  };


  // Upload-on-select. Photos hit /api/uploads immediately so they're
  // safe across page refreshes; only the resulting server URLs go into
  // photoUrls. The transient `files` list lives just long enough to
  // show previews during upload.
  const pickFiles = async (e) => {
    const arr = Array.from(e.target.files || []).slice(0, 9 - photoUrls.length);
    if (!arr.length) return;
    setFiles((prev) => [...prev, ...arr]);
    // Parallel uploads.
    const uploaded = await Promise.all(
      arr.map(async (f) => {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const { data } = await api.post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          return data.name; // store the bare filename
        } catch (err) {
          toast.error(`Upload failed for ${f.name}`);
          return null;
        }
      })
    );
    const ok = uploaded.filter(Boolean);
    setPhotoUrls((prev) => [...prev, ...ok]);
    // Drop the local File objects now that the URLs are persisted.
    setFiles((prev) => prev.filter((f) => !arr.includes(f)));
    // SEND-213 (Howard ruled 2026-08-14) — "The photo lands in this same
    // row." The top row is the SERVER'S union, and a freshly uploaded file is
    // in none of its stores until the run's list is saved — the autosave is
    // debounced, so waiting for it is waiting for a photo to appear by
    // itself. This says the list out loud through the SAME session door
    // (which merges, so nothing can be dropped) and then re-reads the row.
    if (ok.length && estimateId) {
      try {
        await api.put(`/measure/sessions/${estimateId}`,
          { estimate_id: estimateId, photo_urls: [...photoUrls, ...ok] });
      } catch (err) {
        toast.error("Uploaded, but the job's photo list did not save — reopen this screen");
      }
      await loadNoAiPhotos();
    }
  };

  // SEND-213 — DROP ON THE + TILE. Only images are taken; anything else is
  // ignored rather than uploaded and refused later.
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const photoCapped = photoUrls.length >= 9;
  const dropPhotos = (e) => {
    e.preventDefault();
    setPhotoDragOver(false);
    const imgs = Array.from(e.dataTransfer?.files || [])
      .filter((f) => (f.type || "").startsWith("image/"));
    if (imgs.length) pickFiles({ target: { files: imgs } });
  };

  // SEND-204 — the wizard's open state is gone with its mount. Nothing on
  // this screen holds a door to a retired screen.
  // SEND-204 (Howard ruled 2026-08-14) — THE WALK'S HAND-OFF IS GONE WITH
  // THE WALK. `handleWizardComplete` uploaded the wizard's captures,
  // pre-tagged their elevations, merged its annotations and armed an
  // auto-run; with the wizard retired there is nothing to hand off, and a
  // live handler for a dead screen is how a retired door creeps back.
  // WHAT IT DID THAT STILL MATTERS HAS ITS OWN HOME: photos arrive through
  // the file input, the wall tag is set BY HAND on Photo Takeoff
  // (SEND-165), and the per-photo AI choice is the switch on the photo list
  // (SEND-183 item 3), which was always the owner of that field. No
  // auto-run survives: a read starts when he presses the run button.
  // Iter 56c: pull a free Esri aerial tile for the estimate's address
  // and add it as an 8th photo. The endpoint resolves the address →
  // lat/lon → satellite JPEG and writes the file straight into the same
  // UPLOAD_DIR /api/uploads uses, so we just append the filename to
  // photoUrls and auto-tag it as "aerial" so Claude knows what it is.
  const fetchSatellite = async () => {
    if (satBusy) return;
    if (!address || !String(address).trim()) {
      toast.error("Fill in the Address in Job Information first — I need it to find the property");
      return;
    }
    if (photoUrls.length >= 9) {
      toast.error("Already at 9 photos — remove one to add the aerial view");
      return;
    }
    setSatBusy(true);
    try {
      const fd = new FormData();
      fd.append("address", address);
      const { data } = await api.post("/measure/satellite-tile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      const name = data?.filename;
      if (!name) throw new Error("No filename in response");
      setPhotoUrls((prev) => [...prev, name]);
      setPhotoAnnotations((prev) => ({
        ...prev,
        [name]: { ...(prev[name] || {}), elevation: "aerial" },
      }));
      toast.success(`Aerial view added · ${(data.bytes / 1024).toFixed(0)} KB`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Satellite fetch failed";
      toast.error(detail);
    } finally {
      setSatBusy(false);
    }
  };

  // SEND-212 — `removePhoto` IS GONE WITH THE SECOND GALLERY. It took a
  // photo off this run's list and left its marks and its scale behind, which
  // is exactly the ghost Howard hit: the server's union still saw the photo
  // and put it straight back in the top list. The one delete door is
  // `deleteJobPhoto`, below, and it hits every store.

  // SEND-212 (Howard ruled 2026-08-14) — ONE PHOTO LIST, AND DELETE IS
  // DELETE. The old × took the name off `ai_measure_sessions.photo_urls`
  // only, so a photo with a mark or a scale stayed alive in the server's
  // union and came straight back into the top list. THIS IS THE ONLY DELETE
  // DOOR ON THE SCREEN and it calls the one route that hits every store.
  //
  // THE ASK IS THE SERVER'S. With confirmed marks on the photo the route
  // refuses with 409 and HIS OWN SENTENCE; the screen prints what arrived
  // and offers NO / YES. NO changes nothing at all.
  const [photoDeleteAsk, setPhotoDeleteAsk] = useState(null);
  const [photoDeleteBusy, setPhotoDeleteBusy] = useState(null);
  const deleteJobPhoto = async (name, confirmed = false) => {
    if (!estimateId || !name) return;
    setPhotoDeleteBusy(name);
    try {
      const { data } = await api.delete(
        `/estimates/${estimateId}/photo-takeoff/photos/${encodeURIComponent(name)}`
        + (confirmed ? "?confirm=true" : ""));
      setPhotoDeleteAsk(null);
      setPhotoUrls((prev) => prev.filter((n) => n !== name));
      await loadNoAiPhotos();
      loadPhotoPrefs();
      toast.success(`Photo deleted from this job · ${data?.removed?.marks || 0} mark(s) went with it`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.needs_confirm) {
        setPhotoDeleteAsk({ name, ask: d.ask, count: d.confirmed_marks,
                            note: d.note });
        return;
      }
      toast.error(typeof d === "string" ? d : "Could not delete that photo");
    } finally {
      setPhotoDeleteBusy(null);
    }
  };

  // Iter 79j.64 — Fix 3: proceed handler for the destructive-action
  // confirm modal. "start_over" and "start_fresh" both wipe the local
  // state AND delete the server session. SEND-212 — the per-photo branch is
  // gone: a photo delete is its own door with its own ask.
  const proceedDestructive = () => {
    const dc = destructiveConfirm;
    setDestructiveConfirm(null);
    if (!dc) return;
    startOver();
  };

  // Iter 79i (Phase 4) — Missing-wall warning modal. Before firing
  // runMeasure we check which of the 4 primary walls (front/back/left/
  // right) have at least one tagged photo. Corner shots (front-left,
  // rear-right, etc.) count as covering both adjacent primary walls
  // since the AI can read both elevations from a 45° corner. If fewer
  // than 4 primary walls are covered, we open a modal so the contractor
  // can go back and capture the missing ones — or bypass with "Run
  // anyway" if they have a valid reason (e.g. inaccessible side yard).
  const [missingWallsModal, setMissingWallsModal] = useState(null);
  // Iter 79j.29 — inline "no photos" banner shown when Re-Run is
  // clicked with an empty photo grid. Auto-clears on next successful
  // upload or run.
  const [noPhotosBanner, setNoPhotosBanner] = useState(false);
  // Iter 79j.30 — persistent inline error banner shown when a run
  // fails. Sonner toasts are hidden behind the modal and auto-dismiss
  // in 4s — for a $-affecting failure like budget-exceeded that's not
  // enough. Cleared automatically on the next successful run.
  const [runError, setRunError] = useState(null);
  // Iter 79j.44 — Additional context for the persistent error banner.
  // Stage tells the user which phase failed (Phase A extraction,
  // Phase B reconcile, or upstream) and elapsed lets them judge
  // whether it looks like a timeout vs a fast failure.
  const [runErrorMeta, setRunErrorMeta] = useState(null); // { stage, elapsedMs, kind }
  const runStartTsRef = useRef(0);
  // Iter 79j.45 — AI service health ping. `aiHealth` is the last
  // response from GET /api/measure/ai-measure/health. Cached on the
  // client for 45s (matching the server-side TTL) so we ping at most
  // once every 45s across all sources (modal-open, Run-click).
  //
  // Rules (per Howard):
  //   1. Never ping on every render. Only on modal-open + Run-click.
  //   2. Distinguish outcomes — do NOT collapse every failure into
  //      "budget exhausted".
  //   3. A broken health check must NOT hard-lock Run. `ambiguous`
  //      keeps the Run button enabled with a soft warning banner.
  const [aiHealth, setAiHealth] = useState(null); // {status, detail, checked_at, cached}
  const aiHealthLastRef = useRef(0);
  const AI_HEALTH_CLIENT_TTL_MS = 45_000;
  const refreshAiHealth = async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && aiHealth && (now - aiHealthLastRef.current) < AI_HEALTH_CLIENT_TTL_MS) {
      return aiHealth; // still fresh — no network call
    }
    try {
      const { data } = await api.get("/measure/ai-measure/health");
      aiHealthLastRef.current = Date.now();
      setAiHealth(data);
      return data;
    } catch (e) {
      // If the health endpoint itself is broken, treat as ambiguous
      // so the Run button STAYS ENABLED. A broken health check must
      // never be able to disable the product.
      const fallback = {
        status: "ambiguous",
        detail: "Couldn't reach the AI health check. You can still run — proceed with caution.",
        raw_error: e?.message || String(e),
      };
      aiHealthLastRef.current = Date.now();
      setAiHealth(fallback);
      return fallback;
    }
  };
  // Iter 79j.46 — Event-driven auto-recovery. When the modal is open
  // AND health is red (budget_exceeded / unavailable), listen for the
  // browser tab regaining focus or visibility — that's the "went to
  // the billing page, topped up, came back" moment. Do NOT poll while
  // green: a healthy button has nothing to learn from a ping.
  const isHealthRed =
    aiHealth?.status === "budget_exceeded" || aiHealth?.status === "unavailable";

  // Iter 79j.45 — Ping AI service health when the modal opens. If a
  // fresh ping is already in the 45s window, this call short-circuits
  // to the cached value (no network hit). We don't await — the health
  // flag flips the Run button state as soon as the response lands,
  // but the rest of the modal renders immediately.
  useEffect(() => {
    if (!open) return;
    refreshAiHealth();
  }, [open]);

  // Iter 79j.46 — Auto-recovery event listeners. Only wired up while
  // the modal is open AND health is red. Detaches immediately when
  // either condition flips — a green button gets no listeners, so a
  // healthy user pays zero cost.
  useEffect(() => {
    if (!open || !isHealthRed) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshAiHealth({ force: true });
      }
    };
    const onFocus = () => refreshAiHealth({ force: true });
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [open, isHealthRed]);

  // Iter 79j.46 — Slow-backoff timer, ONLY while the modal is open
  // AND the status is red. Backoff schedule: 60s → 2min → 5min, then
  // stays at 5min. Any status change (red → green, red → different
  // red variant) cancels the timer and restarts fresh. This gives a
  // topped-up budget a chance to recover without the user re-clicking
  // Run, but never fires while green (nothing to learn).
  const backoffTimerRef = useRef(null);
  const backoffStepRef = useRef(0);
  useEffect(() => {
    // Clear any prior timer on every effect run.
    if (backoffTimerRef.current) {
      clearTimeout(backoffTimerRef.current);
      backoffTimerRef.current = null;
    }
    if (!open || !isHealthRed) {
      backoffStepRef.current = 0;
      return undefined;
    }
    const schedule = [60_000, 120_000, 300_000]; // 60s → 2min → 5min → stays 5min
    const tick = () => {
      const idx = Math.min(backoffStepRef.current, schedule.length - 1);
      backoffStepRef.current += 1;
      backoffTimerRef.current = setTimeout(async () => {
        // Only ping if we're still red AND the modal is still open.
        // Otherwise this callback is a no-op that lets React GC the timer.
        if (!open) return;
        const latest = await refreshAiHealth({ force: true });
        if (
          open &&
          (latest?.status === "budget_exceeded" || latest?.status === "unavailable")
        ) {
          tick();
        } else {
          backoffStepRef.current = 0;
        }
      }, schedule[idx]);
    };
    tick();
    return () => {
      if (backoffTimerRef.current) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
    };
  }, [open, isHealthRed]);

  const primaryWallsCovered = () => {
    const covered = new Set();
    // Iter 79j.35 — Two sources of elevation tags:
    // 1. `photoAnnotations` — contractor-set (via the ProfileAnnotator or
    //    the elevation dropdown on each thumbnail).
    // 2. `preview.measurements._ai_photos` — Claude's per-photo
    //    classification from the most recent run. These are the tags
    //    the contractor SEES as colored badges on each photo thumbnail
    //    (front, back, front-right, aerial, etc). Prior versions of
    //    this guard only read source #1, so hitting Re-run on a run
    //    whose photos had Claude-classified elevations (but no
    //    contractor overrides) triggered a false "0 of 4 walls
    //    captured" modal.
    const aiPhotos = preview?.measurements?._ai_photos || preview?.raw_ai?.photos || [];
    photoUrls.forEach((name, idx) => {
      const eManual = (photoAnnotations[name]?.elevation || "").toLowerCase();
      const eAi = (aiPhotos[idx]?.elevation || "").toLowerCase();
      const e = eManual || eAi;
      if (!e) return;
      if (e === "front" || e === "front-left" || e === "front-right") covered.add("front");
      if (e === "back" || e === "rear-left" || e === "rear-right") covered.add("back");
      if (e === "left" || e === "front-left" || e === "rear-left") covered.add("left");
      if (e === "right" || e === "front-right" || e === "rear-right") covered.add("right");
    });
    return covered;
  };
  const missingPrimaryWalls = () => {
    const c = primaryWallsCovered();
    return ["front", "back", "left", "right"].filter((w) => !c.has(w));
  };

  const runMeasure = async (opts = {}) => {
    if (!photoUrls.length) {
      // Iter 79j.29 — LOUD failure. Sonner toasts aren't visible when
      // the main modal covers the screen, so we also flash an inline
      // banner state that blocks further clicks until the contractor
      // re-uploads. Prior behavior was a silent no-op that looked
      // exactly like "the Re-Run button is broken".
      toast.error("Add at least one photo before running");
      setNoPhotosBanner(true);
      return;
    }
    setNoPhotosBanner(false);
    // SEND-189 ITEM (Howard ruled 2026-08-14) — THE SWITCH DECIDES WHAT AI
    // LOOKS AT, AND THE WALK'S EXIT IS ONLY A DEFAULT.
    //
    // Until this send the per-photo choice changed what the EDITOR showed
    // and nothing else: the read was built from every photo on the screen,
    // so a photo marked HAND ONLY was still sent to the model. The walk's
    // exit won and the switch never competed.
    //
    // THE FILTER LIVES HERE, IN THE BROWSER, DELIBERATELY. SEND-183's
    // split is that the server may STORE the choice and may never READ it,
    // and its pins forbid the collection being named outside its own two
    // endpoints. Filtering the photo list before the call keeps that
    // whole — the choice never reaches the read, and no basis can see it.
    //
    // AND IT IS SAID OUT LOUD, never silently: the held-back photos are
    // named in a toast, and refusing everything is a refusal in words
    // rather than a run on photos he took off the list.
    const heldBack = photoUrls.filter(
      (n) => photoPrefs[n]?.ai_allowed === false);
    const readable = photoUrls.filter(
      (n) => photoPrefs[n]?.ai_allowed !== false);
    if (!readable.length) {
      toast.error(
        `Every photo on this job is marked HAND ONLY (${heldBack.length}) — `
        + "nothing was sent to the AI. Switch a photo to AI MAY READ on the "
        + "photo list, or measure it by hand.");
      return;
    }
    if (heldBack.length) {
      toast.info(
        `${heldBack.length} photo${heldBack.length === 1 ? "" : "s"} marked `
        + "HAND ONLY — held back from this read. "
        + `${readable.length} going to the AI.`);
    }
    // Iter 79j.60 — Reset the per-photo HUD on every fresh run so the
    // previous run's failed dots don't linger into a new attempt.
    setPhotoProgress(null);
    // Iter 79i — pre-flight guardrail. Skip if the caller passes
    // `bypassMissingWallsGuard: true` (from the "Run anyway" button in
    // the missing-walls modal). Iter 79j.20: switched from a state
    // sentinel to an explicit param because setTimeout captured the
    // stale `missingWallsModal` value from the render at click time,
    // so the guard re-fired on the "bypassed" run and the modal
    // reappeared with nothing happening.
    if (!opts.bypassMissingWallsGuard) {
      const missing = missingPrimaryWalls();
      if (missing.length > 0) {
        setMissingWallsModal({ missing });
        return;
      }
    }
    setMissingWallsModal(null);
    // Iter 79j.45 — Health gate. Refresh the AI service health right
    // before dispatching (may serve from cache if <45s old). Only two
    // outcomes hard-block the run: `budget_exceeded` and `unavailable`.
    // `ambiguous` and any other value proceed — a broken health check
    // must NEVER be able to lock the Run button.
    const health = await refreshAiHealth();
    if (health?.status === "budget_exceeded") {
      setRunError(
        "Universal Key budget is exhausted. Open Profile → Universal Key → " +
        "Add Balance (or enable Auto Top-up), then retry the run."
      );
      setRunErrorMeta({ stage: "preflight", elapsedMs: 0, kind: "BudgetExceeded" });
      return;
    }
    if (health?.status === "unavailable") {
      setRunError(
        (health?.detail || "AI service is not responding right now.") +
        " Retry in a minute."
      );
      setRunErrorMeta({ stage: "preflight", elapsedMs: 0, kind: "ServiceUnavailable" });
      return;
    }
    setBusy(true);
    setPreview(null);
    setRunError(null);
    setRunErrorMeta(null);
    runStartTsRef.current = Date.now();
    // Iter 79j.44 — Mutable stage tracker hoisted OUTSIDE try so the
    // catch can read it. React's `busyStage` state is captured in the
    // render closure and does NOT reflect setBusyStage() calls made
    // inside this async function.
    let liveStage = "starting";
    try {
      const fd = new FormData();

      // Iter 56: photo annotations. For each photo that has annotations
      // (scale anchor, no-siding zones, or elevation tag), we render an
      // annotated PNG client-side and upload it as a fresh file. The
      // un-annotated photos stay referenced by their existing
      // /api/uploads path for free.
      const annotatedFiles = [];   // [{ name (original), file }]
      const passThroughUrls = [];  // original photoUrls that have no annot
      const elevations = {};       // { originalName: elevation }
      // Iter 57j — track per-photo elevation aligned to backend order
      // (photo_paths first, then files). Empty strings preserve slot
      // alignment when an elevation is unknown.
      const passThroughElevs = [];
      const annotatedElevs = [];
      for (const name of readable) {
        const a = photoAnnotations[name];
        if (annotEmpty(a)) {
          passThroughUrls.push(name);
          passThroughElevs.push((a && a.elevation) || "");
          continue;
        }
        try {
          const blob = await renderAnnotated(`/api/uploads/${name}`, a);
          const file = new File([blob], `annotated-${name.replace(/\.\w+$/, "")}.jpg`, { type: "image/jpeg" });
          annotatedFiles.push({ name, file });
          annotatedElevs.push(a.elevation || "");
          if (a.elevation) elevations[name] = a.elevation;
        } catch (e) {
          // Render failed (e.g. CORS) — fall back to the original photo
          // path. Still pass the structured description as text so
          // Claude has at least that.
          console.warn("annotate render failed for", name, e);
          passThroughUrls.push(name);
          passThroughElevs.push((a && a.elevation) || "");
        }
      }
      if (passThroughUrls.length) {
        fd.append("photo_paths", passThroughUrls.join(","));
      }
      for (const { file } of annotatedFiles) {
        fd.append("files", file);
      }

      // Reference dim + structured annotation description go into the
      // SAME reference_dim field — Claude reads it as contractor-
      // provided context inside the user prompt.
      const refBits = [];
      if (refDim) refBits.push(refDim);
      if (wallHeight) refBits.push(`average wall height = ${wallHeight} ft`);
      if (sidingPct) refBits.push(`siding covers ~${sidingPct}% of total wall area (rest is brick / stone / garage / etc.)`);
      // Build a per-photo description from the annotations so Claude has
      // BOTH visual (burned-in marks) and structured (text) cues.
      const annotEntries = photoUrls
        .map((name) => ({ photoName: name, ...(photoAnnotations[name] || {}) }))
        .filter((e) => !annotEmpty(e));
      const annotText = describeAnnotations(annotEntries);
      if (annotText) refBits.push(`Pre-AI photo annotations:\n${annotText}`);
      const refCombined = refBits.join("; ");
      if (refCombined) fd.append("reference_dim", refCombined);
      if (address) fd.append("address", address);
      fd.append("kind", kind || "siding");
      // Soffit pieces are computed server-side using this overhang.
      fd.append("overhang_in", String(overhangIn ?? 12));
      // Iter 57g — pass the contractor's course-counting overrides
      // (blank = use Claude's defaults). Backend feeds these into the
      // prompt as additional sizing context.
      if (brickCourse && parseFloat(brickCourse) > 0) {
        fd.append("brick_course_in", String(parseFloat(brickCourse)));
      }
      if (sidingExposure && parseFloat(sidingExposure) > 0) {
        fd.append("siding_exposure_in", String(parseFloat(sidingExposure)));
      }
      // Iter 79j.44 — Deep Dormer Scan is a no-op now; Phase A/B
      // owns dormer detection. Don't append the flag — cleaner
      // request payload.
      // Elevation tags aligned with the backend photo order
      // (photo_paths first, then files). Kept for elevation hints
      // (front/right/back/left) that Phase A uses to seed extraction.
      const elevTagList = [...passThroughElevs, ...annotatedElevs];
      if (elevTagList.length) {
        fd.append("elevation_tags", elevTagList.join(","));
      }
      // Iter 79j.15 — A/B model choice (admin bake-offs). Non-admin
      // requests clamp server-side to the validated configuration.
      if (modelChoice) {
        fd.append("model_choice", modelChoice);
      }
      // Iter 57r — link the run to this estimate so a later modal open
      // can offer to Resume / Restore the most recent run for this job.
      if (estimateId) {
        fd.append("estimate_id", estimateId);
      }
      // CONTRACTOR GABLES (ruled 2026-07-24): structured payload persists
      // on the run doc → Field Verify rows + sheet callouts. The burned
      // triangles + annotation text already carry them to Claude as
      // ground truth; this is the takeoff-visible record.
      const contractorGables = [];
      const risesByElevation = {};
      for (const gname of photoUrls) {
        const ga = photoAnnotations[gname];
        if (!ga || !ga.gables || !ga.gables.length) continue;
        const inPerPx = gableInchesPerPx(ga.reference, ga.windowReference);
        for (const gb of ga.gables) {
          const d = gableNetArea(gb, ga.zones || [], inPerPx);
          if (!d) continue;
          const gelev = ga.elevation || "other";
          // Photo-frac norms (ruled 2026-07-25 follow-up #2 — exact
          // mirror of the dormer quads): the triangle's position rides
          // along so the sheet binder can draw the gable AT the taps.
          const gnW = ga.imageDims?.w || 0;
          const gnH = ga.imageDims?.h || 0;
          const gr4 = (v) => Math.round(v * 10000) / 10000;
          const [gL, gP, gR] = gb.pts;
          const row = {
            elevation: gelev,
            pitch: d.pitch,
            base_ft: d.baseFt !== undefined ? Math.round(d.baseFt * 10) / 10 : null,
            rise_ft: d.riseFt !== undefined ? Math.round(d.riseFt * 10) / 10 : null,
            area_ft: d.netAreaFt !== undefined ? Math.round(d.netAreaFt * 10) / 10 : null,
            masked_ft: d.maskedFt ? Math.round(d.maskedFt * 10) / 10 : 0,
            photo: gname,
            x_center_frac: gnW ? gr4((gL.x + gR.x) / 2 / gnW) : null,
            y_eave_frac: gnH ? gr4((gL.y + gR.y) / 2 / gnH) : null,
            peak_x_frac: gnW ? gr4(gP.x / gnW) : null,
            base_frac: gnW && d.basePx ? gr4(d.basePx / gnW) : null,
            rise_frac: gnH && d.risePx ? gr4(d.risePx / gnH) : null,
          };
          contractorGables.push(row);
          if (row.rise_ft !== null) {
            (risesByElevation[gelev] = risesByElevation[gelev] || []).push(row.rise_ft);
          }
        }
      }
      if (contractorGables.length) {
        fd.append("contractor_gables", JSON.stringify(contractorGables));
        // Ridge cross-check (ruled: 1.0 ft) — gentle, never blocks.
        const ridgeWarn = crossCheckRidges(risesByElevation);
        if (ridgeWarn) toast.warning(ridgeWarn, { duration: 9000 });
      }
      // CONTRACTOR DORMERS (ruled 2026-07-25 — mirror of gables): the
      // structured payload persists on the run doc → Field Verify rows +
      // sheet callouts. Burn-in + annotation text already carry them to
      // Claude as ground truth; this is the takeoff-visible record.
      const contractorDormers = [];
      for (const dname of photoUrls) {
        const da = photoAnnotations[dname];
        if (!da || !da.dormers || !da.dormers.length) continue;
        const dInPerPx = gableInchesPerPx(da.reference, da.windowReference);
        for (const dm of da.dormers) {
          const d = dormerNetArea(dm, da.zones || [], dInPerPx);
          if (!d) continue;
          // Photo-frac norms (ruled 2026-07-25 follow-up): the quad's
          // position rides along so the elevation-sheet binder can move
          // the DRAWN dormer band to the contractor's taps — the same
          // bbox-frac convention the AI openings already use.
          const nW = da.imageDims?.w || 0;
          const nH = da.imageDims?.h || 0;
          const r4 = (v) => Math.round(v * 10000) / 10000;
          contractorDormers.push({
            elevation: da.elevation || "other",
            width_ft: d.widthFt !== undefined ? Math.round(d.widthFt * 10) / 10 : null,
            height_ft: d.heightFt !== undefined ? Math.round(d.heightFt * 10) / 10 : null,
            area_ft: d.netAreaFt !== undefined ? Math.round(d.netAreaFt * 10) / 10 : null,
            masked_ft: d.maskedFt ? Math.round(d.maskedFt * 10) / 10 : 0,
            photo: dname,
            // Depth is typed on the dormer row (ruled 2026-07-26) —
            // cheeks derive server-side (2 × face height × depth).
            depth_ft: dm.depth_ft != null && dm.depth_ft !== "" && !isNaN(parseFloat(dm.depth_ft))
              ? Math.round(parseFloat(dm.depth_ft) * 10) / 10 : null,
            x_center_frac: nW ? r4(dm.pts.reduce((s, p) => s + p.x, 0) / 4 / nW) : null,
            y_bottom_frac: nH ? r4((dm.pts[0].y + dm.pts[1].y) / 2 / nH) : null,
            width_frac: nW && d.widthPx ? r4(d.widthPx / nW) : null,
            height_frac: nH && d.heightPx ? r4(d.heightPx / nH) : null,
          });
        }
      }
      if (contractorDormers.length) {
        fd.append("contractor_dormers", JSON.stringify(contractorDormers));
      }
      // Iter 57q — async launcher + polling. The backend used to do
      // all the Claude work synchronously in a single 60–120 s
      // request which hit the Kubernetes ingress timeout on 8-photo
      // houses. Now we POST → get `run_id` in <300 ms → poll
      // `/measure/ai-measure/status/{run_id}` every 3 s until status
      // is "done" or "error". Timeouts are no longer possible.
      const launch = await api.post("/measure/ai-measure", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,   // 60 s is generous for just uploading the photos
      });
      const runId = launch?.data?.run_id;
      if (!runId) {
        throw new Error("Backend didn't return a run_id");
      }
      setCurrentRunId(runId);
      setBusyStage(launch?.data?.stage || "starting");
      liveStage = launch?.data?.stage || "starting";
      // Iter 79j.48 — Poll until done. Client budget MUST comfortably
      // exceed the server's legitimate worst case so a run the server
      // is about to finish never gets phantom-failed as a client
      // timeout. Server worst case with two-phase pipeline:
      //   Phase A total cap (300s) + drain (5s) + Phase B (~180s) ≈ 485s
      // Client: 200 polls × 3s ≈ 600s → 115s cushion.
      //
      // We also track `lastUpdatedAt` (server-side heartbeat via the
      // run doc's `updated_at`). If the server IS still progressing
      // when the client is about to give up (updated_at within last
      // 30s), we grant an additional grace-poll window instead of
      // wasting a run that would have completed in the next 5-10s.
      let result = null;
      let finalStatus = null;
      let lastUpdatedAtSeen = null;
      const MAX_POLLS = 200;         // 200 × 3s = 600s hard ceiling
      const GRACE_POLLS = 40;        // +120s if the server is still alive at the end
      let extraGraceGranted = false;
      let i = 0;
      while (i < MAX_POLLS) {
        await new Promise((r) => setTimeout(r, 3000));
        let statusResp;
        try {
          statusResp = await api.get(`/measure/ai-measure/status/${runId}`);
        } catch (e) {
          // Transient network blip — just retry on the next tick.
          if (i >= 5) console.warn("ai-measure status poll failed", e?.message);
          i += 1;
          continue;
        }
        const s = statusResp?.data || {};
        if (s.stage && s.stage !== liveStage) {
          liveStage = s.stage;
          setBusyStage(s.stage);
        }
        if (s.phase_a_progress) setPhotoProgress(s.phase_a_progress);
        // Heartbeat: the /status endpoint returns `elapsed_ms` computed
        // from `updated_at`. We only care whether the doc is still
        // being written to — a stalled heartbeat suggests the worker
        // died silently and grace polls would just delay the failure.
        if (typeof s.elapsed_ms === "number") {
          lastUpdatedAtSeen = { at: Date.now(), elapsedMs: s.elapsed_ms };
        }
        if (s.status === "error") {
          // Iter 79j.44 — Include stage/kind so the persistent banner
          // can tell the user WHICH phase died. `s.error` is now
          // guaranteed non-empty by the backend.
          const err = new Error(s.error || "AI measure failed (no error message from backend)");
          err._stage = s.stage || liveStage || "unknown";
          err._kind = s.error_kind || "";
          throw err;
        }
        if (s.status === "done") {
          result = s.result;
          finalStatus = s;
          break;
        }
        i += 1;
        // If we're at the tail of the normal budget AND the server
        // heartbeat is fresh (worker still writing), extend once with
        // a bounded grace window — a run about to finish is worth 2
        // extra minutes over a phantom "client timeout" failure.
        if (i === MAX_POLLS && !extraGraceGranted && lastUpdatedAtSeen &&
            (Date.now() - lastUpdatedAtSeen.at) < 30_000) {
          extraGraceGranted = true;
          console.info("[ai-measure] client-poll: server heartbeat still fresh — granting +120s grace");
          setBusyStage(liveStage + " (finishing…)");
          i = MAX_POLLS - GRACE_POLLS;
        }
      }
      if (!result) {
        // Iter 79j.44 — Stamp the last-known server stage on the
        // client-side timeout error so the banner never shows
        // "Phase: UNKNOWN" for a poll that stalled mid-Phase-A.
        // Iter 79j.48 — Copy now names the ACTUAL timeout (10 min +
        // grace) and hints that the server may still be running.
        const elapsedMin = Math.round((MAX_POLLS * 3) / 60);
        const err = new Error(
          `AI measure did not complete within ${elapsedMin} minutes — the server may still ` +
          `be finishing this run in the background. Check the estimate again in a minute ` +
          `or re-run with fewer photos.`
        );
        err._stage = liveStage || "unknown";
        err._kind = "ClientPollTimeout";
        throw err;
      }
      const data = result;
      // Iter 79j.37 — Thread per-photo extractions + pipeline flag
      // into the preview object so the Debug view can show them.
      if (finalStatus) {
        data.raw_per_photo = finalStatus.raw_per_photo;
        data.pipeline = finalStatus.pipeline;
      }
      // Iter 57: trust the walls. Claude occasionally returns
      // siding_pct_this_wall in a way the aggregator can't recover
      // (e.g. 0.5 meaning 50% but post-clamp becomes 0.5%, deflating
      // siding_sqft by 100×). The wall table totals — computed from
      // width × eave height directly — are the honest geometry. Apply
      // recomputeFromWalls right away so measurements.siding_sqft
      // matches what the contractor sees in the Wall Breakdown.
      if (data?.raw_ai?.walls?.length) {
        const totals = recomputeFromWalls(data.raw_ai.walls);
        // Iter 58: force all LF / count fields back to whatever Claude
        // just returned. Previously stale edits from the Linear
        // Measurements panel (or a restored session with overrides)
        // could leak through and produce mismatches like
        // raw_ai.eaves_lf=72 but measurements.eaves_lf=5.
        const r = data.raw_ai;
        data.measurements = {
          ...data.measurements,
          siding_sqft: totals.siding_sqft,
          siding_with_openings_sqft: totals.siding_sqft,
          _ai_gable_sqft: totals._ai_gable_sqft,
          _ai_dormer_sqft: totals._ai_dormer_sqft,
          eaves_lf: Number(r.eaves_lf) || data.measurements.eaves_lf || 0,
          rakes_lf: Number(r.rakes_lf) || data.measurements.rakes_lf || 0,
          starter_lf:
            Number(r.starter_lf) ||
            Number(r.eaves_lf) ||
            data.measurements.starter_lf ||
            0,
          outside_corner_lf:
            Number(r.outside_corner_lf) ||
            data.measurements.outside_corner_lf ||
            0,
          inside_corner_lf:
            Number(r.inside_corner_lf) ||
            data.measurements.inside_corner_lf ||
            0,
        };
        // Lines came back from the backend with the OLD tiny qty — flag
        // dirty so Apply re-runs /measure/map with the corrected
        // measurements. ISS apply already re-derives from measurements
        // directly, so that path is fine without /measure/map.
        setWallsDirty(true);
      } else {
        setWallsDirty(false);
      }
      setPreview(data);
      setAnnotDirtySinceRun(false); // fresh run folded the annotations in
      // Inline Elevation Sheets panel probes once on mount — tell it a
      // run just completed so the sheets appear without a page reload.
      window.dispatchEvent(new CustomEvent("ai-run-completed", { detail: { estimateId } }));
      setRunError(null);   // Iter 79j.30 — clear any prior failure banner
      setRunErrorMeta(null);
      // Iter 57: auto-apply Claude's per-photo elevation guesses to
      // any photo that isn't already explicitly tagged. Saves the
      // contractor 4-8 dropdown taps per measurement. Manual tags
      // always win — we only fill blanks.
      const aiPhotos = data?.measurements?._ai_photos || data?.raw_ai?.photos || [];
      if (aiPhotos.length > 0 && photoUrls.length > 0) {
        setPhotoAnnotations((prev) => {
          const next = { ...prev };
          let autoTagged = 0;
          aiPhotos.forEach((ap) => {
            const idx = Number(ap?.index);
            if (!Number.isFinite(idx) || idx < 0 || idx >= photoUrls.length) return;
            const name = photoUrls[idx];
            const claudeElev = ap?.elevation;
            if (!claudeElev) return;
            const cur = next[name] || {};
            if (cur.elevation && cur.elevation !== "") return; // user tag wins
            const conf = Number(ap?.elevation_confidence) || 0;
            if (conf < 40) return; // skip very low-confidence guesses
            next[name] = { ...cur, elevation: claudeElev, _auto: true };
            autoTagged += 1;
          });
          if (autoTagged > 0) {
            toast.success(`Auto-tagged ${autoTagged} elevation${autoTagged > 1 ? "s" : ""} from AI`);
          }
          return next;
        });
      }
    } catch (e) {
      // Iter 79j.30 — Toasts get covered by the modal + auto-dismiss.
      // Surface the error as a persistent inline banner. Budget
      // exceeded is common enough (Emergent LLM Key runs out) that it
      // gets its own recognizable copy + link to the fix.
      // Iter 79j.44 — Capture stage + elapsed so the banner tells the
      // user WHICH phase failed and how long it took. Drop the
      // transient toast since the modal-body banner is persistent and
      // Retry-actionable.
      const msg = e?.response?.data?.detail || e?.message || "AI measure failed";
      const elapsedMs = runStartTsRef.current ? Date.now() - runStartTsRef.current : 0;
      setRunError(String(msg));
      setRunErrorMeta({
        stage: e?._stage || liveStage || busyStage || "unknown",
        elapsedMs,
        kind: e?._kind || "",
      });
    } finally {
      setBusy(false);
      setBusyStage("");
    }
  };

  const apply = async () => {
    if (!preview?.measurements) return;
    // Iter 79j.51 — Zero-data safety net. Distinct from the orphan-wall
    // partial-data warning below. When Phase B (reconciliation) fails
    // entirely, the aggregator still emits a placeholder measurements
    // object with 0 walls / 0 openings. Applying that writes silently-
    // wrong data (extrapolated outside-corner LF from a nonexistent
    // footprint) into the customer quote. Hard-block, don't warn.
    // Iter 79j.52 — Signals live in `raw_ai` (walls/dormers/openings
    // arrays) and `measurements.siding_sqft` / `eaves_lf`, NOT in
    // `measurements.walls` (which was never populated). The prior
    // predicate was permanently false-positive-blocking successful
    // runs; the runtime guard would trigger even when reconciliation
    // succeeded. Trust siding sqft + raw_ai geometry counts instead.
    const ra = preview?.raw_ai || {};
    if (ra._reconciliation_error) {
      toast.error(
        "Reconciliation failed — Apply is disabled. " +
        "Use Retry Reconciliation on the failed run first."
      );
      return;
    }
    const m = preview?.measurements || {};
    const raWalls = Array.isArray(ra.walls) ? ra.walls.length : 0;
    const raDormers = Array.isArray(ra.dormers) ? ra.dormers.length : 0;
    const raOpenings = Array.isArray(ra.openings) ? ra.openings.length : 0;
    const sidingSqft = Number(m.siding_sqft || 0);
    const eavesLf = Number(m.eaves_lf || 0);
    const hasReconciledFootprint =
      raWalls > 0 || raDormers > 0 || raOpenings > 0 || sidingSqft > 0 || eavesLf > 0;
    if (!hasReconciledFootprint) {
      toast.error(
        "Reconciliation produced no measurement — Apply is disabled. " +
        "Use Retry Reconciliation on the failed run first."
      );
      return;
    }
    // Iter 79j.44 — Orphan-wall safety net. If Phase A left one of the
    // 4 cardinal walls with no direct-view coverage, warn the user
    // BEFORE writing numbers into the estimate. Their dimensions are
    // extrapolated, so a silent Apply lets un-measured walls slip into
    // a customer-facing quote. Bypass via ?window.confirm().
    const orphaned = preview?.measurements?._ai_orphaned_walls || [];
    if (orphaned.length > 0) {
      const ok = window.confirm(
        `This takeoff has unmeasured walls: ${orphaned.join(", ")}.\n\n` +
        `Their dimensions are extrapolated from the walls Claude could see. ` +
        `Apply anyway? (You can also close this dialog, re-shoot the ` +
        `flagged elevations, and Re-Run first.)`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      let toApply = preview;
      // If the contractor edited wall geometry, refresh the line items
      // via /measure/map so Charter Oak qty etc. reflect the override
      // before the page merges them into the estimate.
      if (wallsDirty) {
        try {
          const { data } = await api.post("/measure/map", {
            measurements: preview.measurements,
          });
          toApply = { ...preview, lines: data.lines || preview.lines };
        } catch {
          // Non-fatal: fall back to original lines if /measure/map fails.
        }
      }

      // RULING B (2026-07-26): the flat ceil(sqft/4) swap math RETIRED.
      // The toggles are a thin alias — they set gable/dormer surfaces to
      // SHAKE on the per-elevation breakdown and re-derive the lines via
      // the SAME conventions path (/measure/map) a hand-set profile uses.
      // Equivalence by construction: toggle ≡ hand-set profile.
      if (quoteGablesAsShake || quoteDormersAsShake) {
        const meas = { ...(toApply.measurements || {}) };
        const perElev = (meas._per_elevation_breakdown || []).map((e) => ({ ...e }));
        const per = { ...(meas._per_profile_sqft || {}) };
        let moved = false;
        const setSurfaceShake = (e, kind) => {
          const sqft = Number(e[`${kind}_sqft`]) || 0;
          if (sqft <= 0) return;
          const from = e[`${kind}_profile`] || "lap";
          if (from === "shake") return;
          e[`${kind}_profile`] = "shake";
          per[from] = Math.max(0, (Number(per[from]) || 0) - sqft);
          per.shake = (Number(per.shake) || 0) + sqft;
          moved = true;
        };
        for (const e of perElev) {
          if (quoteGablesAsShake) setSurfaceShake(e, "gable");
          if (quoteDormersAsShake) setSurfaceShake(e, "dormer");
        }
        if (moved) {
          const newMeas = { ...meas, _per_elevation_breakdown: perElev, _per_profile_sqft: per };
          try {
            const { data } = await api.post("/measure/map", { measurements: newMeas });
            toApply = { ...toApply, measurements: newMeas, lines: data.lines || toApply.lines };
            toast.success("Gable/dormer surfaces set to SHAKE on the breakdown — lines re-derived via the conventions path (sealed shake waste applies)");
          } catch {
            toast.error("Could not re-derive lines for the shake override — applied without it. Set the profile on the breakdown card instead.");
          }
        } else {
          toast.info("Shake override: those surfaces are already shake (or carry no ft²) — nothing to move.");
        }
      }

      // Iter 78s — stash the rendered elevation drawings (with any
      // contractor nudges + roof overrides) on `measurements._ai_elevations`
      // so the customer Quote PDF can embed them as HOVER-style takeoff
      // sheets. The shape is the same one `ElevationDrawing` consumes.
      try {
        const elevs = buildElevationsFromAIMeasure({
          walls: preview.raw_ai?.walls,
          openings: preview.raw_ai?.openings,
          avg_wall_height_ft: preview.measurements?._ai_avg_wall_height_ft,
        });
        const edits = preview.measurements?._ai_elevation_edits || {};
        const merged = elevs.map((e) => {
          const editsForElev = edits[e.label] || {};
          const opEdits = editsForElev.openings || {};
          return {
            ...e,
            roof_style: editsForElev.roof_style || e.roof_style,
            openings: e.openings.map((op) =>
              opEdits[op.id]
                ? { ...op, x_pct: opEdits[op.id].x_pct, y_pct: opEdits[op.id].y_pct }
                : op
            ),
          };
        });
        if (merged.length) {
          // Iter 78u — write to source-keyed bucket so Compare modal can
          // surface drift across multiple measurement sources. Keep
          // `_ai_elevations` populated with the most recent set (used by
          // the customer Quote PDF).
          const prevBySource =
            toApply.measurements?._ai_elevations_by_source || {};
          toApply = {
            ...toApply,
            measurements: {
              ...(toApply.measurements || {}),
              _ai_elevations: merged,
              _ai_elevations_by_source: { ...prevBySource, ai_photo: merged },
            },
          };
        }
      } catch {
        /* non-fatal */
      }

      // Pass the full preview {measurements, lines, vero_openings, raw_ai}
      // so the page can choose how to merge. ISS uses just measurements;
      // siding/windows merge `lines` directly.
      await onApply(toApply);
      toast.success("AI measurements applied — verify all quantities before quoting");
      // Iter 56g: KEEP + FLUSH the session after Apply.
      // Previously (Iter 50) we deleted the session on Apply. That made
      // logout/login lose everything (photos, annotations, target pin,
      // wall edits) even though re-applying is idempotent. Now we
      // proactively SAVE the session before closing so even a quick
      // Apply (< 1s after AI Measure) gets persisted ahead of the
      // debounced autosave. Session is cleared only by Start Over.
      if (estimateId) {
        try {
          await api.put(`/measure/sessions/${estimateId}`, {
            estimate_id: estimateId,
            photo_urls: photoUrls,
            reference_dim: refDim,
            brick_course_in: brickCourse && parseFloat(brickCourse) > 0 ? parseFloat(brickCourse) : null,
            siding_exposure_in: sidingExposure && parseFloat(sidingExposure) > 0 ? parseFloat(sidingExposure) : null,
            wall_height: wallHeight,
            siding_pct: sidingPct,
            overhang_in: Number(overhangIn ?? 12),
            preview,
            photo_annotations: photoAnnotations,
          });
        } catch {
          // non-fatal — local state still good; the next autosave will
          // catch up if the modal stays open.
        }
      }
      // Close the modal but KEEP local state — re-opening AI Measure
      // lets the contractor add more photos / refine more values without
      // starting over. State is wiped only when the user explicitly
      // cancels via the "Start Over" button.
      setOpen(false);
      // Iter 79j.63 — Same re-arm as closeAll(). Apply also closes the
      // modal; if local state is later cleared before the user
      // reopens, we want the session-check effect to re-fire and
      // surface the Resume banner from the freshly-persisted server
      // doc. Symmetric with closeAll — every path that flips
      // `open=false` must reset the latch.
      setSessionChecked(false);
    } catch (e) {
      toast.error(e.message || "Apply failed");
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => {
    if (busy) return;
    // "Cancel" / X button: just hide the modal. State (photos, AI result,
    // refinements) is preserved so re-opening picks up where we left off.
    //
    // Iter 56f: flush the autosave IMMEDIATELY before closing. The 1-second
    // debounce was getting cancelled if the contractor closed the modal
    // within 1s of uploading photos / saving annotations — those changes
    // would silently never reach MongoDB and the "Resume" prompt wouldn't
    // appear when the contractor came back. Fire-and-forget; local state
    // is the source of truth either way.
    // Iter 79j.53 — Never persist a failed preview on close. Same
    // guard as the debounced autosave: the run doc is the retry
    // target, the session's job is to remember success only. A
    // failed preview on close would silently overwrite the last-good
    // one and bury a reconciled house.
    const previewIsFailure = !!(preview && preview?.raw_ai?._reconciliation_error);
    if (estimateId && (photoUrls.length > 0 || preview != null) && !previewIsFailure) {
      api
        .put(`/measure/sessions/${estimateId}`, {
          estimate_id: estimateId,
          photo_urls: photoUrls,
          reference_dim: refDim,
          brick_course_in: brickCourse && parseFloat(brickCourse) > 0 ? parseFloat(brickCourse) : null,
          siding_exposure_in: sidingExposure && parseFloat(sidingExposure) > 0 ? parseFloat(sidingExposure) : null,
          wall_height: wallHeight,
          siding_pct: sidingPct,
          overhang_in: Number(overhangIn ?? 12),
          preview,
          photo_annotations: photoAnnotations,
        })
        .catch(() => { /* non-fatal */ });
    }
    setOpen(false);
    // Iter 79j.63 — Re-arm the session-check latch on close so the
    // next modal open re-fetches the server session doc. Before this
    // fix, `sessionChecked` stayed `true` for the lifetime of the
    // mount, meaning any transient local-state loss (photoUrls
    // silently cleared to [] by some code path, e.g. the Jul 7 2026
    // Refine on Photo incident on EST-910869) became unrecoverable:
    // close+reopen the modal, sessionChecked was still true, GET
    // /measure/sessions was skipped, resumePrompt stayed false, the
    // Resume banner never appeared even though the DB doc was intact.
    // Resetting here means the next open always re-syncs against the
    // server — the DB is the source of truth for cross-open recovery.
    setSessionChecked(false);
    setGateAck(false);
  };

  const conf = preview?.measurements?._ai_scale_confidence || "low";
  // STEP 4 — CONFIDENCE GATE (findings 4 + 9a, ruled 2026-08-01): loud
  // flag + explicit acknowledgment before Apply when any read is a
  // suggestion (substituted height, wall confidence <50, low scale).
  const confidenceGate = preview?.measurements?._confidence_gate || null;
  const gateBlocked = !!confidenceGate && !gateAck;
  const confColor =
    conf === "high"
      ? "text-[var(--success)] border-[var(--success)] bg-green-50"
      : conf === "medium"
      ? "text-[#D97706] border-[#D97706] bg-[var(--hint-bg)]"
      : "text-[var(--danger-text)] border-[#DC2626] bg-red-50";

  return (
    <div data-testid="ai-measure" className="w-full">
      <button
        type="button"
        className="w-full justify-center px-3 py-1.5 bg-[var(--surface)] text-[var(--ai)] border border-[var(--ai)] hover:bg-[var(--surface-muted)] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
        onClick={() => setOpen(true)}
        data-testid="ai-measure-btn"
        title={preview ? "Resume AI measure session — add more photos or refine" : "AI photo measure — upload 2-8 phone photos of the house (+ optional aerial)"}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {preview ? t("intake.aiMeasureResume") : t("intake.aiMeasure")}
      </button>

      {/* SEND-188 ITEM 2 (Howard ruled 2026-08-14) — THE SECOND MEASURE
          BUTTON IS RETIRED. THE DOOR IS GONE; THE CODE IS NOT.
          `measure-without-ai-btn` and its refusal line stood here from
          SEND-151 to SEND-188. The merge did not delete a capability: the
          hand lane is the ordinary path on the one screen now — every
          photo on the job is listed there (the SAME server route this
          button's picker read), each row carries the per-photo AI choice,
          and a photo marked hand-only opens the SAME editor with no AI
          call. THE SENTENCE SURVIVED WITH THE DOOR: the server's own
          "no photo is on this estimate yet — upload one and this door
          opens" is printed at `one-door-photo-list-empty`, still verbatim
          and still never composed here, so SEND-179's ungate is intact —
          it is the same refusal on a different surface, not a silence.
          THE PICKER BELOW IS KEPT IN THE TREE AND NAMED RETIRED, per the
          removal rule: nothing sets `noAiPickerOpen` true any more. A
          later send deletes it if nothing needs it. See
          `memory/retired_doors_register.md`. */}

      {/* RETIRED DOOR'S SCREEN — KEPT IN THE TREE ON PURPOSE (SEND-188).
          This is the picker the second Measure button opened. Nothing sets
          `noAiPickerOpen` true any longer, so it does not render; it is
          left standing because SEND-132's precedent is that a removal is
          scoped to the door, and because its own route
          (`/photo-takeoff/photos`) is still the source of the one screen's
          photo list. Registered in `memory/retired_doors_register.md` with
          what would delete it. */}
      {noAiPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setNoAiPickerOpen(false)} data-testid="measure-without-ai-backdrop">
          <div className="bg-[var(--surface)] max-w-lg w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()} data-testid="measure-without-ai-modal">
            <div className="px-3 py-2 bg-[var(--bar-bg)] flex items-center justify-between">
              <span className="text-white text-xs font-bold uppercase tracking-wider">
                Measure without AI — pick a photo
              </span>
              <button type="button" onClick={() => setNoAiPickerOpen(false)}
                className="text-white hover:opacity-80" data-testid="measure-without-ai-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-3 py-1.5 bg-[#EFF6FF] border-b border-[#3B82F6] text-[10px] text-[var(--ink-2)] leading-snug"
              data-testid="measure-without-ai-note">
              No AI call is made here. You get the two-tap or typed scale, the
              STARTER and FASCIA lines you draw, and a siding zone you draw by
              hand. No annotate step and no proposals. <b>Run AI Measure stays
              where it is and stays optional.</b>
            </div>
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-auto">
              {noAiPhotos.map((p, i) => (
                <button key={p.photo_key} type="button"
                  onClick={() => openTakeoff(p.photo_key)}
                  className="border border-[var(--border)] hover:border-[var(--ink)] text-left overflow-hidden bg-[var(--surface-muted)]"
                  data-testid={`measure-without-ai-photo-${i}`}
                  title={`Draw on this photo — ${(p.from || []).join(" · ")}`}>
                  <div className="aspect-video">
                    <img src={p.url} alt={p.photo_key} className="w-full h-full object-cover" />
                  </div>
                  <div className="px-1.5 py-1 text-[9px] uppercase tracking-wider font-bold text-[var(--ink-2)] flex items-center justify-between gap-1">
                    <span>photo {i + 1}</span>
                    <span data-testid={`measure-without-ai-photo-state-${i}`}>
                      {p.marks > 0 ? `${p.marks} mark${p.marks === 1 ? "" : "s"}` : "no marks"}
                      {p.has_scale ? " · scaled" : ""}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {/* SEND-166 item 1 — THE PICKER KEEPS A CLEAR WAY BACK to the
                estimate page, for when he is actually finished. Otherwise
                one trap is traded for another. */}
            <div className="px-3 py-2 border-t border-[var(--border)] flex items-center justify-between gap-2">
              <span className="text-[10px] text-[var(--muted)]">
                {noAiPhotos.length} photo{noAiPhotos.length === 1 ? "" : "s"} · walk them with NEXT inside the editor
              </span>
              <button type="button" onClick={() => setNoAiPickerOpen(false)}
                data-testid="measure-without-ai-back-to-estimate"
                className="px-3 py-1 border border-[var(--ink)] bg-[var(--surface)] text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--surface-muted)]">
                Done — back to the estimate page
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeAll}
          data-testid="ai-measure-backdrop"
        >
          <div
            className="bg-[var(--surface)] max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="ai-measure-modal"
          >
            {/* Iter 79j.57c — Onboarding checklist overlay. Opens the
                first time a user reaches AI Measure (localStorage
                remembers the dismissal) and can be re-opened from the
                purple "Tips" button in the header. Explains Howard's
                marker SOP so 8-photo runs don't ship with corners-only
                markers (which the reconciler treats as amber). */}
            {showOnboarding && (
              <div
                className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6"
                onClick={dismissOnboarding}
                data-testid="ai-measure-onboarding-backdrop"
              >
                <div
                  className="bg-[var(--surface)] max-w-lg w-full flex flex-col shadow-2xl border border-[var(--border)]"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="ai-measure-onboarding-modal"
                >
                  <div className="bg-[var(--ai)] text-white px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" />
                      <div className="font-bold uppercase tracking-wider text-[11px]">
                        Photo checklist — read this once
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={dismissOnboarding}
                      className="text-white/90 hover:text-white"
                      aria-label="Close onboarding"
                      data-testid="ai-measure-onboarding-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-5 space-y-3 text-[12px] text-[var(--ink)] leading-relaxed">
                    {/* Iter 79j.61 — Quantified contextual warning.
                        Appears at the top of the checklist only when
                        the modal was auto-opened because the user has
                        photos but no scale refs (Howard 2026-07-07:
                        "quantified warnings get obeyed; vague ones
                        get dismissed"). */}
                    {unanchoredNudgeActive && (
                      <div
                        className="p-3 border border-[#F59E0B] bg-[#FEF3C7] text-[#78350F] text-[12px]"
                        data-testid="ai-measure-onboarding-unanchored-nudge"
                      >
                        <div className="font-bold uppercase tracking-wider text-[10px] mb-1 text-[#B45309]">
                          Your uploaded photos have no reference markers
                        </div>
                        Photos without reference markers can drift <b>25–90% on dormers
                        and upper features</b>. Add a <b>WALL REF</b> or <b>WIN REF</b> to
                        each elevation for the strongest ref-anchored accuracy
                        (AI reads — not tape).
                      </div>
                    )}
                    <p className="text-[var(--ink-2)]">
                      The AI measures from what it can <b>see and scale</b>. Follow this
                      checklist and 4–6 photos will beat 10 sloppy ones:
                    </p>
                    <ul className="space-y-2.5">
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-markers">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>3 markers per elevation.</b> Left side, right side, and a wall
                          reference dimension (a taped 4&apos;&nbsp;or&nbsp;5&apos; strip works). The
                          reconciler uses the two edge markers to check for lens distortion
                          and the wall-ref to lock scale.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-ref-plane">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Wall refs live on the MAIN wall plane.</b> Draw the reference
                          across the widest full span of the wall itself — never across a
                          dormer, pop-up, or porch face. A ref that crosses planes scales
                          the wrong surface and drags the whole wall&apos;s confidence down.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-dormers">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>One marker per dormer face.</b> Stand square to the dormer if you
                          can. Without a direct read, width is back-solved from the window
                          and comes in <span className="text-[#F59E0B] font-bold">amber</span>.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-pin-windows">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Pin your windows — especially any window up in the roof.</b>{" "}
                          A yellow pin makes that window ground truth: the AI enumerates it
                          on the correct face (dormer vs. wall) instead of guessing — which
                          is exactly where phantom window collisions come from.
                        </span>
                      </li>
                      {/* Iter 79j.69 — SOP line from the red-house exam
                          (right wall: shrubs hid the bottom courses, count
                          declined, cross-plane fallback read +1.5 ft). */}
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-short-walls">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Short or cluttered walls:</b> shoot close enough
                          that the bottom siding courses are visible — the AI counts courses
                          to verify height, and it can&apos;t count what shrubs hide.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-corners">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Shoot each corner from both walls.</b>{" "}
                          Course counts anchor at corners, and only matching same-corner counts
                          from two photos earn the enumerated (tape-provable) tier — everything
                          else stays an amber estimate. Frame the corner so the wall you&apos;re
                          measuring fills most of the shot, and still cover every wall.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-elevations">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>All 4 elevations, square-on.</b> Front, right, back, left — stand
                          centered and shoot the wall flat, not from a corner angle. An
                          oblique frame gets re-read as a corner view and the wall falls back
                          to an assumed width. Any missing wall is flagged in the result and
                          comes in as an estimate, not a measurement.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-aerial">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Add the free aerial view.</b> One click, no extra cost — it
                          confirms footprint and catches walls the ground photos miss.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="ai-measure-onboarding-tip-reference">
                        <Check className="w-4 h-4 text-[var(--success)] flex-shrink-0 mt-0.5" />
                        <span>
                          <b>Reference dimension counts for a lot.</b> A single accurate
                          reference (a wall width you measured, or a standard 3&apos;0&quot; door
                          you know) flips scale confidence from LOW to HIGH.
                        </span>
                      </li>
                    </ul>
                    <div className="text-[10px] text-[var(--muted)] italic pt-1 border-t border-[var(--border)]">
                      You can re-open this checklist any time from the <b>Tips</b> button in
                      the AI Measure header.
                    </div>
                    <div
                      className="text-[10px] text-[var(--ink)] pt-1"
                      data-testid="ai-measure-onboarding-run-readiness-note"
                    >
                      <b>Every property after this one:</b> the <b>Run Readiness</b> checklist
                      at the top of the photo screen is the per-property SOP — the same short
                      list whether it&apos;s your first house or your fiftieth.
                    </div>
                  </div>
                  <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={dismissOnboarding}
                      className="px-4 py-2 bg-[var(--ai)] text-white hover:bg-[#6D28D9] text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid="ai-measure-onboarding-dismiss"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Got it, don&apos;t show again
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Iter 79j.57d — Re-run confirmation. Only appears when a
                successful reconciliation exists; specific stats make
                the warning concrete instead of generic. */}
            {rerunConfirm && (() => {
              const ra = preview?.raw_ai || {};
              const dormers = Array.isArray(ra.dormers)
                ? ra.dormers
                : (ra.dormer ? [ra.dormer] : []);
              const sidingSqft = Math.round(preview?.measurements?.siding_sqft || 0);
              const wallCount = Array.isArray(ra.walls) ? ra.walls.length : 0;
              const openingCount = Array.isArray(ra.openings) ? ra.openings.length : 0;
              return (
                <div
                  className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6"
                  onClick={() => setRerunConfirm(false)}
                  data-testid="ai-measure-rerun-confirm-backdrop"
                >
                  <div
                    className="bg-[var(--surface)] max-w-md w-full flex flex-col shadow-2xl border border-[#F59E0B]"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ai-measure-rerun-confirm-modal"
                  >
                    <div className="bg-[#FEF3C7] px-4 py-3 flex items-center gap-2 border-b border-[#F59E0B]">
                      <AlertTriangle className="w-4 h-4 text-[#B45309]" />
                      <div className="font-bold uppercase tracking-wider text-[11px] text-[#78350F]">
                        Replace the active reconciled result?
                      </div>
                    </div>
                    <div className="p-5 space-y-3 text-[12px] text-[var(--ink)] leading-relaxed">
                      <p>
                        This estimate has a <b>successful reconciled run</b> —{" "}
                        <b data-testid="ai-measure-rerun-confirm-walls">{wallCount} walls</b>,{" "}
                        <b data-testid="ai-measure-rerun-confirm-dormers">{dormers.length} dormer{dormers.length === 1 ? "" : "s"}</b>,{" "}
                        <b data-testid="ai-measure-rerun-confirm-openings">{openingCount} openings</b>
                        {sidingSqft > 0 && (
                          <>, <b data-testid="ai-measure-rerun-confirm-siding">{sidingSqft} sqft siding</b></>
                        )}.
                      </p>
                      <p className="text-[var(--ink-2)]">
                        Re-running will <b>replace it as the active result</b>. The prior run
                        stays in Debug View history — but this estimate&apos;s applied
                        measurements, line items and 3D render will switch to the new one.
                      </p>
                      {dormers.length > 0 && (
                        <ul className="text-[11px] text-[var(--muted)] list-disc pl-5 space-y-0.5" data-testid="ai-measure-rerun-confirm-dormer-list">
                          {dormers.map((d, i) => (
                            <li key={i}>
                              Dormer {i + 1}: face <b className="text-[var(--ink-2)]">{d.face || "—"}</b>,
                              width <b className="text-[var(--ink-2)]">{d.width_ft != null ? `${Number(d.width_ft).toFixed(1)} ft` : "—"}</b>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setRerunConfirm(false)}
                        className="px-4 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--surface-muted)] text-[11px] font-bold uppercase tracking-wider"
                        data-testid="ai-measure-rerun-confirm-cancel"
                      >
                        Keep current
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRerunConfirm(false); runMeasure(); }}
                        className="px-4 py-2 bg-[#B45309] text-white hover:bg-[#92400E] text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                        data-testid="ai-measure-rerun-confirm-proceed"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Yes, replace
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* SEND-212 (Howard ruled 2026-08-14) — THE ASK, WHEN THE PHOTO
                CARRIES CONFIRMED ITEMS. The sentence is the SERVER'S, word
                for word, arriving with its 409: the screen composes no
                warning of its own. NO changes nothing at all — nothing has
                been deleted at this point. YES deletes the photo AND every
                mark on it, and the quantities those marks carried drop with
                them. */}
            {photoDeleteAsk && (
              <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-6"
                onClick={() => setPhotoDeleteAsk(null)}
                data-testid="photo-delete-confirm-backdrop">
                <div className="bg-[var(--surface)] max-w-md w-full shadow-2xl border border-[#DC2626]"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="photo-delete-confirm-modal">
                  <div className="bg-[#FEE2E2] px-4 py-3 flex items-center gap-2 border-b border-[#DC2626]">
                    <AlertTriangle className="w-4 h-4 text-[#B91C1C]" />
                    <div className="font-bold uppercase tracking-wider text-[11px] text-[#7F1D1D]"
                      data-testid="photo-delete-confirm-count">
                      {photoDeleteAsk.count} confirmed item{photoDeleteAsk.count === 1 ? "" : "s"} on this photo
                    </div>
                  </div>
                  <div className="p-5 space-y-2 text-[12px] text-[var(--ink)] leading-relaxed">
                    <p className="font-bold" data-testid="photo-delete-confirm-sentence">
                      {photoDeleteAsk.ask}
                    </p>
                    <p className="text-[11px] text-[var(--ink-2)]"
                      data-testid="photo-delete-confirm-note">
                      {photoDeleteAsk.note}
                    </p>
                  </div>
                  <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setPhotoDeleteAsk(null)}
                      className="px-4 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] text-[11px] font-bold uppercase tracking-wider"
                      data-testid="photo-delete-confirm-no">
                      {t("oneDoor.deleteNo")}
                    </button>
                    <button type="button"
                      disabled={photoDeleteBusy === photoDeleteAsk.name}
                      onClick={() => deleteJobPhoto(photoDeleteAsk.name, true)}
                      className="px-4 py-2 bg-[#DC2626] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
                      data-testid="photo-delete-confirm-yes">
                      {t("oneDoor.deleteYes")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Iter 79j.64 — Fix 3: destructive-action confirm. Guards
                Start Over and Start Fresh (recovery banner) with a
                SPECIFIC data-loss statement — not a generic "are you sure".
                SEND-212 — THE PER-PHOTO BRANCH IS GONE FROM HERE: a photo
                delete is its own door with its own ask, and two modals that
                could both delete a photo is how the two lists drifted. */}
            {destructiveConfirm && (() => {
              const dc = destructiveConfirm;
              const isFresh = dc.kind === "start_fresh";
              // What exactly gets destroyed:
              const localPhotos = photoUrls.length;
              const localMarkers = photoUrls.filter((n) => !annotEmpty(photoAnnotations[n])).length;
              const serverPhotos = pendingSessionMeta?.photos || 0;
              const serverHasResult = !!pendingSessionMeta?.hasResult;
              const serverMarkers = pendingSessionMeta?.annotated || 0;
              const title = isFresh
                ? "Permanently delete the saved session?"
                : "Wipe this AI Measure session?";
              return (
                <div
                  className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6"
                  onClick={() => setDestructiveConfirm(null)}
                  data-testid="ai-measure-destructive-confirm-backdrop"
                >
                  <div
                    className="bg-[var(--surface)] max-w-md w-full flex flex-col shadow-2xl border border-[#DC2626]"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ai-measure-destructive-confirm-modal"
                  >
                    <div className="bg-[#FEE2E2] px-4 py-3 flex items-center gap-2 border-b border-[#DC2626]">
                      <AlertTriangle className="w-4 h-4 text-[#B91C1C]" />
                      <div className="font-bold uppercase tracking-wider text-[11px] text-[#7F1D1D]">
                        {title}
                      </div>
                    </div>
                    <div className="p-5 space-y-3 text-[12px] text-[var(--ink)] leading-relaxed" data-testid="ai-measure-destructive-confirm-details">
                      {isFresh ? (
                        <p>
                          The server session holds{" "}
                          <b>{serverPhotos} photo{serverPhotos === 1 ? "" : "s"}</b>
                          {serverHasResult && <>, <b>a reconciled AI result</b></>}
                          {serverMarkers > 0 && <>, <b>markers on {serverMarkers} photo{serverMarkers === 1 ? "" : "s"}</b></>}
                          . Start Fresh <b>permanently deletes all of it from the server</b> — there is no undo.
                          If you want that work, click Cancel and hit Resume instead.
                        </p>
                      ) : (
                        <p>
                          This wipes{" "}
                          <b>{localPhotos} uploaded photo{localPhotos === 1 ? "" : "s"}</b>
                          {preview != null && <>, <b>the current AI result</b></>}
                          {localMarkers > 0 && <>, <b>markers on {localMarkers} photo{localMarkers === 1 ? "" : "s"}</b></>}
                          {" "}and <b>deletes the saved session from the server</b>. Prior runs stay in
                          Debug View history, but this working session cannot be recovered.
                        </p>
                      )}
                    </div>
                    <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDestructiveConfirm(null)}
                        className="px-4 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--surface-muted)] text-[11px] font-bold uppercase tracking-wider"
                        data-testid="ai-measure-destructive-confirm-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={proceedDestructive}
                        className="px-4 py-2 bg-[#DC2626] text-white hover:bg-[#B91C1C] text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                        data-testid="ai-measure-destructive-confirm-proceed"
                      >
                        <X className="w-3.5 h-3.5" />
                        Yes, delete it
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                <div>
                  <div className="font-heading text-lg">Photo Measure</div>
                  <div className="text-xs opacity-90 mt-0.5">
                    One screen · you pick who reads each photo · {MODEL_PICKER_LABELS[modelChoice] || MODEL_PICKER_LABELS[VALIDATED_MODEL_KEY]}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Iter 79j.57c — Re-open the onboarding checklist on
                    demand. Small, unobtrusive; the checklist itself
                    only auto-opens the FIRST time (localStorage). */}
                <button
                  type="button"
                  className="text-white/90 hover:text-white p-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold"
                  onClick={() => setShowOnboarding(true)}
                  data-testid="ai-measure-open-onboarding"
                  title="Show the photo-capture checklist"
                >
                  <HelpCircle className="w-4 h-4" />
                  Tips
                </button>
                <button
                  type="button"
                  className="text-white/90 hover:text-white"
                  onClick={closeAll}
                  aria-label="Close"
                  data-testid="ai-measure-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              {/* SEND-188 ITEM 2 (Howard ruled 2026-08-14) — ONE WAY IN, ONE
                  EDITOR, AND ON EACH PHOTO HE PICKS WHETHER AI LOOKS AT IT.
                  This list is the SERVER'S OWN UNION of every photo on the
                  job — uploaded to this run, applied to the estimate,
                  already drawn on, already scaled — the same route the
                  retired picker read (`/photo-takeoff/photos`, which calls
                  nothing and spends nothing). It is here rather than beside
                  the upload grid because the upload grid only knows this
                  run's own list: a photo added by hand would otherwise have
                  no way in at all once the second door was gone.
                  AN EMPTY LIST PRINTS THE SERVER'S SENTENCE. No placeholder
                  photo is invented and no copy is composed here. */}
              <div className="mb-4 border border-[var(--ink)]" data-testid="one-door-photo-list">
                <div className="px-3 py-2 bg-[var(--bar-bg)] flex items-center justify-between gap-2">
                  <span className="text-white text-[11px] font-bold uppercase tracking-wider">
                    {t("oneDoor.listTitle")}
                  </span>
                  {/* SEND-213 (Howard ruled 2026-08-14) — THE COUNT LIVES IN
                      THIS HEADER, and there is NO SECOND COUNT: the lower
                      box's own count went with the box. One number, and it
                      is the server's union — the same list the tiles below
                      are drawn from, so the two cannot disagree. */}
                  <span className="text-white/80 text-[10px] uppercase tracking-wider font-bold"
                    data-testid="one-door-photo-count">
                    {noAiPhotos.length} on this job
                    {files.length > 0 ? ` · ${files.length} uploading…` : ""}
                  </span>
                </div>
                {noAiPhotos.length === 0 && (
                  <div className="px-3 py-2 text-[10px] leading-snug text-[var(--ink-2)]"
                    data-testid="one-door-photo-list-empty">
                    {noAiNote}
                  </div>
                )}
                <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {noAiPhotos.map((p, i) => {
                      const pref = photoPrefs[p.photo_key];
                      const recorded = pref && typeof pref.ai_allowed === "boolean";
                      const aiAllowed = recorded ? pref.ai_allowed : null;
                      return (
                        <div key={p.photo_key}
                          className="border border-[var(--border)] bg-[var(--surface-muted)] flex flex-col"
                          data-testid={`one-door-photo-row-${i}`}>
                          <button type="button" onClick={() => openTakeoff(p.photo_key)}
                            className="text-left hover:opacity-90"
                            data-testid={`one-door-photo-open-${i}`}
                            title={`Draw on this photo — ${(p.from || []).join(" · ")}`}>
                            <div className="aspect-video bg-black/5">
                              <img src={p.url} alt={p.photo_key} className="w-full h-full object-cover" />
                            </div>
                            <div className="px-1.5 py-1 text-[9px] uppercase tracking-wider font-bold text-[var(--ink-2)] flex items-center justify-between gap-1">
                              {/* SEND-212 — THE FACE RIDES THE ROW. The top
                                  row is the only gallery now, so the wall
                                  this photo shows is printed here: the
                                  server's own call, the one the editor
                                  prints. No face means no face. */}
                              <span data-testid={`one-door-photo-face-${i}`}>
                                {p.face || t("oneDoor.faceNone")}
                              </span>
                              <span data-testid={`one-door-photo-state-${i}`}>
                                {p.marks > 0 ? `${p.marks} mark${p.marks === 1 ? "" : "s"}` : "no marks"}
                                {p.has_scale ? " · scaled" : ""}
                              </span>
                            </div>
                          </button>
                          <div className="px-1.5 pb-1 text-[9px] text-[var(--muted)] leading-snug"
                            data-testid={`one-door-photo-from-${i}`}>
                            {(p.from || []).join(" · ")}
                          </div>
                          {/* THE PER-PHOTO SWITCH — THE STORE'S FIRST WRITER,
                              AND IT IS THREE-STATE BECAUSE THE STORE IS.
                              "No choice recorded" is not hand-only and not
                              AI-allowed; it is what every photo taken before
                              today says, and clearing returns a photo to it. */}
                          <div className="px-1.5 pb-1.5 border-t border-[var(--border)] pt-1">
                            <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--ink-2)] mb-1"
                              data-testid={`one-door-ai-state-${i}`}>
                              {aiAllowed === true ? t("oneDoor.stateAi")
                                : aiAllowed === false ? t("oneDoor.stateHand")
                                : t("oneDoor.stateNone")}
                            </div>
                            <div className="flex items-center gap-1">
                              <button type="button" disabled={prefBusy === p.photo_key}
                                onClick={() => setPhotoAiPref(p.photo_key, true)}
                                data-testid={`one-door-ai-yes-${i}`}
                                className={`px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-wider ${
                                  aiAllowed === true
                                    ? "bg-[var(--ai)] text-white border-[var(--ai)]"
                                    : "bg-[var(--surface)] text-[var(--ai)] border-[var(--ai)]"}`}>
                                {t("oneDoor.aiMayRead")}
                              </button>
                              <button type="button" disabled={prefBusy === p.photo_key}
                                onClick={() => setPhotoAiPref(p.photo_key, false)}
                                data-testid={`one-door-ai-no-${i}`}
                                className={`px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-wider ${
                                  aiAllowed === false
                                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                    : "bg-[var(--surface)] text-[var(--ink)] border-[var(--ink)]"}`}>
                                {t("oneDoor.handOnly")}
                              </button>
                              {recorded && (
                                <button type="button" disabled={prefBusy === p.photo_key}
                                  onClick={() => setPhotoAiPref(p.photo_key, null)}
                                  data-testid={`one-door-ai-clear-${i}`}
                                  className="px-1.5 py-0.5 border border-[var(--border)] bg-[var(--surface)] text-[9px] font-bold uppercase tracking-wider text-[var(--muted)]">
                                  {t("oneDoor.noChoice")}
                                </button>
                              )}
                              {/* SEND-212 — CLEAR IS DELETE, and it is the
                                  ONLY delete on this screen. One click hits
                                  every store that still shows the photo; the
                                  ask, when the photo carries confirmed
                                  marks, is the SERVER'S refusal, printed. */}
                              <button type="button"
                                disabled={photoDeleteBusy === p.photo_key}
                                onClick={() => deleteJobPhoto(p.photo_key)}
                                data-testid={`one-door-photo-delete-${i}`}
                                title="Delete this photo from the job — its marks, its scale, this choice and its face tag go with it"
                                className="ml-auto px-1.5 py-0.5 border border-[var(--danger)] bg-[var(--surface)] text-[9px] font-bold uppercase tracking-wider text-[var(--danger)] disabled:opacity-50">
                                {t("oneDoor.clear")}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* SEND-213 (Howard ruled 2026-08-14) — ADD PHOTO LIVES
                        WITH THE PHOTOS. The last tile in the SAME row: click
                        it or drop images on it and they land in this row.
                        Not a second box, not a second gallery, no second
                        count. At the 9-photo cap it STAYS VISIBLE and says
                        why it is disabled — a control that vanishes reads as
                        a bug. */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); if (!photoCapped) setPhotoDragOver(true); }}
                      onDragLeave={() => setPhotoDragOver(false)}
                      onDrop={(e) => (photoCapped ? e.preventDefault() : dropPhotos(e))}
                      className={`border-2 border-dashed flex flex-col ${photoDragOver ? "border-[var(--ai)] bg-[#F0F9FF]" : "border-[var(--border)] bg-[var(--surface-muted)]"}`}
                      data-testid="one-door-add-tile">
                      <button type="button" disabled={photoCapped}
                        onClick={() => fileRef.current?.click()}
                        className="aspect-video w-full flex flex-col items-center justify-center gap-1 text-[var(--ai)] disabled:opacity-50 disabled:cursor-not-allowed"
                        title={photoCapped ? "9 photos is the cap on a job — delete one to add another" : "Add a photo to this job — click, or drop images here"}
                        data-testid="one-door-add-photo-btn">
                        <Plus className="w-7 h-7" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          {photoCapped ? "9 is the cap" : "add photo"}
                        </span>
                      </button>
                      <input ref={fileRef} type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment" multiple className="hidden"
                        onChange={pickFiles}
                        data-testid="ai-measure-file-input" />
                      <div className="px-1.5 pb-1 text-[9px] uppercase tracking-wider font-bold text-[var(--muted)] text-center"
                        data-testid="one-door-add-tile-note">
                        {photoCapped ? "delete one to add another" : "click or drop"}
                      </div>
                    </div>
                  </div>
                {/* SEND-265 (Howard ruled 2026-08-14) — TWO CONTROLS, UNDER
                    THE PHOTOS THEY ACT ON. "HAND ONLY for now. And yes —
                    apply the photos to the catalog." Each one is a single
                    server call that NAMES WHAT IT DID; neither composes a
                    sentence here, and neither runs a read. */}
                <div className="px-3 py-2 border-t border-[var(--border)] flex flex-wrap items-center gap-2"
                  data-testid="one-door-bulk-row">
                  {/* SEND-265 — THE GATE IS THE LIST THE TILES COME FROM.
                      The first build gated on `photoUrls`, the SESSION's own
                      list, so on a job whose photos came from the estimate
                      itself the tiles showed a photo while both controls sat
                      disabled (found in the browser,
                      test_reports/iteration_84.json). `noAiPhotos` is the
                      server's list — the one these buttons act on. */}
                  <button type="button" disabled={bulkBusy !== "" || noAiPhotos.length === 0}
                    onClick={handOnlyAllPhotos}
                    title="Mark every photo on this job HAND ONLY. It sets no photo to AI MAY READ, it runs no read, and it touches no mark, scale or geometry"
                    className="px-2 py-1 border border-[var(--ink)] bg-[var(--surface)] text-[10px] font-bold uppercase tracking-wider text-[var(--ink)] disabled:opacity-50"
                    data-testid="one-door-hand-only-all">
                    {bulkBusy === "hand" ? "…" : "hand only · all photos"}
                  </button>
                  <button type="button" disabled={bulkBusy !== "" || noAiPhotos.length === 0}
                    onClick={applyAllPhotosToCatalog}
                    title="Push the confirmed, scaled marks from every photo on this job onto this estimate's catalog lines. A photo with no scale or no confirmed marks is skipped and named; quantities are recomputed, never added twice"
                    className="px-2 py-1 border border-[var(--ai)] bg-[var(--surface)] text-[10px] font-bold uppercase tracking-wider text-[var(--ai)] disabled:opacity-50"
                    data-testid="one-door-apply-all-to-catalog">
                    {bulkBusy === "apply" ? "…" : "apply all photos to catalog"}
                  </button>
                  {bulkNote ? (
                    <span className="text-[9px] leading-snug text-[var(--ink-2)] w-full"
                      data-testid="one-door-bulk-note">{bulkNote}</span>
                  ) : null}
                  {bulkSkipped.length > 0 ? (
                    <ul className="w-full text-[9px] leading-snug text-[var(--warning-text)]"
                      data-testid="one-door-bulk-skipped">
                      {bulkSkipped.map((s) => (
                        <li key={s.photo_key}
                          data-testid={`one-door-bulk-skipped-${s.photo_key}`}>
                          <b>{s.photo_key}</b> — {s.named}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {/* SEND-251 — THE BOTTOM BLOCK OF THE PICKER IS HIDDEN, NOT
                    DELETED: the three lines he named sit in this one block,
                    behind the one flag. */}
                {PICKER_NOTE_SHOWN ? (
                <div className="px-3 py-1.5 border-t border-[var(--border)] text-[9px] leading-snug text-[var(--ink-2)]"
                  data-testid="one-door-photo-list-note">
                  {noAiPhotos.length > 0 && (
                    <div className="mb-1 font-bold uppercase tracking-wider text-[var(--ink)]"
                      data-testid="one-door-read-scope">
                      {(() => {
                        if (photoUrls.length === 0) {
                          return "No photo is on this run yet — a read has nothing to look at. "
                            + "Opening a photo below needs no read.";
                        }
                        const held = photoUrls.filter((n) => photoPrefs[n]?.ai_allowed === false).length;
                        const going = photoUrls.length - held;
                        return `A read would look at ${going} of ${photoUrls.length} on this run · ${held} held back as hand only`;
                      })()}
                    </div>
                  )}
                  The choice is recorded against the photo, not against the job, and
                  it changes no size, no kind and no geometry: <b>no basis reads it</b>.
                  Opening a photo marked hand-only makes no AI call — the two-tap or
                  typed scale, the lines you draw and a zone you draw yourself.
                  {/* SEND-204's line, MOVED HERE BY NAME (SEND-213): the box
                      it used to sit in is gone. Guided Capture is still
                      retired and the walk is still on Photo Takeoff — one
                      sentence, in the only photo block on the screen. */}
                  <div className="mt-1 font-bold uppercase tracking-wider text-[var(--ink)]"
                    data-testid="ai-measure-wizard-retired-note">
                    Step-by-step walk moved to Photo Takeoff — open a photo and it guides you, layer by layer.
                  </div>
                </div>
                ) : null}
              </div>
              {/* Iter 79j.45 — Soft health-warning banner. Only shown
                  when the health ping returned `ambiguous` (unknown
                  response format). The Run button STAYS enabled — a
                  broken health check must not lock the product. */}
              {aiHealth?.status === "ambiguous" && (
                <div
                  className="mb-4 p-3 border border-[#F59E0B] bg-[#FEF3C7] text-[#78350F] text-[11px] flex items-start gap-2"
                  data-testid="ai-measure-health-warning-banner"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--warning-text)]" />
                  <div className="flex-1">
                    <div className="font-bold uppercase tracking-wider text-[10px] mb-1">
                      AI health check inconclusive
                    </div>
                    <div className="whitespace-pre-wrap break-words">
                      {aiHealth.detail || "Health check returned an unexpected response — you can still run, but if the run fails, check the LLM proxy."}
                    </div>
                  </div>
                </div>
              )}
              {/* Iter 79j.30 — persistent run-error banner shown at the
                  very top of the modal body (above Resume) so a budget-
                  exceeded or worker failure ALWAYS surfaces, even when
                  the user has no preview yet. Budget errors get their
                  own actionable copy pointing at Profile → Universal Key. */}
              {runError && (
                <div
                  className="mb-4 p-3 border border-[var(--danger)] bg-[#FEE2E2] text-[#7F1D1D] text-[11px] flex items-start gap-2"
                  data-testid="ai-measure-run-error-banner"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--danger-text)]" />
                  <div className="flex-1">
                    <div className="font-bold uppercase tracking-wider text-[10px] mb-1 flex items-center justify-between">
                      <span>{
                        /Budget/i.test(runError)
                          ? "Universal Key budget exhausted"
                          : (runErrorMeta?.origin === "resume"
                              ? "Prior reconciliation failed"
                              : "AI Measure failed")
                      }</span>
                      <button
                        type="button"
                        className="text-[10px] font-normal text-[#7F1D1D] underline hover:no-underline"
                        onClick={dismissRunError}
                        data-testid="ai-measure-run-error-dismiss"
                      >
                        dismiss
                      </button>
                    </div>
                    {/Budget/i.test(runError) ? (
                      <div>
                        Your Emergent LLM Key balance is spent. Open the platform menu
                        in the top-right and go to <b>Profile → Universal Key → Add Balance</b>
                        {" "}(or toggle Auto Top-up). Once topped up, click Run AI Measure
                        again — nothing on this estimate is lost.
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap break-words">
                        {runErrorMeta?.origin === "resume" && (
                          <div className="mb-1 italic text-[#7F1D1D] opacity-80">
                            Restored from a previous session — no fresh call was made.
                            Click Retry Reconciliation below to try Phase B again.
                          </div>
                        )}
                        {runError}
                      </div>
                    )}
                    {/* Iter 79j.44 — Phase / elapsed / retry footer.
                        Never let a failed run disappear in a toast: we
                        stamp the stage the pipeline was in, wall-clock
                        elapsed since the user clicked Run, and give a
                        one-click Retry. */}
                    {runErrorMeta && (
                      <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-wider text-[#7F1D1D] opacity-80">
                        <span data-testid="ai-measure-run-error-stage">
                          Phase: <b>{runErrorMeta.stage || "unknown"}</b>
                        </span>
                        {runErrorMeta.elapsedMs != null && (
                          <span data-testid="ai-measure-run-error-elapsed">
                            Elapsed: <b>{Math.round((runErrorMeta.elapsedMs || 0) / 1000)}s</b>
                          </span>
                        )}
                        {runErrorMeta.kind && (
                          <span data-testid="ai-measure-run-error-kind">
                            Kind: <b>{runErrorMeta.kind}</b>
                          </span>
                        )}
                        {runErrorMeta.origin === "resume" && (
                          <span data-testid="ai-measure-run-error-origin">
                            Origin: <b>resumed session</b>
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setRunError(null); setRunErrorMeta(null); runMeasure(); }}
                        disabled={busy || photoUrls.length === 0}
                        className="px-3 py-1.5 bg-[#7F1D1D] text-white hover:bg-[#991B1B] text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="ai-measure-run-error-retry"
                      >
                        <RotateCcw className="w-3 h-3" /> Retry Run
                      </button>
                      {/* Iter 79j.51 — Reconcile-only retry button.
                          Only shown when the run failure was in Phase B
                          (reconciliation) AND we have a currentRunId
                          whose raw_per_photo is still on disk. Runs
                          Phase B alone against the saved extractions —
                          skips paying for Phase A vision again. */}
                      {currentRunId && /reconcil|BadGateway|502|Phase\s*B/i.test(runError || "") && (
                        <button
                          type="button"
                          onClick={() => retryReconcileOnly(currentRunId)}
                          disabled={busy}
                          className="px-3 py-1.5 bg-[#7F1D1D] text-white hover:bg-[#991B1B] text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="ai-measure-reconcile-only-retry"
                          title="Retry ONLY Phase B (reconciliation). Keeps Phase A's vision output — costs pennies instead of a full re-run."
                        >
                          <RotateCcw className="w-3 h-3" /> Retry Reconciliation
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* SEND-247 — THE ORANGE SAVED WORK FOUND BANNER IS HIDDEN,
                  NOT DELETED: header, RESUME and START FRESH together, behind
                  the one flag. Nothing about the recovery itself changed. */}
              {RESUME_BANNER_SHOWN && resumePrompt && (
                <div
                  className="mb-4 border-2 border-[#F59E0B] bg-[#FFFBEB]"
                  data-testid="ai-measure-resume-banner"
                >
                  {/* Iter 79j.64 — Fix 2: LOUD recovery banner. Fires when
                      the server holds a saved session but this device's
                      memory is empty — the exact state where a stray
                      "Start fresh" click destroys real work. Names the
                      specific contents so the choice is informed. */}
                  <div className="bg-[#F59E0B] px-3 py-1.5 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-white" />
                    <span className="text-white font-bold uppercase tracking-wider text-[11px]">
                      Saved work found on the server
                    </span>
                  </div>
                  <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-[#78350F] leading-relaxed" data-testid="ai-measure-recovery-details">
                      This device has nothing loaded, but the server holds your saved AI Measure session:{" "}
                      <b>{pendingSessionMeta?.photos || 0} photo{(pendingSessionMeta?.photos || 0) === 1 ? "" : "s"}</b>
                      {pendingSessionMeta?.hasResult && <>, <b>a reconciled AI result</b></>}
                      {(pendingSessionMeta?.annotated || 0) > 0 && (
                        <>, <b>markers on {pendingSessionMeta.annotated} photo{pendingSessionMeta.annotated === 1 ? "" : "s"}</b></>
                      )}
                      . <b>Resume</b> restores everything. <b>Start fresh</b> permanently deletes it.
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={resumeSession}
                        className="px-4 py-2 bg-[#0EA5E9] text-white hover:bg-[#0284C7] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                        data-testid="ai-measure-resume-btn"
                      >
                        <RotateCcw className="w-3 h-3" /> Resume
                      </button>
                      <button
                        type="button"
                        onClick={() => setDestructiveConfirm({ kind: "start_fresh" })}
                        className="px-3 py-2 bg-[var(--surface)] text-[var(--danger-text)] border border-[#DC2626] hover:bg-red-50 text-xs font-bold uppercase tracking-wider"
                        data-testid="ai-measure-discard-btn"
                      >
                        Start fresh
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Iter 79j.60 — Live per-photo HUD. Contractor-plain
                  language ONLY — the strip says "photo 3 didn't
                  complete", not "wave 2 timed out". Renders while a
                  Phase A run is in flight AND briefly after it
                  finishes so the failed dots stay visible next to
                  the gap banner below. Cleared on the next Run. */}
              {photoProgress && photoProgress.total > 0 && (() => {
                const status = photoProgress.photo_status || {};
                const total = photoProgress.total;
                const failedList = [];
                const okList = [];
                const pendingList = [];
                let inFlightList = [];
                for (let i = 0; i < total; i += 1) {
                  const st = status[String(i)];
                  if (st === "failed") failedList.push(i);
                  else if (st === "ok") okList.push(i);
                  else pendingList.push(i);
                }
                // Contractor-plain status line: prefer failures (they
                // are the actionable news) over throughput counts.
                let statusLine;
                if (failedList.length > 0 && (okList.length + failedList.length) === total) {
                  statusLine = failedList.length === 1
                    ? `Photo ${failedList[0] + 1} didn't complete — you can re-shoot it later.`
                    : `Photos ${failedList.map((n) => n + 1).join(", ")} didn't complete — you can re-shoot them later.`;
                } else if (failedList.length > 0) {
                  const doneStr = failedList.length === 1
                    ? `Photo ${failedList[0] + 1} didn't complete.`
                    : `Photos ${failedList.map((n) => n + 1).join(", ")} didn't complete.`;
                  statusLine = `Reading photos… ${doneStr} Continuing with the rest.`;
                } else if (okList.length === total) {
                  statusLine = `All ${total} photos read.`;
                } else if (okList.length > 0) {
                  // Guess in-flight = the smallest pending index (first
                  // photo of the current wave). Simple heuristic —
                  // contractor-plain "reading photo N".
                  inFlightList = pendingList.slice(0, Math.max(1, Math.min(pendingList.length, photoProgress.concurrency || 2)));
                  const inFlightStr = inFlightList.length === 1
                    ? `photo ${inFlightList[0] + 1}`
                    : `photos ${inFlightList.map((n) => n + 1).join(" & ")}`;
                  statusLine = `Read ${okList.length} of ${total} · working on ${inFlightStr}…`;
                } else {
                  statusLine = `Reading photos 1–${total}…`;
                }
                return (
                  <div
                    className="mb-4 p-3 border border-[var(--border)] bg-[var(--surface-muted)]"
                    data-testid="ai-measure-photo-hud"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] mb-1.5">
                      Photo progress
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center mb-2" data-testid="ai-measure-photo-hud-dots">
                      {Array.from({ length: total }).map((_, i) => {
                        const st = status[String(i)];
                        let dotCls;
                        let dotLabel;
                        if (st === "ok") {
                          dotCls = "bg-[var(--success)] text-white";
                          dotLabel = `Photo ${i + 1}: read`;
                        } else if (st === "failed") {
                          dotCls = "bg-[#B45309] text-white";
                          dotLabel = `Photo ${i + 1}: didn't complete`;
                        } else if (inFlightList.includes(i)) {
                          dotCls = "bg-[var(--ai)] text-white animate-pulse";
                          dotLabel = `Photo ${i + 1}: reading now`;
                        } else {
                          dotCls = "bg-[var(--border)] text-[var(--muted)]";
                          dotLabel = `Photo ${i + 1}: pending`;
                        }
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10px] font-bold ${dotCls}`}
                            title={dotLabel}
                            data-testid={`ai-measure-photo-hud-dot-${i}`}
                            data-status={st || (inFlightList.includes(i) ? "in_progress" : "pending")}
                          >
                            {i + 1}
                          </span>
                        );
                      })}
                    </div>
                    <div
                      className="text-[11px] text-[var(--ink-2)]"
                      data-testid="ai-measure-photo-hud-status-line"
                    >
                      {statusLine}
                    </div>
                  </div>
                );
              })()}
              {/* SEND-246 — THE TAIL OF THIS DIALOG IS HIDDEN, NOT DELETED.
                  Everything from the reference-marker coverage tile down is
                  behind the one constant; the photo grid above it, with Add
                  Photo, AI May Read, Hand Only and Clear, is untouched. */}
              {MEASURE_TAIL_SHOWN && (<>
              {/* Iter 79j.62 — Marker Coverage Tile. Read-only 4-cell
                  grid (front / right / back / left) that lights green
                  when the elevation has ≥1 `_scale_refs` entry, red
                  when it doesn't, and amber when a dormer pin sits on
                  a ref-less elevation (that's the specific accuracy
                  risk the 79j.61 plumbing quantifies). Same data
                  source as the contextual nudge — `photoAnnotations`
                  — no new state or network. Persistent so contractors
                  see the accuracy contract at every stage (upload,
                  running, results). */}
              {photoUrls.length > 0 && (() => {
                // Iter 79j.63 — Coverage tile rewired to read the SESSION
                // shape of `photoAnnotations` (keyed by photo name,
                // entries are `{elevation, reference, windowReference,
                // zones, ...}`) — NOT the ProfileAnnotator shape (keyed
                // by index string, entries are arrays of boxes).
                //
                // The original Iter 79j.61 tile iterated the wrong
                // shape and skipped every entry via
                // `Array.isArray(boxes)`, so all 4 cardinals rendered
                // "No coverage yet" even when the contractor had
                // placed all 8 WALL_REFs + 8 WIN_REFs. The Jul 7 2026
                // Red-House incident surfaced this — the operator's
                // 324/444/324/etc. refs were present verbatim in the
                // session doc but the tile insisted no coverage
                // existed. This is a pure display bug, not data loss.
                const cardinals = ["front", "right", "back", "left"];
                // A cardinal is "covered" if ANY photo whose elevation
                // matches (or contains) that cardinal has a wall or
                // window reference. Corner photos (front-right,
                // rear-left, etc.) count toward BOTH cardinals they
                // border — a well-placed WIN_REF on a front-right shot
                // supplies scale for both front and right dormers.
                const matchesCardinal = (elev, card) => {
                  if (!elev) return false;
                  const e = String(elev).toLowerCase();
                  if (e === card) return true;
                  // corner photos like "front-right" match both parts
                  return e.split(/[-_/ ]/).includes(card);
                };
                const cellFor = (card) => {
                  let hasRef = false;
                  let hasWinRef = false;
                  let hasPin = false;
                  for (const [name, entry] of Object.entries(photoAnnotations || {})) {
                    if (name.startsWith("_")) continue;
                    if (!entry || typeof entry !== "object") continue;
                    if (!matchesCardinal(entry.elevation, card)) continue;
                    hasPin = true; // photo has this elevation tagged
                    if (entry.reference && entry.reference.inches > 0) hasRef = true;
                    if (entry.windowReference && entry.windowReference.inches > 0) hasWinRef = true;
                  }
                  if (hasRef) return { state: "green", label: `Wall ref set` };
                  if (hasWinRef) return { state: "green", label: `Win ref set` };
                  if (hasPin) return { state: "red", label: "Pinned, no marker" };
                  return { state: "grey", label: "No coverage yet" };
                };
                const cells = cardinals.map((c) => ({ elev: c, ...cellFor(c) }));
                const anyAmber = cells.some((c) => c.state === "amber");
                const anyRedOrGrey = cells.some((c) => c.state === "red" || c.state === "grey");
                return (
                  <div
                    className="mb-4 border border-[var(--border)] bg-[var(--surface-muted)]"
                    data-testid="ai-measure-marker-coverage"
                  >
                    <div className="px-3 py-1.5 border-b border-[var(--border)] flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
                        Reference marker coverage
                      </span>
                      <span
                        className="text-[10px] text-[var(--muted)]"
                        data-testid="ai-measure-marker-coverage-summary"
                      >
                        {anyAmber
                          ? "Dormer pinned without a marker — widths will drift 25–90%"
                          : anyRedOrGrey
                          ? "Some elevations missing markers — accuracy risk on those"
                          : "All 4 elevations ref-anchored (AI reads — not tape)"}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 p-2">
                      {cells.map((c) => {
                        let cls;
                        let dotCls;
                        if (c.state === "green" || c.state === "green_dormer") {
                          cls = "border-[var(--success)] bg-[var(--success)]/10 text-[#065F46]";
                          dotCls = "bg-[var(--success)]";
                        } else if (c.state === "amber") {
                          cls = "border-[#F59E0B] bg-[#FEF3C7] text-[#78350F]";
                          dotCls = "bg-[#B45309]";
                        } else if (c.state === "red") {
                          cls = "border-[var(--danger)] bg-[var(--danger)]/10 text-[#7F1D1D]";
                          dotCls = "bg-[var(--danger)]";
                        } else {
                          cls = "border-[var(--border)] bg-[var(--surface)] text-[var(--muted)]";
                          dotCls = "bg-[var(--border)]";
                        }
                        return (
                          <div
                            key={c.elev}
                            className={`border ${cls} p-1.5 flex flex-col items-start gap-0.5`}
                            data-testid={`ai-measure-marker-coverage-cell-${c.elev}`}
                            data-state={c.state}
                            title={`${c.elev.toUpperCase()} — ${c.label}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} aria-hidden />
                              <span className="text-[10px] font-bold uppercase tracking-wider">{c.elev}</span>
                            </div>
                            <div className="text-[9px] leading-tight">{c.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {/* Iter 57r — Resume last AI run banner */}
              {lastRun && (lastRun.status === "running" || (lastRun.status === "done" && lastRun.result) || lastRun.status === "error") && (
                <div
                  className="mb-4 p-3 border border-[var(--ai)] bg-purple-50 flex items-center justify-between gap-3 flex-wrap"
                  data-testid="ai-measure-resume-run-banner"
                >
                  <div className="text-xs text-[#581C87]">
                    <span className="font-bold uppercase tracking-wider text-[10px] mr-2">
                      {lastRun.status === "running" ? "AI run in progress" : lastRun.status === "error" ? "Last AI run failed" : "Recent AI run"}
                    </span>
                    {lastRun.status === "running" && (
                      <>
                        {lastRun.photo_count || 0} photo{(lastRun.photo_count || 0) === 1 ? "" : "s"} —
                        started {Math.round((lastRun.age_seconds || 0))}s ago, currently <b>{lastRun.stage || "running"}</b>.
                        Reconnect to keep watching progress.
                      </>
                    )}
                    {lastRun.status === "done" && (
                      <>
                        Finished {Math.round((lastRun.age_seconds || 0) / 60)} min ago on {lastRun.photo_count || 0} photo{(lastRun.photo_count || 0) === 1 ? "" : "s"}.
                        Restore the preview without re-running Claude.
                      </>
                    )}
                    {lastRun.status === "error" && (
                      <>
                        {lastRun.error || "Worker crashed"} — try a smaller photo set.
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {lastRun.status === "running" && (
                      <button
                        type="button"
                        onClick={resumeRunPolling}
                        className="px-3 py-1.5 bg-[var(--ai)] text-white hover:bg-[#6D28D9] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                        data-testid="ai-measure-resume-run-btn"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" /> Reconnect
                      </button>
                    )}
                    {lastRun.status === "done" && (
                      <button
                        type="button"
                        onClick={restoreLastRun}
                        className="px-3 py-1.5 bg-[var(--ai)] text-white hover:bg-[#6D28D9] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                        data-testid="ai-measure-restore-run-btn"
                      >
                        <Check className="w-3 h-3" /> Restore preview
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setLastRun(null)}
                      className="px-3 py-1.5 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--surface-muted)] text-xs font-bold uppercase tracking-wider"
                      data-testid="ai-measure-dismiss-run-btn"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {/* Warning banner — set expectations honestly */}
              <div className="border border-[var(--hint-line)] bg-[var(--hint-bg)] px-3 py-2 mb-4 text-xs text-[var(--hint-ink-2)] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  AI photo measurement is an <strong>estimate, not a survey</strong>.
                  Upload <strong>all 4 elevations</strong> (front, back, left, right) for best
                  accuracy — Claude will only count walls it can actually see and will
                  flag any missing sides in the result.
                </div>
              </div>

              {!preview && (
                <>
                  {/* Iter 79j.73 — pre-run readiness checklist (dual
                      audience: field SOP + first-house onboarding).
                      SEND-214 (Howard ruled 2026-08-14) — HIDDEN FROM THE
                      CONTRACTOR, KEPT IN THE CODE: "same rule as aerial."
                      The block and its 2/7 list are not shown. NOTHING IS
                      DELETED — the component, all seven checks and the
                      calibration / tape / wall-ref / profile logic are
                      untouched on disk and still tested. A later send flips
                      the one constant and the block is back. */}
                  {READINESS_SHOWN && (
                    <RunReadinessChecklist
                      estimateId={estimateId}
                      sidingExposure={sidingExposure}
                      annotations={savedProfileAnnotations}
                    />
                  )}
                  {/* SEND-213 (Howard ruled 2026-08-14) — THE BIG DASHED
                      ADD MORE PHOTOS BOX IS GONE. "The lower ADD MORE
                      PHOTOS box is empty and too big. Move add-a-photo into
                      the top section with the existing photo." The file
                      input, the add control, the count and the walk sentence
                      all moved INTO the top photo block: the + tile is the
                      last tile in that row and a photo added there lands in
                      that row. Its tip text and its second count went with
                      the box, by instruction.

                      THE AERIAL IS KEPT IN THE CODE AND HIDDEN FROM THE
                      CONTRACTOR, exactly as he ruled: `fetchSatellite`, its
                      route and its address rule are untouched and still
                      tested — only the button and its address hint are not
                      rendered. It is NOT deleted and NOT moved onto the +
                      tile. Flip one constant to show it again. */}
                  {AERIAL_SHOWN && (
                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={fetchSatellite}
                        disabled={satBusy || photoUrls.length >= 9}
                        className="px-3 py-1.5 bg-[var(--surface)] text-[#0EA5E9] border border-[#0EA5E9] hover:bg-[#F0F9FF] text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="ai-measure-satellite-btn"
                        title={address ? "Fetch a free top-down satellite view from Esri World Imagery" : "Fill in the Address field in Job Information first"}
                      >
                        {satBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {satBusy ? "Fetching aerial\u2026" : "Add aerial view (free)"}
                      </button>
                      {!address && (
                        <div className="text-[10px] text-[var(--muted)] mt-1">
                          Address required \u2014 fill in <b>Address</b> in Job Information first.
                        </div>
                      )}
                    </div>
                  )}

                </>
              )}

              {/* Result preview */}
              {preview && (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${confColor}`} data-testid="ai-measure-confidence">
                      Confidence: {conf}
                    </span>
                    {preview.model && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-[var(--ai)] text-[var(--ai)] bg-[#FAF5FF]"
                        data-testid="ai-measure-model-badge"
                        title={`Ran on ${preview.model_provider || ""} · ${preview.model}`}
                      >
                        {(() => {
                          // Compact label from the full model id.
                          const m = String(preview.model).toLowerCase();
                          if (m.includes("opus-4-5")) return "Opus 4.5";
                          if (m.includes("opus-4-8")) return "Opus 4.8";
                          if (m.includes("sonnet-4-6")) return "Sonnet 4.6";
                          if (m.includes("fable-5")) return "Fable 5";
                          if (m.includes("gemini-3.5")) return "Gemini 3.5 Flash";
                          if (m.includes("gemini-3.1")) return "Gemini 3.1 Pro";
                          if (m.includes("gemini-3-flash")) return "Gemini 3 Flash";
                          if (m.includes("gpt-5.5")) return "GPT-5.5";
                          if (m.includes("gpt-5.4")) return "GPT-5.4";
                          return preview.model;
                        })()}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                      Reference: {preview.measurements._ai_reference_used || "none"}
                    </span>
                    {preview.measurements._ai_story_count != null && (
                      <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                        {preview.measurements._ai_story_count}-story
                      </span>
                    )}
                    {preview.measurements._ai_avg_wall_height_ft != null && (
                      <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                        wall ht {preview.measurements._ai_avg_wall_height_ft} ft
                      </span>
                    )}
                    {preview.measurements._ai_siding_coverage_pct != null && (
                      <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                        siding {preview.measurements._ai_siding_coverage_pct}%
                      </span>
                    )}
                  </div>

                  {/* Iter 79j.16 — Model Comparison panel. Renders only
                      when 2+ runs on this estimate have used at least 2
                      DIFFERENT models — nothing to compare otherwise. */}
                  {(() => {
                    const uniqueModels = new Set(modelHistory.map((r) => r.model_choice));
                    if (modelHistory.length < 2 || uniqueModels.size < 2) return null;
                    const MODEL_LABELS = {
                      "claude-opus-4-5":     "Opus 4.5",
                      "claude-opus-4-8":     "Opus 4.8",
                      "claude-sonnet-4-6":   "Sonnet 4.6",
                      "claude-fable-5":      "Fable 5",
                      "gemini-3.5-flash":    "Gemini 3.5 Flash",
                      "gemini-3.1-pro":      "Gemini 3.1 Pro",
                      "gpt-5.5":             "GPT-5.5",
                      "gpt-5.4":             "GPT-5.4",
                    };
                    const fmtElapsed = (ms) => {
                      if (!ms) return "—";
                      if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
                      return `${(ms / 60000).toFixed(1)}m`;
                    };
                    return (
                      <div className="mb-3 border border-[var(--ai)] bg-[#FAF5FF]" data-testid="ai-measure-model-comparison">
                        <div className="px-3 py-1.5 border-b border-[var(--ai)] bg-[var(--ai)] text-white text-[10px] uppercase tracking-wider font-bold flex items-center justify-between gap-2 flex-wrap">
                          <span>Model Comparison · last {modelHistory.length} runs on this estimate</span>
                          <span className="text-[10px] font-normal flex items-center gap-2">
                            <span className="opacity-80">Higher = winner</span>
                          </span>
                        </div>
                        {modelHistoryWindow && (
                          <div className="px-3 py-1 border-b border-[#E9D5FF] bg-[var(--surface)] text-[9px] leading-snug text-[var(--muted)]"
                            data-testid="ai-measure-model-comparison-window">
                            {modelHistoryWindow}
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="border-b border-[#E9D5FF] bg-[var(--surface)]">
                                <th className="text-left px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Model</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Conf</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Windows</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Doors</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Walls</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Siding ft²</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Eaves LF</th>
                                <th className="text-right px-2 py-1.5 font-bold text-[var(--muted)] uppercase tracking-wider text-[9px]">Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {modelHistory.map((r, i) => (
                                <tr key={r.run_id} className={i === 0 ? "bg-[var(--ai-soft)] font-medium" : "bg-[var(--surface)]"}>
                                  <td className="px-2 py-1.5 font-bold text-[var(--ink)]">
                                    {MODEL_LABELS[r.model_choice] || r.model_choice}
                                    {i === 0 && <span className="ml-1 text-[8px] uppercase text-[var(--ai)]">(latest)</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{r.confidence != null ? r.confidence : "—"}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{r.window_count}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{r.door_count}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{r.wall_count}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{Math.round(r.siding_sqft || 0).toLocaleString()}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums">{Math.round(r.eaves_lf || 0)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono-num tabular-nums text-[var(--muted)]">{fmtElapsed(r.elapsed_ms)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="px-3 py-1.5 border-t border-[#E9D5FF] bg-[var(--surface)] text-[9px] text-[var(--muted)] italic">
                          Compare Conf + counts vs your ground-truth field measurement.
                        </div>
                      </div>
                    );
                  })()}
                  {preview.measurements._ai_story_count_reasoning && (
                    <div className="text-[11px] text-[var(--muted)] mb-2 italic">
                      Story count: {preview.measurements._ai_story_count_reasoning}
                    </div>
                  )}
                  {((preview.measurements._ai_gable_sqft || 0) > 0 ||
                    (preview.measurements._ai_dormer_sqft || 0) > 0) && (
                    <div className="text-[11px] text-[var(--muted)] mb-2 italic" data-testid="ai-measure-geometry-breakdown">
                      Geometry: rectangular walls
                      {(preview.measurements._ai_gable_sqft || 0) > 0 && (
                        <> · gable triangles add <span className="font-bold not-italic">{preview.measurements._ai_gable_sqft} ft²</span></>
                      )}
                      {(preview.measurements._ai_dormer_sqft || 0) > 0 && (
                        <> · dormer faces add <span className="font-bold not-italic">{preview.measurements._ai_dormer_sqft} ft²</span></>
                      )}
                      {" "}— if this doesn&apos;t match the photos, lower the affected wall&apos;s height_ft.
                    </div>
                  )}
                  {preview.measurements._ai_notes && (
                    <div className="text-xs text-[var(--ink-2)] mb-3 italic border-l-2 border-[var(--ai)] pl-3" data-testid="ai-measure-notes">
                      {preview.measurements._ai_notes}
                    </div>
                  )}
                  {/* Iter 79j.22 — Preview / 3D Model tab toggle. Auto-lands
                      on Preview each new run; contractor can flip to 3D
                      Model to sanity-check the parametric geometry
                      Claude/Gemini inferred (pitch, eave, wall widths,
                      opening layout). All estimate line quantities remain
                      SSOT — the 3D side panel READS from preview.lines,
                      never re-computes.  */}
                  <div className="flex items-center gap-1 mb-3 border-b border-[var(--border)]">
                    <button
                      type="button"
                      onClick={() => setPreviewTab("preview")}
                      className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider border-b-2 transition-colors ${
                        previewTab === "preview"
                          ? "border-[var(--ai)] text-[var(--ai)]"
                          : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"
                      }`}
                      data-testid="ai-measure-preview-tab"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewTab("3d")}
                      className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center gap-1.5 ${
                        previewTab === "3d"
                          ? "border-[var(--ai)] text-[var(--ai)]"
                          : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"
                      }`}
                      data-testid="ai-measure-3d-tab"
                    >
                      {RENDER_3D_ENABLED ? "3D Model" : "Field Verify"}
                      {RENDER_3D_ENABLED && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#F3F4F6] text-[var(--ai)] tracking-normal">BETA</span>
                      )}
                    </button>
                  </div>

                  {/* Iter 79j.29 — photos-lost banner. When preview
                      exists but the photo grid is empty (typically an
                      older run whose upload names were never persisted),
                      the Re-Run button is disabled and the contractor
                      can't tell why. Explicit inline banner spells it
                      out and points to the upload input. */}
                  {photoUrls.length === 0 && (
                    <div
                      className="mb-3 p-3 bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] text-[11px] flex items-start gap-2"
                      data-testid="ai-measure-photos-lost-banner"
                    >
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--warning-text)]" />
                      <div className="flex-1">
                        <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">Photos missing</div>
                        <div>
                          The measurements below are from an older run whose photo files
                          were not persisted. <b>Re-upload the same photos above</b> to
                          enable Re-Run · Refine on Photo · A/B model comparison. Nothing
                          in the current takeoff will be lost.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Iter 79j.43 — Empty-photo warning banner. When the
                      two-phase Phase A returned nothing (twice, after
                      the auto-retry), name the affected photo(s) and
                      which walls now have no direct-view coverage.
                      A dead photo must NEVER fail silently. */}
                  {(preview?.measurements?._ai_empty_photos?.length > 0
                    || preview?.measurements?._ai_orphaned_walls?.length > 0
                    || preview?.measurements?._ai_pin_gap_hints?.length > 0) && (
                    <div
                      className="mb-3 p-3 bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] text-[11px] flex items-start gap-2"
                      data-testid="ai-measure-empty-photos-banner"
                    >
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--warning-text)]" />
                      <div className="flex-1">
                        <div className="font-bold uppercase tracking-wider text-[10px] mb-1">
                          AI extraction gaps detected
                        </div>
                        {preview?.measurements?._ai_empty_photos?.length > 0 && (
                          <div className="mb-1" data-testid="ai-measure-empty-photos-list">
                            <b>{preview.measurements._ai_empty_photos.length} photo{preview.measurements._ai_empty_photos.length === 1 ? "" : "s"} returned no useful data</b> after an automatic retry:
                            <ul className="list-disc list-inside mt-0.5 ml-1">
                              {preview.measurements._ai_empty_photos.map((p, i) => (
                                <li key={i}>
                                  Photo #{Number(p.photo_idx) + 1}
                                  {p.reason ? ` — ${p.reason}` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {preview?.measurements?._ai_orphaned_walls?.length > 0 && (
                          <div className="mb-1" data-testid="ai-measure-orphaned-walls">
                            <b>Walls with no direct-view coverage:</b>{" "}
                            {preview.measurements._ai_orphaned_walls.join(", ")}.
                            Their dimensions are extrapolated — capture a
                            direct side shot of each and Re-Run before quoting.
                          </div>
                        )}
                        {/* Iter 79j.59 — Contractor-pin gap-signal.
                            When user pins tag a feature on an elevation
                            the reconciled output couldn't cover, we
                            promote that mismatch from the trace into
                            the banner. Each hint carries an actionable
                            re-shoot elevation — no bare warnings. */}
                        {preview?.measurements?._ai_pin_gap_hints?.length > 0 && (
                          <div className="mb-1" data-testid="ai-measure-pin-gap-hints">
                            <b>Your pins suggest coverage the AI didn&apos;t confirm:</b>
                            <ul className="list-disc list-inside mt-0.5 ml-1">
                              {preview.measurements._ai_pin_gap_hints.map((h, i) => (
                                <li
                                  key={`${h.kind}-${h.elevation}-${i}`}
                                  data-testid={`ai-measure-pin-gap-hint-${h.kind}-${h.elevation}`}
                                >
                                  {h.message}
                                  {Array.isArray(h.source_photo_idxs) && h.source_photo_idxs.length > 0 && (
                                    <span className="text-[10px] text-[var(--muted)] ml-1">
                                      (pin
                                      {h.source_photo_idxs.length === 1 ? " on " : "s on "}
                                      photo{h.source_photo_idxs.length === 1 ? "" : "s"}
                                      {" "}
                                      {h.source_photo_idxs.map((n) => `#${n + 1}`).join(", ")})
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="text-[var(--warning-text)]">
                          Re-shoot the flagged photos (or delete them and re-upload)
                          then click <b>Re-Run</b> below.
                        </div>
                      </div>
                    </div>
                  )}

                  {previewTab === "3d" && (
                    <div className="mb-4" data-testid="ai-measure-3d-panel">
                      {/* Field Verify (approved 2026-07-20): tape check +
                          taped dims + per-wall takeoff live here; the 3D
                          render is dark behind RENDER_3D_ENABLED. */}
                      <FieldVerifyCard
                        preview={preview}
                        estimate={estimate}
                        runId={currentRunId}
                      />
                      {(() => {
                        // Iter 79j.52 — Never draw a placeholder house
                        // when reconciliation failed or produced zero
                        // geometry. Different failure semantics need
                        // different UI: an empty box implies "we
                        // measured a tiny house", the incomplete
                        // banner correctly reads "we didn't measure".
                        const ra = preview?.raw_ai || {};
                        const failed = !!ra._reconciliation_error;
                        const wallsN = Array.isArray(ra.walls) ? ra.walls.length : 0;
                        const dormersN = Array.isArray(ra.dormers) ? ra.dormers.length : 0;
                        const empty = !failed && wallsN === 0 && dormersN === 0;
                        if (failed || empty) {
                          return (
                            <div
                              className="p-4 border border-dashed border-[var(--danger)] bg-[#FEE2E2] text-[#7F1D1D] text-[11px]"
                              data-testid="ai-measure-3d-empty-state"
                            >
                              <div className="font-bold uppercase tracking-wider text-[10px] mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Measurement incomplete — reconciliation failed
                              </div>
                              <div className="mb-3 whitespace-pre-wrap break-words">
                                {failed
                                  ? String(ra._reconciliation_error)
                                  : "Phase B produced no walls or dormers — nothing to render in 3D. Retry reconciliation on the saved Phase A extractions before applying."}
                              </div>
                              {currentRunId && (
                                <button
                                  type="button"
                                  onClick={() => retryReconcileOnly(currentRunId)}
                                  disabled={busy}
                                  className="px-3 py-1.5 bg-[#7F1D1D] text-white hover:bg-[#991B1B] text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid="ai-measure-3d-retry-reconciliation"
                                  title="Retry ONLY Phase B (reconciliation). Keeps Phase A's vision output."
                                >
                                  <RotateCcw className="w-3 h-3" /> Retry Reconciliation
                                </button>
                              )}
                            </div>
                          );
                        }
                        return RENDER_3D_ENABLED ? (
                          <HouseModel3D
                            preview={preview}
                            estimate={estimate}
                            runId={currentRunId}
                            onSnapshot={estimateId ? saveModelSnapshot : undefined}
                            hasSnapshot={modelSnapshotSaved || !!estimate?.model3d_png_url}
                          />
                        ) : null;
                      })()}
                    </div>
                  )}

                  {previewTab === "preview" && (
                  <>
                  {/* SEND-144 (Howard ruled 2026-08-28) — THE PHOTOS AS READ.
                      An annotated photo is a DIFFERENT FILE from the one on
                      the estimate: the read looked at the annotated copy, and
                      the starting zones sit on THAT file. These buttons open
                      the takeoff on the photo the read actually read, so the
                      zones are never out of reach. The elevation beside each
                      one is the READ'S OWN call, not ours. */}
                  {readPhotos.length > 0 && (
                    <div className="mb-3 border border-[var(--ai)] bg-[#EFF6FF] p-2" data-testid="ai-measure-read-photos">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ai)] mb-1">
                        The photos this read read — starting zones live here
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                        {readPhotos.map((name, i) => {
                          const elev = (preview?.raw_ai?.photos || []).find((x) => x?.index === i)?.elevation;
                          return (
                            <button key={name} type="button" onClick={() => openTakeoff(name)}
                              className="flex items-center gap-1 px-2 py-1 bg-white border border-[var(--ai)] text-[10px] font-bold uppercase tracking-wider text-[var(--ai)] hover:opacity-90"
                              data-testid={`ai-measure-read-photo-takeoff-${i}`}
                              title="Open the photo takeoff on the photo this read read — its starting zones are on this file">
                              <Ruler className="w-2.5 h-2.5" />
                              <span className="truncate">{elev ? String(elev).toUpperCase() : `photo ${i + 1}`}</span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-1 text-[9px] text-[var(--ink-2)] leading-snug">
                        Every zone is PROVISIONAL and its shape is a STARTING SHAPE — move it onto the wall you see, add
                        what the read missed, then confirm. A refused face has no zone and says why.
                      </div>
                    </div>
                  )}
                  {/* Iter 78z (P1.3 + Cross-Check) — Per-Elevation Breakdown,
                      "+ Add Accent", and "🔁 Re-check with AI" button */}
                  <PerElevationBreakdownCard
                    measurements={preview.measurements || {}}
                    runId={currentRunId}
                    onUpdate={({ measurements: newMeas, lines: newLines }) => {
                      setPreview((p) => p && ({
                        ...p,
                        measurements: newMeas,
                        ...(newLines ? { lines: newLines } : {}),
                      }));
                    }}
                  />
                  {/* Iter 79j.12 — Elevation Drawings block removed per
                      Howard's feedback (2026-02-28): the auto-generated 2D
                      diagrams often didn't match the actual house
                      structure closely enough and hinted at inaccuracy
                      to the contractor. Wall breakdown table below still
                      shows the same underlying data. */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {Object.entries(preview.measurements)
                      .filter(([k, v]) => !k.startsWith("_") && v !== 0 && v !== null && v !== undefined)
                      .map(([k, v]) => (
                        <div key={k} data-testid={`ai-measure-stat-${k}`}>
                          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{KEY_LABELS[k] || k}</div>
                          <div className="font-mono-num text-sm font-bold text-[var(--ink)]">
                            {fmt(v)} {unitOf(k)}
                          </div>
                        </div>
                      ))}
                  </div>
                  {preview.raw_ai?.walls?.length > 0 && (
                    <details className="text-xs mb-3" open>
                      <summary className="cursor-pointer text-[var(--ai)] font-bold uppercase tracking-wider">
                        Wall breakdown ({preview.raw_ai.walls.length}) — tap to edit
                      </summary>
                      <div className="text-[11px] text-[var(--muted)] mt-2 italic">
                        If the AI got the geometry wrong (e.g. called a 1-story dormer a 2-story wall),
                        edit the numbers below. Siding ft² updates live. Apply re-runs the line math.
                      </div>
                      {/* BLOCK A — NO PHOTO, NO WALL: the gap is named at
                          the top of the table, in the contractor's words. */}
                      {(preview.measurements?._faces_refused || []).length > 0 && (
                        <div className="mt-2 p-2 bg-[#FEF2F2] border border-[#DC2626] text-[10px] text-[#B91C1C] leading-snug" data-testid="ai-measure-faces-refused-banner">
                          <b className="uppercase tracking-wider">No photo, no wall.</b>{" "}
                          {(preview.measurements._faces_refused || []).map((f) => f.refusal).join(" ")}{" "}
                          Nothing is priced from a refused face — no ft², no gable, no starter, no corner, no J-channel, no line.
                          House-level runs (eaves, rakes, corners, footprint) are REFUSED as well: the read computed them over the faces it mirrored, and they cannot be pulled apart afterwards. They are refusals, not zeros.
                          {preview.measurements._face_rule_stale && (
                            <div className="mt-1 pt-1 border-t border-[#DC2626]" data-testid="ai-measure-face-rule-stale">
                              <b>This read predates the rule and CANNOT BE APPLIED.</b>{" "}
                              {preview.measurements._face_refusal_note}
                            </div>
                          )}
                        </div>
                      )}
                      <table className="w-full mt-2 text-xs" data-testid="ai-measure-wall-table">
                        <thead className="text-left text-[var(--muted)] uppercase tracking-wider text-[10px]">
                          <tr>
                            <th>Wall</th>
                            <th>W (ft)</th>
                            <th>H eave (ft)</th>
                            <th>Gable h (ft)</th>
                            <th>Dormer (ft²)</th>
                            <th>Gable ft²</th>
                            <th>Total ft²</th>
                            <th title="Claude's per-wall confidence — green = high, amber = medium, red = low. Verify low/medium walls in the field.">Conf</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.raw_ai.walls.map((w, i) => {
                            // BLOCK A — a REFUSED face shows its refusal and
                            // nothing else: no ft², no editable number. The
                            // claim the model made stays in the tooltip so the
                            // record is intact, and typing here can never turn
                            // a face nobody photographed into money.
                            if (w._refused) {
                              return (
                                <tr key={i} className="border-b border-[var(--bg-app)] bg-[#FEF2F2]" data-testid={`ai-measure-wall-refused-${i}`}>
                                  <td className="py-1 font-bold text-[#B91C1C] uppercase tracking-wider text-[10px]">{w.label}</td>
                                  <td colSpan={7} className="text-[10px] text-[#B91C1C] leading-snug"
                                    title={`The read offered ${w.width_ft || "?"} × ${w.height_ft || "?"} ft (width source: ${w.width_ft_source || "?"}, height source: ${w.height_ft_source || "?"}). Refused — a photo of this wall is the only thing that measures it.`}>
                                    <b>REFUSED</b> — {w._refusal || "no photo of this wall"} Nothing is priced from it: no ft², no gable, no starter, no corner, no line. Take a photo of this wall, or draw it yourself on a photo in the takeoff editor.
                                  </td>
                                </tr>
                              );
                            }
                            const width = Number(w.width_ft) || 0;
                            const eave = Number(w.height_ft) || 0;
                            const gable = Number(w.gable_triangle_height_ft) || 0;
                            const dormer = Number(w.dormer_face_sqft) || 0;
                            const gableArea = 0.5 * width * gable;
                            const area = width * eave + gableArea + dormer;
                            const confScore = Math.round(Number(w.confidence) || 0);
                            const confTier = confScore >= 80 ? "high" : confScore >= 60 ? "med" : confScore >= 30 ? "low" : "guess";
                            const confChip = {
                              high:  { bg: "bg-[var(--success)]", label: "HIGH" },
                              med:   { bg: "bg-[#CA8A04]", label: "MED" },
                              low:   { bg: "bg-[var(--brand-hover)]", label: "LOW" },
                              guess: { bg: "bg-[#DC2626]", label: "GUESS" },
                            }[confTier];
                            return (
                              <tr key={i} className="border-b border-[var(--bg-app)]">
                                <td className="py-1 font-bold text-[var(--ink-2)] uppercase tracking-wider text-[10px]">{w.label}</td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.5"
                                    className="w-16 px-1 py-0.5 border border-[var(--border)] font-mono-num text-xs"
                                    value={width}
                                    onChange={(e) => setWall(i, "width_ft", e.target.value)}
                                    data-testid={`ai-measure-wall-w-${i}`}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.5"
                                    className="w-16 px-1 py-0.5 border border-[var(--border)] font-mono-num text-xs"
                                    value={eave}
                                    onChange={(e) => setWall(i, "height_ft", e.target.value)}
                                    data-testid={`ai-measure-wall-h-${i}`}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    className="w-16 px-1 py-0.5 border border-[var(--border)] font-mono-num text-xs"
                                    value={gable}
                                    onChange={(e) => setWall(i, "gable_triangle_height_ft", e.target.value)}
                                    data-testid={`ai-measure-wall-gable-${i}`}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    className="w-16 px-1 py-0.5 border border-[var(--border)] font-mono-num text-xs"
                                    value={dormer}
                                    onChange={(e) => setWall(i, "dormer_face_sqft", e.target.value)}
                                    data-testid={`ai-measure-wall-dormer-${i}`}
                                  />
                                  {/* Iter 79j.66 — the ft² alone forces the
                                      contractor to know the face-area formula.
                                      Expose the matched dormer's W × knee (the
                                      same fields the 3D sidebar shows); edits
                                      rescale the ft² automatically. */}
                                  {(preview.raw_ai.dormers || [])
                                    .map((d, j) => ({ d, j }))
                                    .filter(({ d }) => (d.face || "").toLowerCase() === (w.label || "").toLowerCase())
                                    .map(({ d, j }) => (
                                      <div
                                        key={j}
                                        className="flex items-center gap-0.5 mt-0.5 text-[9px] text-[var(--muted)]"
                                        title="Dormer width × knee-wall height (ft) — same fields as the 3D sidebar. Editing rescales the face ft² using the AI's geometry factor, and the 3D model follows."
                                        data-testid={`ai-measure-wall-dormer-dims-${i}-${j}`}
                                      >
                                        <input
                                          type="number"
                                          step="0.5"
                                          min="0"
                                          className="w-11 px-1 py-0.5 border border-[var(--border)] font-mono-num text-[10px]"
                                          value={Number(d.width_ft) || 0}
                                          onChange={(e) => setDormerDims(j, i, "width_ft", e.target.value)}
                                          data-testid={`ai-measure-wall-dormer-w-${i}-${j}`}
                                        />
                                        <span>×</span>
                                        <input
                                          type="number"
                                          step="0.25"
                                          min="0"
                                          className="w-11 px-1 py-0.5 border border-[var(--border)] font-mono-num text-[10px]"
                                          value={Number(d.knee_wall_height_ft) || 0}
                                          onChange={(e) => setDormerDims(j, i, "knee_wall_height_ft", e.target.value)}
                                          data-testid={`ai-measure-wall-dormer-knee-${i}-${j}`}
                                        />
                                        <span className="uppercase tracking-wider">w×knee</span>
                                      </div>
                                    ))}
                                </td>
                                <td className="font-mono-num font-bold text-[var(--ai)]" data-testid={`ai-measure-wall-gable-ft2-${i}`}>
                                  {gableArea > 0 ? gableArea.toFixed(0) : "—"}
                                </td>
                                <td className="font-mono-num font-bold">{area.toFixed(0)}</td>
                                <td
                                  className="text-center"
                                  title={(w.confidence_reasoning || "") + (confScore ? ` · score ${confScore}/100` : "")}
                                  data-testid={`ai-measure-wall-conf-${i}`}
                                >
                                  {confScore > 0 ? (
                                    <span className={`inline-block ${confChip.bg} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider`}>
                                      {confChip.label} {confScore}
                                    </span>
                                  ) : (
                                    <span className="text-[var(--muted)]">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Totals row — emphasizes the gable ft² so the
                              contractor can spec shake siding for those
                              areas if the homeowner wants it. */}
                          {(() => {
                            const totalGable = preview.raw_ai.walls.reduce((a, w) => {
                              const ww = Number(w.width_ft) || 0;
                              const gh = Number(w.gable_triangle_height_ft) || 0;
                              return a + 0.5 * ww * gh;
                            }, 0);
                            const totalArea = preview.raw_ai.walls.reduce((a, w) => {
                              const ww = Number(w.width_ft) || 0;
                              const eh = Number(w.height_ft) || 0;
                              const gh = Number(w.gable_triangle_height_ft) || 0;
                              const dr = Number(w.dormer_face_sqft) || 0;
                              return a + ww * eh + 0.5 * ww * gh + dr;
                            }, 0);
                            return (
                              <tr className="border-t-2 border-[var(--border-strong)]" data-testid="ai-measure-wall-totals">
                                <td colSpan={5} className="py-1 text-[10px] uppercase tracking-wider font-bold text-[var(--ink-2)] text-right">
                                  Totals
                                </td>
                                <td className="font-mono-num font-bold text-[var(--ai)]" data-testid="ai-measure-total-gable-ft2">
                                  {totalGable > 0 ? totalGable.toFixed(0) : "—"}
                                </td>
                                <td className="font-mono-num font-bold">{totalArea.toFixed(0)}</td>
                                <td>&nbsp;</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                      {preview.raw_ai.walls.some((w) => Number(w.gable_triangle_height_ft) > 0) && (
                        <>
                          <div className="text-[11px] text-[var(--ai)] mt-2 inline-flex items-start gap-1" data-testid="ai-measure-gable-shake-hint">
                            <Lightbulb aria-hidden="true" className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>Gable ft² is broken out so you can quote shake / scallop siding for those triangles if the homeowner wants a different look up top.</span>
                          </div>
                          <label className="mt-2 flex items-center gap-2 cursor-pointer p-2 border border-[var(--border)] hover:border-[var(--ai)] transition-colors">
                            <input
                              type="checkbox"
                              checked={quoteGablesAsShake}
                              onChange={(e) => setQuoteGablesAsShake(e.target.checked)}
                              data-testid="ai-measure-quote-gables-shake"
                            />
                            <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                              Quote gables as shake
                            </span>
                          </label>
                          {quoteGablesAsShake && (
                            <div className="text-[10px] text-[var(--ink-2)] mt-1 ml-7" data-testid="ai-measure-shake-preview">
                              On Apply: gable surfaces flip to <span className="font-bold">SHAKE</span> on the per-elevation breakdown ({(preview?.measurements?._ai_gable_sqft || 0).toFixed(0)} ft²) — lines re-derive via the conventions path (sealed shake waste 15%). Same result as hand-setting the profile.
                            </div>
                          )}
                        </>
                      )}
                      {wallsDirty && (
                        <div className="text-[10px] text-[var(--brand-text)] uppercase tracking-wider font-bold mt-2" data-testid="ai-measure-walls-dirty">
                          ✎ Edited — line items will refresh on Apply
                        </div>
                      )}
                      {preview.raw_ai.walls.some((w) => Number(w.dormer_face_sqft) > 0) && (
                        <>
                          <label className="mt-2 flex items-center gap-2 cursor-pointer p-2 border border-[var(--border)] hover:border-[var(--ai)] transition-colors">
                            <input
                              type="checkbox"
                              checked={quoteDormersAsShake}
                              onChange={(e) => setQuoteDormersAsShake(e.target.checked)}
                              data-testid="ai-measure-quote-dormers-shake"
                            />
                            <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                              Quote dormers as shake
                            </span>
                          </label>
                          {quoteDormersAsShake && (
                            <div className="text-[10px] text-[var(--ink-2)] mt-1 ml-7" data-testid="ai-measure-dormer-shake-preview">
                              On Apply: dormer surfaces flip to <span className="font-bold">SHAKE</span> on the per-elevation breakdown ({(preview?.measurements?._ai_dormer_sqft || 0).toFixed(0)} ft²) — lines re-derive via the conventions path (sealed shake waste 15%). Same result as hand-setting the profile.
                            </div>
                          )}
                        </>
                      )}
                    </details>
                  )}

                  {/* Iter 57: HOVER-style extras.
                      • Missing-elevations banner — warn if Claude didn't see all 4 walls
                      • Openings schedule — collapsed grouped view (elevation × type × size)
                      • Double-count check — Claude's reconciliation note */}
                  {(preview.measurements?._ai_missing_elevations?.length ?? 0) > 0 && (
                    <div
                      className="border border-[#F59E0B] bg-[#FEF3C7] px-3 py-2 mb-3 text-xs text-[#78350F] flex items-start gap-2"
                      data-testid="ai-measure-missing-elevs"
                    >
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        Claude couldn&apos;t see these elevations —
                        add photos to capture them: {" "}
                        <strong>
                          {preview.measurements._ai_missing_elevations
                            .map((e) => e.toUpperCase())
                            .join(", ")}
                        </strong>
                        .
                      </div>
                    </div>
                  )}

                  {/* Iter 57o — Labeled photos preview. Surfaces the
                      bbox callouts (yellow boxes + labels) on each photo
                      so the contractor can spot-check Claude's
                      per-opening placements BEFORE generating the PDF. */}
                  {(() => {
                    const schedule = preview.measurements?._ai_openings_schedule
                      || preview.raw_ai?.openings_schedule || [];
                    const totalLocs = schedule.reduce(
                      (n, r) => n + (Array.isArray(r.locations) ? r.locations.length : 0), 0
                    );
                    if (totalLocs === 0 || photoUrls.length === 0) return null;
                    return (
                      <details className="text-xs mb-3" open data-testid="ai-measure-labeled-photos">
                        <summary className="cursor-pointer text-[var(--ai)] font-bold uppercase tracking-wider">
                          Labeled photos — {totalLocs} opening{totalLocs === 1 ? "" : "s"} tagged by Claude
                        </summary>
                        <div className="text-[11px] text-[var(--muted)] mt-2 italic">
                          Same yellow boxes + labels appear on the photos in the downloaded measurement PDF. If one looks wrong, edit the opening size/style in the Openings schedule below — the label updates automatically.
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {photoUrls.map((name, i) => {
                            // Build the callouts for THIS photo, same
                            // logic as the upload-grid overlay above.
                            const photoCallouts = [];
                            const seenKeys = new Set();
                            for (const row of schedule) {
                              for (const loc of (row.locations || [])) {
                                if (Number(loc.photo_idx) !== i) continue;
                                const bb = loc.bbox || {};
                                const bx = Number(bb.x), by = Number(bb.y);
                                const bw = Number(bb.w || 0), bh = Number(bb.h || 0);
                                if (!(bx >= 0 && bx <= 1 && by >= 0 && by <= 1 && bw > 0 && bh > 0 && bx + bw <= 1.001 && by + bh <= 1.001)) continue;
                                const key = `${bx.toFixed(3)},${by.toFixed(3)},${bw.toFixed(3)},${bh.toFixed(3)}`;
                                if (seenKeys.has(key)) continue;
                                seenKeys.add(key);
                                const wi = Math.round(Number(row.width_in) || 0);
                                const hi = Math.round(Number(row.height_in) || 0);
                                const t = String(row.type || "window").toLowerCase();
                                const style = String(row.style || "");
                                let label = `${wi}×${hi}`;
                                if (t === "garage_door") label = `${wi}×${hi} Garage`;
                                else if (t === "entry_door") label = `${wi}×${hi} Entry`;
                                else if (t === "patio_door") label = `${wi}×${hi} Patio`;
                                else {
                                  let short = "";
                                  if (/Double Hung|Twin Double/i.test(style)) short = "DH";
                                  else if (/Single Hung/i.test(style)) short = "SH";
                                  else if (/Casement/i.test(style)) short = "CS";
                                  else if (/Slider/i.test(style)) short = "SL";
                                  else if (/Picture/i.test(style)) short = "PIC";
                                  else if (/Awning/i.test(style)) short = "AW";
                                  else if (/Hopper/i.test(style)) short = "HP";
                                  if (short) label = `${short} ${label}`;
                                }
                                const labelY = by > 0.07 ? by - 0.025 : by + 0.005;
                                const lcx = bx + bw / 2;
                                const lblFs = 3.0;
                                const bgW = Math.min(0.98 - lcx + 0.5, Math.max(0.10, label.length * lblFs * 0.0048));
                                const bgX = Math.max(0.005, Math.min(1 - bgW - 0.005, lcx - bgW / 2));
                                photoCallouts.push(
                                  <g key={key}>
                                    <rect x={bx * 100} y={by * 100} width={bw * 100} height={bh * 100}
                                          fill="none" stroke="#FACC15" strokeWidth={0.6} />
                                    <rect x={bgX * 100} y={labelY * 100} width={bgW * 100} height={lblFs + 0.6}
                                          fill="#09090B" />
                                    <text x={(bgX + bgW / 2) * 100} y={labelY * 100 + lblFs * 0.85}
                                          textAnchor="middle" fontSize={lblFs} fontWeight={700} fill="#FACC15">
                                      {label}
                                    </text>
                                  </g>
                                );
                              }
                            }
                            const meta = (preview.measurements?._ai_photos || preview.raw_ai?.photos || [])
                              .find((p) => Number(p.index) === i) || {};
                            const elev = (meta.elevation || "").toUpperCase();
                            return (
                              <div key={name} className="relative border border-[var(--border)] bg-[var(--surface-muted)]" data-testid={`ai-measure-labeled-photo-${i}`}>
                                <div className="relative aspect-video overflow-hidden">
                                  <img
                                    src={`/api/uploads/${name}`}
                                    alt={`Labeled photo ${i + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  {photoCallouts.length > 0 && (
                                    <svg
                                      viewBox="0 0 100 100"
                                      preserveAspectRatio="none"
                                      className="absolute inset-0 w-full h-full pointer-events-none"
                                    >
                                      {photoCallouts}
                                    </svg>
                                  )}
                                  {elev && (
                                    <span className="absolute top-1 left-1 bg-[var(--ai)] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-bold">
                                      {elev}
                                    </span>
                                  )}
                                  <span className="absolute bottom-1 right-1 bg-[#FACC15] text-[var(--ink)] text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-bold" data-testid={`ai-measure-labeled-photo-count-${i}`}>
                                    {photoCallouts.length} tag{photoCallouts.length === 1 ? "" : "s"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    );
                  })()}

                  {(preview.measurements?._ai_openings_schedule?.length ?? 0) > 0 && (
                    <details className="text-xs mb-3" open data-testid="ai-measure-openings-schedule">
                      <summary className="cursor-pointer text-[var(--ai)] font-bold uppercase tracking-wider">
                        Openings schedule — grouped by elevation × size
                      </summary>
                      <div className="text-[11px] text-[var(--muted)] mt-2 italic">
                        Each elevation is grouped together with a colored chip and total count so it&apos;s easy to verify against the house. Sizes are listed underneath.
                      </div>
                      {/* Iter 57c — Option B: rows grouped by elevation
                          with a colored chip + total opening count per
                          group. Kills the visual confusion of seeing
                          "LEFT, LEFT, LEFT" repeated. */}
                      {(() => {
                        const ELEVATION_COLORS = {
                          front:  { bg: "bg-[#3B82F6]", soft: "bg-[#EFF6FF]", text: "text-[#1E40AF]" },
                          back:   { bg: "bg-[var(--success)]", soft: "bg-[#F0FDF4]", text: "text-[#166534]" },
                          left:   { bg: "bg-[var(--brand-hover)]", soft: "bg-[#FFF7ED]", text: "text-[#9A3412]" },
                          right:  { bg: "bg-[var(--ai)]", soft: "bg-[#FAF5FF]", text: "text-[#5B21B6]" },
                          other:  { bg: "bg-[var(--ink-2)]", soft: "bg-[var(--surface-muted)]", text: "text-[#27272A]" },
                        };
                        const schedule = preview.measurements._ai_openings_schedule || [];
                        // Group by elevation in a fixed display order.
                        const order = ["front", "back", "left", "right", "other"];
                        const groups = {};
                        schedule.forEach((o) => {
                          const elev = (o.elevation || "other").toLowerCase();
                          const k = order.includes(elev) ? elev : "other";
                          if (!groups[k]) groups[k] = [];
                          groups[k].push(o);
                        });
                        const orderedGroups = order
                          .filter((k) => groups[k]?.length)
                          .map((k) => [k, groups[k]]);

                        return (
                          <div className="mt-2" data-testid="ai-measure-openings-grouped">
                            {/* Tiny house diagram so the colors map to spatial position */}
                            <div className="flex items-center justify-center gap-3 py-2 mb-2 border-y border-[var(--border)]" data-testid="ai-measure-elevation-legend">
                              <svg width="56" height="56" viewBox="0 0 56 56" className="flex-shrink-0">
                                <rect x="14" y="14" width="28" height="28" fill="#FAFAFA" stroke="#A1A1AA" strokeWidth="1" />
                                <rect x="14" y="11" width="28" height="3" fill="#3B82F6" />
                                <rect x="14" y="42" width="28" height="3" fill="#16A34A" />
                                <rect x="11" y="14" width="3" height="28" fill="#EA580C" />
                                <rect x="42" y="14" width="3" height="28" fill="#7C3AED" />
                                <text x="28" y="9" fontSize="6" fill="#3B82F6" textAnchor="middle" fontWeight="700">FRONT</text>
                                <text x="28" y="52" fontSize="6" fill="#16A34A" textAnchor="middle" fontWeight="700">BACK</text>
                                <text x="8" y="30" fontSize="5" fill="#EA580C" textAnchor="middle" fontWeight="700" transform="rotate(-90 8 30)">LEFT</text>
                                <text x="48" y="30" fontSize="5" fill="#7C3AED" textAnchor="middle" fontWeight="700" transform="rotate(90 48 30)">RIGHT</text>
                              </svg>
                              <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
                                Color = which side of the house
                              </div>
                            </div>
                            {orderedGroups.map(([elev, items]) => {
                              const color = ELEVATION_COLORS[elev] || ELEVATION_COLORS.other;
                              const totalCount = items.reduce((sum, o) => sum + (Number(o.count) || 0), 0);
                              return (
                                <div
                                  key={elev}
                                  className="mb-2"
                                  data-testid={`ai-measure-opening-group-${elev}`}
                                >
                                  <div className={`${color.soft} flex items-center gap-2 px-2 py-1.5 border-l-4 ${color.bg.replace("bg-", "border-")}`}>
                                    <span className={`${color.bg} text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider`}>
                                      {elev}
                                    </span>
                                    <span className={`text-[11px] font-bold ${color.text}`}>
                                      {totalCount} opening{totalCount !== 1 ? "s" : ""}
                                    </span>
                                    <span className="text-[10px] text-[var(--muted)] ml-2 italic">
                                      {items.map((o) => {
                                        const sz = o.size_label || `${Math.round(Number(o.width_in) || 0)}×${Math.round(Number(o.height_in) || 0)} in`;
                                        const st = (o.style || "").trim();
                                        return st ? `${st} ${sz}×${o.count}` : `${sz}×${o.count}`;
                                      }).join(" · ")}
                                    </span>
                                  </div>
                                  <table className="w-full text-xs border-b border-[var(--border)]">
                                    <tbody>
                                      {items.map((o, i) => {
                                        const isWindow = (o.type || "").toLowerCase() === "window";
                                        const styleVal = o.style || "";
                                        const styleConf = Number(o.style_confidence) || 0;
                                        const confChip = styleConf >= 80 ? "bg-[var(--success)]" : styleConf >= 60 ? "bg-[#CA8A04]" : styleConf >= 30 ? "bg-[var(--brand-hover)]" : "bg-[#DC2626]";
                                        const sizeLabel = o.size_label || `${Math.round(Number(o.width_in) || 0)}×${Math.round(Number(o.height_in) || 0)} in`;
                                        return (
                                          <tr key={i} className="hover:bg-[var(--surface-muted)]" data-testid={`ai-measure-opening-row-${elev}-${i}`}>
                                            <td className="py-1 pl-4 capitalize text-[var(--ink-2)]" style={{ width: "22%" }}>
                                              {(o.type || "—").replace(/_/g, " ")}
                                            </td>
                                            <td className="font-mono-num text-[#27272A]" style={{ width: "20%" }}>
                                              {sizeLabel}
                                            </td>
                                            <td className="py-1" style={{ width: "45%" }}>
                                              {isWindow ? (
                                                <div className="flex items-center gap-1">
                                                  <select
                                                    value={styleVal}
                                                    onChange={(e) => updateOpeningStyle(elev, o.type, o.size_label, o.width_in, o.height_in, e.target.value)}
                                                    className="text-xs border border-[var(--border)] px-1 py-0.5 bg-[var(--surface)] hover:border-[var(--ai)] cursor-pointer w-full max-w-[180px]"
                                                    data-testid={`ai-measure-opening-style-${elev}-${i}`}
                                                    title={styleVal ? `Claude's guess: ${styleVal} (${styleConf}% confident). Change if wrong — this flows to the customer PDF and the Vero quote.` : "Pick a window style — flows to the customer PDF and the Vero quote"}
                                                  >
                                                    {WINDOW_STYLES.map((s) => (
                                                      <option key={s.value} value={s.value}>{s.label}</option>
                                                    ))}
                                                  </select>
                                                  {styleVal && styleConf > 0 && (
                                                    <span
                                                      className={`${confChip} text-white text-[8px] font-bold px-1 rounded-sm tracking-wider`}
                                                      title={`Claude is ${styleConf}% confident on this style`}
                                                    >
                                                      {styleConf}
                                                    </span>
                                                  )}
                                                </div>
                                              ) : (
                                                <span className="text-[var(--muted)] italic text-[11px]">—</span>
                                              )}
                                            </td>
                                            <td className="font-mono-num font-bold text-right pr-2" style={{ width: "13%" }}>
                                              ×{Number(o.count) || 0}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </details>
                  )}

                  {preview.measurements?._ai_double_count_check && (
                    <div
                      className="text-[11px] text-[var(--ink-2)] italic border-l-2 border-[#0EA5E9] pl-3 mb-3"
                      data-testid="ai-measure-double-count"
                    >
                      <span className="not-italic font-bold text-[#0EA5E9] mr-1">Cross-check:</span>
                      {preview.measurements._ai_double_count_check}
                    </div>
                  )}
                  {preview.measurements && (preview.raw_ai?._reconciliation_error || preview.raw_ai?._parse_error) ? (
                    /* Register pin (ruled 2026-07-17): a failed/incomplete
                       reconcile NEVER renders zeros as editable readings —
                       empty/pending states render as empty, never 0 LF. */
                    <div
                      className="text-[11px] border-l-2 border-[#F59E0B] pl-3 mb-3 text-[var(--warning-text)]"
                      data-testid="ai-measure-lf-pending"
                    >
                      <span className="font-bold uppercase tracking-wider text-[10px]">Linear measurements pending</span>
                      <div className="mt-0.5 text-[var(--ink-2)]">
                        Reconciliation did not complete — no readings exist yet, so nothing
                        is shown as 0. Use Retry reconciliation; the fields unlock on a
                        successful run.
                      </div>
                    </div>
                  ) : preview.measurements && (
                    <details className="text-xs mb-3" open data-testid="ai-measure-lf-table">
                      <summary className="cursor-pointer text-[var(--ai)] font-bold uppercase tracking-wider">
                        Linear measurements — tap to edit
                      </summary>
                      <div className="text-[11px] text-[var(--muted)] mt-2 italic">
                        If the AI under-counted (often because not every elevation was photographed),
                        type the real numbers. ISS soffit / gutter / capping qty re-derives from these on Apply.
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {[
                          ["eaves_lf", "Eaves LF"],
                          ["rakes_lf", "Rakes LF"],
                          ["starter_lf", "Starter LF"],
                          ["outside_corner_lf", "Outside corner LF"],
                          ["inside_corner_lf", "Inside corner LF"],
                          ["opening_perimeter_lf", "Opening perimeter LF"],
                          ["window_count", "Window count"],
                          ["entry_door_count", "Entry door count"],
                          ["patio_door_count", "Patio door count"],
                          ["garage_door_count", "Garage door count"],
                        ].map(([key, label]) => (
                          <label key={key} className="flex flex-col text-[10px] text-[var(--muted)] uppercase tracking-wider">
                            <span className="mb-1">{label}</span>
                            <input
                              type="number"
                              step={key.endsWith("_count") ? "1" : "0.5"}
                              min="0"
                              value={preview.measurements[key] ?? ""}
                              placeholder="—"
                              onChange={(e) => setMeasurementField(key, e.target.value)}
                              className="px-2 py-1 border border-[var(--border)] font-mono-num text-sm text-[var(--ink)] normal-case"
                              data-testid={`ai-measure-lf-${key}`}
                            />
                          </label>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Iter 56: Raw AI JSON for debugging — collapsed by
                      default. Useful when the numbers look wrong and we
                      need to see exactly what Claude returned. */}
                  {(preview.raw_ai || preview.measurements) && (
                    <details className="text-xs mb-3" data-testid="ai-measure-raw-debug">
                      <summary className="cursor-pointer text-[var(--muted)] font-bold uppercase tracking-wider text-[10px] inline-flex items-center gap-1">
                        <ScanSearch aria-hidden="true" className="w-3 h-3" /> Show raw AI output (debug)
                      </summary>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold mb-1">
                            raw_ai (what Claude returned)
                          </div>
                          <pre className="bg-[var(--bar-bg)] text-[#22D3EE] p-2 text-[10px] overflow-auto max-h-64 whitespace-pre-wrap break-all" data-testid="ai-measure-raw-ai-json">
{JSON.stringify(preview.raw_ai || {}, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold mb-1">
                            measurements (post-aggregator)
                          </div>
                          <pre className="bg-[var(--bar-bg)] text-[#A78BFA] p-2 text-[10px] overflow-auto max-h-64 whitespace-pre-wrap break-all" data-testid="ai-measure-measurements-json">
{JSON.stringify(preview.measurements || {}, null, 2)}
                          </pre>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            JSON.stringify(
                              { raw_ai: preview.raw_ai, measurements: preview.measurements },
                              null,
                              2,
                            ),
                          );
                          toast.success("Raw AI output copied to clipboard");
                        }}
                        className="mt-2 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-[var(--surface)] border border-[var(--border)] hover:bg-[var(--surface-muted)]"
                        data-testid="ai-measure-copy-raw"
                      >
                        Copy to clipboard
                      </button>
                    </details>
                  )}


                  </>
                  )}
                </>
              )}
              </>)}
            </div>

            {/* SEND-246 — the footer row (Powered by · Calibrate Window
                Sizing · Tag Profiles · Run AI Measure · Close · Start Over,
                and the results buttons beside them) is HIDDEN, NOT DELETED:
                the whole row and every handler it calls stay on disk behind
                the one constant. The X in the dialog's header still closes
                the dialog. */}
            {MEASURE_TAIL_SHOWN && (
            <div className="border-t border-[var(--border)] px-5 py-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3 relative">
              {/* Iter 57h — inline calibration popover. Hidden by default;
                  pops up just above the Run button when the contractor
                  hits the "Calibrate window sizing" link. Stays out of
                  the way for the 80% of jobs that don't need it. */}
              {calibOpen && (
                <div
                  className="absolute bottom-full right-5 mb-2 bg-[var(--surface)] border border-[var(--ai)] shadow-xl p-3 min-w-[280px] z-10"
                  data-testid="ai-measure-course-sizing"
                  onMouseLeave={() => { /* keep open on hover-leave — user explicitly closes */ }}
                >
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--ai)] font-bold leading-tight">
                      Calibrate window sizing
                      <div className="text-[9px] text-[var(--muted)] font-normal mt-0.5">
                        Tell Claude the brick course or siding row height. Optional — leave blank for defaults.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCalibOpen(false)}
                      className="text-[var(--muted)] hover:text-[var(--ink)]"
                      title="Close"
                      data-testid="ai-measure-course-sizing-close"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-[9px] uppercase tracking-wider text-[var(--muted)] font-bold">Brick course (in)</span>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        className="input text-sm"
                        placeholder="8 = standard"
                        value={brickCourse}
                        onChange={(e) => setBrickCourse(e.target.value)}
                        data-testid="ai-measure-brick-course"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[9px] uppercase tracking-wider text-[var(--muted)] font-bold">Siding exposure (in)</span>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        className="input text-sm"
                        placeholder="D5=5, D6=6, CI=7"
                        value={sidingExposure}
                        onChange={(e) => setSidingExposure(e.target.value)}
                        data-testid="ai-measure-siding-exposure"
                      />
                    </label>
                  </div>
                  <div className="text-[9px] text-[var(--muted)] mt-2 italic">
                    Backend snaps every window to nearest standard size after Claude runs, regardless.
                  </div>
                  {/* Iter 79j.44 — Deep Dormer Scan removed. Two-phase
                      Phase A/B now owns dormer detection end-to-end
                      via the `dormers[]` array with per-face provenance.
                      The legacy roofline-crop scan was injecting corrupt
                      data (null opening_ids, wrong-wall face SF crediting,
                      hits on nonexistent walls). UI + request flag gone. */}
                </div>
              )}
              <div className="text-[10px] text-[var(--muted)] flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
                <span className="whitespace-nowrap">Powered by</span>
                {/* Iter 79j.15 — A/B model picker. Compact select so
                    contractors can flip models per-run without leaving
                    the modal. Persisted in localStorage. */}
                {isModelAdmin ? (
                  <select
                    value={modelChoice}
                    onChange={(e) => setModelChoice(e.target.value)}
                    className="text-[10px] font-bold uppercase tracking-wider bg-[var(--surface)] border border-[var(--border)] px-1 py-0.5 focus:outline-none focus:border-[var(--ai)]"
                    data-testid="ai-measure-model-select"
                    title="ADMIN bake-off picker. The validated production configuration is Claude Fable 5 — non-admin runs are clamped to it server-side (anchor integrity)."
                  >
                    {Object.entries(MODEL_PICKER_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}{k === VALIDATED_MODEL_KEY ? " ✓ validated" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)]"
                    data-testid="ai-measure-model-locked"
                    title="Runs use the validated production configuration (anchor integrity). Model selection is admin-only."
                  >
                    {MODEL_PICKER_LABELS[VALIDATED_MODEL_KEY]}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setCalibOpen((v) => !v)}
                  className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 whitespace-nowrap ${
                    (brickCourse || sidingExposure) ? "text-[var(--ai)]" : "text-[var(--muted)] hover:text-[var(--ai)]"
                  }`}
                  data-testid="ai-measure-course-sizing-toggle"
                  title="Tell Claude the brick course or siding row height (optional)"
                >
                  <Ruler className="w-3 h-3" />
                  {(brickCourse || sidingExposure) ? "Calibration on" : "Calibrate window sizing"}
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 min-w-0 [&_button]:whitespace-nowrap">
                <button
                  type="button"
                  className="px-3 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--bg-app)] text-xs font-bold uppercase tracking-wider"
                  onClick={closeAll}
                  disabled={busy}
                  data-testid="ai-measure-cancel"
                >
                  Close
                </button>
                {(preview || photoUrls.length > 0 || files.length > 0) && (
                  <button
                    type="button"
                    className="px-3 py-2 bg-[var(--surface)] text-[var(--danger-text)] border border-[#DC2626] hover:bg-red-50 text-xs font-bold uppercase tracking-wider"
                    onClick={() => setDestructiveConfirm({ kind: "start_over" })}
                    disabled={busy}
                    data-testid="ai-measure-start-over"
                    title="Wipe photos + AI result and start fresh"
                  >
                    Start Over
                  </button>
                )}
                {/* Iter 79j.69 — surgical photo swap. Previously there
                    was NO path to edit photos once a preview loaded
                    except the destructive Start Over. Clears only the
                    LOCAL preview so the picker/gallery re-render; photos,
                    markers and calibration stay; the completed run stays
                    in run history (Restore preview) and Tape Check keeps
                    its scores. */}
                {preview && photoUrls.length > 0 && (
                  <button
                    type="button"
                    className="px-3 py-2 bg-[var(--surface)] text-[var(--ink-2)] border border-[var(--border)] hover:bg-[var(--surface-muted)] text-xs font-bold uppercase tracking-wider"
                    onClick={() => { setPreview(null); setWallsDirty(false); }}
                    disabled={busy}
                    data-testid="ai-measure-adjust-photos"
                    title="Go back to the photo list to remove, replace or add photos — keeps everything else. The current result stays in run history."
                  >
                    Adjust Photos
                  </button>
                )}
                {preview && estimateId && (
                  <button
                    type="button"
                    onClick={downloadReportPdf}
                    disabled={reportBusy || busy}
                    className="px-3 py-2 bg-[var(--surface)] text-[#0EA5E9] border border-[#0EA5E9] hover:bg-[#F0F9FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="ai-measure-report-pdf-btn"
                    title="Download a branded HOVER-style measurement report (photos + confidence chips + openings schedule)"
                  >
                    {reportBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                    {reportBusy ? "Generating…" : "Report PDF"}
                  </button>
                )}
                {!preview ? (
                  <>
                    {photoUrls.length > 0 && estimateId && (
                      <button
                        type="button"
                        onClick={() => setProfileAnnotatorOpen(true)}
                        disabled={busy}
                        className="px-3 py-2 bg-[var(--surface)] text-[var(--brand-text)] border border-[var(--brand)] hover:bg-[#FFF7ED] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50 mr-1"
                        data-testid="ai-measure-tag-profiles-btn"
                        title="Draw boxes to tag Shake / B&B / etc. — guarantees those materials hit the quote"
                      >
                        Tag Profiles
                        {Object.entries(savedProfileAnnotations).filter(([k, v]) => !k.startsWith("_") && Array.isArray(v) && v.length > 0).length > 0 && (
                          <span className="bg-[var(--brand)] text-[var(--on-brand)] px-1 py-0 text-[9px]">
                            {Object.entries(savedProfileAnnotations).reduce((a, [k, v]) => a + (k.startsWith("_") ? 0 : (Array.isArray(v) ? v.length : 0)), 0)}
                          </span>
                        )}
                      </button>
                    )}
                    {(() => {
                      // Iter 79j.45 / 79j.46 — Health-aware Run button.
                      // Order of precedence: busy > uploading > health
                      // outage > normal. `ambiguous` NEVER disables the
                      // button — soft warning only, per rule (3): a
                      // broken health check must not lock the product.
                      //
                      // Iter 79j.46 — Red state is CLICKABLE. Clicking
                      // fires a forced health re-ping (bypasses the
                      // 45s client cache) so the user has a manual
                      // escape hatch: top up in a new tab → click the
                      // red button → tab focus event ALREADY fires a
                      // ping, but the manual click is the belt to
                      // that suspenders.
                      const budgetOut = aiHealth?.status === "budget_exceeded";
                      const svcOut = aiHealth?.status === "unavailable";
                      const isRed = budgetOut || svcOut;
                      let label;
                      if (busy) {
                        label = busyStage === "claude" ? "Claude vision…"
                          : busyStage === "dormer_scan" ? "Deep dormer scan…"
                          : busyStage === "aggregating" ? "Aggregating walls…"
                          : busyStage === "mapping" ? "Mapping to catalog…"
                          : busyStage === "starting" ? "Starting…"
                          : "Analyzing…";
                      } else if (files.length > 0) {
                        label = "Uploading…";
                      } else if (budgetOut) {
                        label = "Budget exhausted — click to re-check";
                      } else if (svcOut) {
                        label = "AI service unavailable — click to re-check";
                      } else {
                        label = "Run AI Measure";
                      }
                      const cls = isRed
                        ? "px-3 py-2 bg-[var(--danger)] text-white hover:bg-[#B91C1C] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer disabled:opacity-70"
                        : "px-3 py-2 bg-[var(--ai)] text-white hover:bg-[#6D28D9] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50";
                      const onClickHandler = isRed
                        ? () => refreshAiHealth({ force: true })
                        : runMeasure;
                      return (
                        <button
                          type="button"
                          onClick={onClickHandler}
                          // Iter 79j.46 — Red state stays clickable for
                          // the re-check escape hatch. Only truly
                          // disabled during a live run / upload / with
                          // zero photos. `data-health-status` lets QA
                          // & automation drive the recovery path.
                          disabled={busy || photoUrls.length === 0 || files.length > 0}
                          className={cls}
                          data-testid="ai-measure-run-btn"
                          data-health-status={aiHealth?.status || "unknown"}
                          title={isRed ? "Click to re-check the AI service health" : undefined}
                        >
                          {busy
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : isRed
                              ? <RotateCcw className="w-3.5 h-3.5" />
                              : <Upload className="w-3.5 h-3.5" />}
                          {label}
                        </button>
                      );
                    })()}
                    {/* SEND-181 ITEM 2 (Howard ruled 2026-08-14) — THE
                        BANNER THE CODE HAS ALWAYS SET AND NO SCREEN EVER
                        SAID. `setNoPhotosBanner(true)` has been called on
                        the no-photo run refusal since Iter 79j.29, whose
                        own comment says a toast is invisible behind this
                        modal and that the prior behaviour "looked exactly
                        like 'the Re-Run button is broken'". It was right,
                        and it stayed broken, because the banner was
                        rendered nowhere.

                        THE RUN BUTTON ITSELF CANNOT TRIGGER IT — it is
                        disabled at zero photos. The refusal is reachable
                        from four OTHER callers: the wizard's auto-run, the
                        Re-run confirm, the run-error retry and the
                        missing-walls "Run anyway". All four land back on
                        this dialog, which is why the banner belongs here,
                        under the button he was reaching for.

                        THE SENTENCE IS THE SERVER'S — the same one the
                        hand door shows, so one door has one wording. Where
                        the estimate DOES carry photos and only this run's
                        list was empty, that is a DIFFERENT fact and it is
                        named as such; the SEND-181 report flags that second
                        line as the one sentence written here, because no
                        route writes it. */}
                    {noPhotosBanner && (
                      <div
                        className="mt-2 w-full px-2 py-1.5 bg-[#FEF3C7] border border-[#F59E0B] text-[10px] font-bold leading-snug text-[var(--warning-text)]"
                        data-testid="ai-measure-no-photos-banner"
                      >
                        {noAiPhotos.length === 0 && noAiNote
                          ? noAiNote
                          : "This run had no photo in its list, though the estimate carries photos — reopen the photos above and try again. Nothing was measured and nothing was invented."}
                      </div>
                    )}

                  </>
                ) : (
                  <>
                    {/* Advanced tools toggle — gates Refine on Photo.
                        Pre-AI annotations (Iter 56) cover most use cases;
                        Refine is the manual-measure escape hatch. */}
                    <button
                      type="button"
                      onClick={() => setShowAdvanced((v) => !v)}
                      className={`px-2 py-2 text-[10px] font-bold uppercase tracking-wider border ${
                        showAdvanced
                          ? "bg-[var(--surface-muted)] text-[var(--ink-2)] border-[var(--muted)]"
                          : "bg-[var(--surface)] text-[var(--muted)] border-[var(--border)] hover:text-[var(--ink-2)]"
                      } mr-1`}
                      data-testid="ai-measure-advanced-toggle"
                      title="Show / hide the Refine on Photo manual-measure tool"
                    >
                      {showAdvanced ? "Hide" : "Advanced"}
                    </button>
                    {/* Amber nudge — Refine/annotate edits only reach the
                        measurements on Re-run. Shown until the next
                        successful run folds them in. */}
                    {annotDirtySinceRun && (
                      <span
                        className="px-2.5 py-2 bg-[#FEF3C7] border border-[#F59E0B] text-[var(--warning-text)] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5"
                        data-testid="ai-measure-annot-dirty-chip"
                        title="You edited photo annotations after the last run. Click Re-run so the measurements fold them in."
                      >
                        <AlertTriangle aria-hidden="true" className="w-3.5 h-3.5" />
                        Annotations changed — Re-run to fold them in
                      </span>
                    )}
                    {/* Iter 79j.16 — Re-run button so contractors can
                        A/B a different model on the SAME photos without
                        hitting Start Over. Uses the current model in
                        the "Powered by" dropdown. */}
                    <button
                      type="button"
                      onClick={attemptRerun}
                      disabled={busy || photoUrls.length === 0 || files.length > 0}
                      className="px-3 py-2 bg-[var(--ai)] text-white hover:bg-[#6D28D9] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="ai-measure-rerun-btn"
                      title="Run the measure again — usually with a different model in the Powered by dropdown to A/B compare accuracy"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {busy
                        ? (busyStage === "claude" ? "Vision…"
                          : busyStage === "dormer_scan" ? "Dormer scan…"
                          : busyStage === "aggregating" ? "Aggregating…"
                          : busyStage === "mapping" ? "Mapping…"
                          : busyStage === "starting" ? "Starting…"
                          : "Running…")
                        : "Re-run"}
                    </button>
                    {/* SEND-139 — "REFINE ON PHOTO" IS RETIRED. It was a
                        door into the old annotator as a DRAWING tool; the
                        gable and dormer tools it was mostly used for now
                        live in Photo Takeoff, where a confirmed mark
                        carries a quantity instead of only feeding a read.
                        Advanced Tools stays for the debug view. */}
                    {/* Iter 79j.36 — Debug view button. Advanced-only.
                        Opens a two-column modal: per-photo raw
                        observations on the left, reconciled house
                        JSON with provenance on the right. Only
                        useful once a preview is loaded — pre-preview
                        state is diagnosed with the upload grid
                        itself. */}
                    {showAdvanced && preview && (
                      <button
                        type="button"
                        onClick={() => setDebugOpen(true)}
                        className="px-3 py-2 bg-[var(--surface)] text-[var(--ai)] border border-[var(--ai)] hover:bg-[var(--ai-soft)] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                        data-testid="ai-measure-debug-btn"
                        title="Show per-photo raw observations and the reconciled house JSON with provenance"
                      >
                        <Bug className="w-3.5 h-3.5" />
                        Debug
                      </button>
                    )}
                    {/* Iter 78z+++ — Tag Profiles also accessible from
                        the preview footer so contractors can correct
                        a missed Shake / B&B / dormer AFTER seeing the
                        AI's first pass. Mirrors the pre-run button. */}
                    {photoUrls.length > 0 && estimateId && (
                      <button
                        type="button"
                        onClick={() => setProfileAnnotatorOpen(true)}
                        disabled={busy}
                        className="px-3 py-2 bg-[var(--surface)] text-[var(--brand-text)] border border-[var(--brand)] hover:bg-[#FFF7ED] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50 mr-1"
                        data-testid="ai-measure-tag-profiles-btn-preview"
                        title="Draw boxes to tag Shake / B&B / Dormer — guarantees those materials hit the quote even if AI missed them"
                      >
                        Tag Profiles
                        {Object.entries(savedProfileAnnotations).filter(([k, v]) => !k.startsWith("_") && Array.isArray(v) && v.length > 0).length > 0 && (
                          <span className="bg-[var(--brand)] text-[var(--on-brand)] px-1 py-0 text-[9px]">
                            {Object.entries(savedProfileAnnotations).reduce((a, [k, v]) => a + (k.startsWith("_") ? 0 : (Array.isArray(v) ? v.length : 0)), 0)}
                          </span>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        printTakeoff({
                          source: "AI Photo Measure",
                          measurements: preview?.measurements || {},
                          lines: preview?.lines || [],
                          openings:
                            preview?.measurements?._ai_openings ||
                            preview?.raw_ai?.openings ||
                            [],
                          est: {
                            customer_name: "",
                            address: address || "",
                            estimate_number: estimateId ? estimateId.slice(0, 8) : "Draft",
                          },
                          kind: kind || "siding",
                        })
                      }
                      disabled={busy || !preview}
                      className="px-3 py-2 bg-[var(--surface)] text-[#0EA5E9] border border-[#0EA5E9] hover:bg-[#F0F9FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="ai-measure-print-btn"
                      title="Print this AI takeoff preview"
                    >
                      <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                    {confidenceGate && (
                      <div
                        className="w-full border-2 border-[#DC2626] bg-red-50 p-3 text-xs text-left"
                        data-testid="confidence-gate-banner"
                      >
                        <div className="font-bold uppercase tracking-wider text-[#DC2626] flex items-center gap-1.5 mb-1">
                          <AlertTriangle className="w-4 h-4" />
                          Low-confidence reads — confirm before these drive the quote
                        </div>
                        <ul className="list-disc ml-5 space-y-0.5 text-[#7F1D1D]">
                          {(confidenceGate.substituted_walls || []).map((s, i) => (
                            <li key={`sub-${i}`} data-testid="gate-substituted-wall">
                              <b>{s.label}</b>: junk read {s.read_ft} ft REPLACED with {s.substituted_ft} ft (story default) — tape this wall or accept the substitute
                            </li>
                          ))}
                          {(confidenceGate.low_confidence_walls || []).map((s, i) => (
                            <li key={`low-${i}`} data-testid="gate-low-confidence-wall">
                              <b>{s.label}</b>: model confidence {Math.round(s.confidence)}/100 — {s.reasoning || "barely visible / inferred"}
                            </li>
                          ))}
                          {confidenceGate.scale_confidence === "low" && (
                            <li data-testid="gate-low-scale">Scale confidence LOW — every ft² above rides on an uncertain scale reference</li>
                          )}
                        </ul>
                        <label className="flex items-center gap-2 mt-2 font-bold text-[#7F1D1D] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={gateAck}
                            onChange={(e) => setGateAck(e.target.checked)}
                            data-testid="confidence-gate-ack"
                          />
                          I reviewed the flagged reads — the siding, corner and starter rows they drive may be wrong until verified
                        </label>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={apply}
                      disabled={busy || gateBlocked || (() => {
                        // Iter 79j.52 — Visually mirror the runtime
                        // hard-block in apply(). Signals live in
                        // raw_ai (walls/dormers/openings arrays) and
                        // measurements.siding_sqft / eaves_lf — NOT
                        // in measurements.walls (that field is never
                        // populated by the aggregator).
                        const ra = preview?.raw_ai || {};
                        if (ra._reconciliation_error) return true;
                        const raWalls = Array.isArray(ra.walls) ? ra.walls.length : 0;
                        const raDormers = Array.isArray(ra.dormers) ? ra.dormers.length : 0;
                        const raOpenings = Array.isArray(ra.openings) ? ra.openings.length : 0;
                        const m = preview?.measurements || {};
                        const sidingSqft = Number(m.siding_sqft || 0);
                        const eavesLf = Number(m.eaves_lf || 0);
                        const hasFootprint =
                          raWalls > 0 || raDormers > 0 || raOpenings > 0 ||
                          sidingSqft > 0 || eavesLf > 0;
                        // BLOCK A (SEND-136) — A STALE READ CANNOT BE
                        // APPLIED. This run was taken before the
                        // no-photo-no-wall rule: its house-level runs
                        // (eaves, rakes, corners, footprint) were summed
                        // over faces no photo showed and cannot be pulled
                        // apart afterwards. Re-run the read, or draw the
                        // missing faces on a photo in the takeoff editor.
                        if (m._face_rule_stale) return true;
                        return !hasFootprint;
                      })()}
                      className="px-3 py-2 bg-[var(--brand)] text-[var(--on-brand)] hover:bg-[var(--brand-hover)] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="ai-measure-apply-btn"
                      title={preview?.measurements?._face_rule_stale
                        ? (preview.measurements._face_refusal_note || "This read predates the no-photo-no-wall rule — re-run it.")
                        : preview?.raw_ai?._reconciliation_error
                          ? "Reconciliation failed — nothing to apply. Retry Phase B first."
                          : "Apply the AI takeoff to the estimate"}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      {busy ? "Saving…" : "Apply Measurements"}
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      {/* SEND-131A — PHOTO TAKEOFF: marks on the photo itself. Mounted
          beside the annotator, never in place of it. */}
      {takeoffOpenFor && (
        <PhotoTakeoffEditor
          /* SEND-166 item 1 — THE RESET IS STRUCTURAL, NOT ASSERTED. The
             photo key IS the React key, so switching photos UNMOUNTS this
             editor and mounts a fresh one: photo B cannot inherit photo A's
             scale, plane, trim or marks, because there is no instance left
             to inherit them from. Swapping the photo inside one live
             component is the shape that leaks state, and this is not it. */
          key={takeoffOpenFor}
          est={{ id: estimateId }}
          photoKey={takeoffOpenFor}
          photoUrl={`/api/uploads/${takeoffOpenFor}`}
          noAi={takeoffNoAi}
          photos={noAiPhotos}
          onSwitch={(key) => openTakeoff(key)}
          /* SEND-226 — the photo write mutates `lines` on the server, so the
             page behind this editor must re-read them. */
          onWrote={onRederived || undefined}
          onClose={() => {
            // SEND-188 — CLOSING LANDS ON THE ONE SCREEN, which is open
            // behind this editor. It used to re-open the no-AI picker,
            // and that picker is the retired door's screen: sending him
            // back to it would put the second way in back on the page
            // through the back door.
            setTakeoffOpenFor(null);
            setTakeoffNoAi(false);
            loadNoAiPhotos();
            loadPhotoPrefs();
          }}
        />
      )}

      {/* SEND-139 — THE ANNOTATOR IS NO LONGER MOUNTED HERE. Nothing on
          this screen opens it as a drawing UI. It survives as the import
          source Photo Takeoff pulls from.
          SEND-204 (Howard ruled 2026-08-14) — AND THE GUIDED CAPTURE WIZARD
          IS NO LONGER MOUNTED EITHER. Marks drawn in it never appeared in
          Photo Takeoff; the two screens are not being wired together. The
          walk moved onto Photo Takeoff — one editor, one canvas, one step at
          a time, each step writing its own layer. THE FILE STAYS ON DISK;
          the door, the mount and the import are gone, so nothing on this
          screen can open it. Run AI Measure is untouched: it reads the
          photos the ordinary file input uploads. */}

      {/* SEND-204 — THE PRE-CAPTURE CALIBRATION PROMPT IS RETIRED WITH THE
          WIZARD IT FRONTED: its only two exits opened that walk, and both
          doors to it are gone. The same figure is still typed on this
          dialog's own calibration row (the siding exposure / brick course
          inputs), so nothing he could set here was taken away. */}
      {/* Iter 79j.36 — Extraction Debug modal (Advanced only). */}
      {debugOpen && (
        <AIExtractionDebugModal
          preview={preview}
          photoUrls={photoUrls}
          estimateId={estimateId}
          onClose={() => setDebugOpen(false)}
        />
      )}
      {/* Iter 78z — Profile Annotator (Tag Shake / B&B / etc.)
          Iter 79j.63 — Wrapped in an ErrorBoundary. Any render crash
          inside the annotator (e.g. the Jul 7 2026 scale_ref schema
          mismatch that crashed via `.toFixed` on undefined) now
          surfaces a recoverable dialog instead of unmounting the
          entire estimate editor. */}
      {profileAnnotatorOpen && estimateId && (
        <AnnotatorErrorBoundary onClose={() => setProfileAnnotatorOpen(false)}>
          <ProfileAnnotator
            estimateId={estimateId}
            photos={photoUrls.map((name, i) => ({
              url: `/api/uploads/${name}`,
              label: photoAnnotations[name]?.elevation || `#${i + 1}`,
            }))}
            initialAnnotations={savedProfileAnnotations}
            defaultElevationByIdx={photoUrls.map((name) => photoAnnotations[name]?.elevation || "other")}
            /* Iter 78z+++ — thread the Wall Scale Anchor from PhotoAnnotateModal
               so the contractor doesn't have to set scale twice. */
            wallScaleRefByPhotoKey={Object.fromEntries(
              photoUrls.map((name, i) => [String(i), photoAnnotations[name]?.reference || null])
            )}
            onClose={() => setProfileAnnotatorOpen(false)}
            onSaved={(saved) => {
              setSavedProfileAnnotations(saved);
            }}
            onSaveAndRerun={currentRunId ? async () => {
              rerunWithAnnotations();
            } : null}
          />
        </AnnotatorErrorBoundary>
      )}
      {/* Iter 79i (Phase 4) — Missing-wall pre-flight modal. Fires
          before runMeasure() if fewer than 4 primary walls are covered.
          Contractor can go back to capture more OR click "Run anyway"
          to bypass (which sets the bypass flag so the modal doesn't
          re-fire on retry). */}
      {missingWallsModal && missingWallsModal !== "bypassed" && (
        <div
          className="fixed inset-0 z-[60] bg-[var(--bar-bg)]/70 flex items-center justify-center p-4"
          data-testid="ai-measure-missing-walls-modal"
        >
          <div className="bg-[var(--surface)] max-w-md w-full p-6 shadow-2xl">
            <div className="text-xs uppercase tracking-wider text-[var(--brand-text)] font-bold mb-2">
              ⚠️ Missing walls
            </div>
            <h3 className="text-lg font-bold text-[var(--ink)] mb-2">
              Only {4 - missingWallsModal.missing.length} of 4 primary walls captured
            </h3>
            <p className="text-sm text-[var(--ink-2)] mb-3">
              Missing: <b className="text-[var(--brand-text)]">{missingWallsModal.missing.join(", ")}</b>
            </p>
            <p className="text-sm text-[var(--ink-2)] mb-4">
              AI Measure will estimate the missing walls from photos of adjacent
              elevations, which drops accuracy by 10-20%. If the side yard is
              inaccessible, run anyway — otherwise, add the missing shots.
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              {/* SEND-204 — this was the third door to the retired wizard.
                  The way to add the missing shots is the file input on this
                  same dialog; no new door was invented for it.
                  SEND-213 — it names the + TILE now, because that is where
                  the control moved when the big add box died. */}
              <span className="px-3 py-2 text-[10px] text-[var(--muted)] uppercase tracking-wider"
                data-testid="ai-measure-missing-walls-add-photos-note">
                add the missing shots with the + tile in the photo list above
              </span>
              <button
                type="button"
                onClick={() => {
                  setMissingWallsModal(null);
                  // Iter 79j.20 — explicit bypass param instead of the
                  // "bypassed" state sentinel. Avoids the stale-closure
                  // bug where setTimeout captured the old runMeasure
                  // and re-fired the guard.
                  runMeasure({ bypassMissingWallsGuard: true });
                }}
                className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] text-[var(--muted)] hover:bg-[var(--surface-muted)] text-xs font-bold uppercase tracking-wider"
                data-testid="ai-measure-missing-walls-run-anyway"
              >
                Run anyway
              </button>
              <button
                type="button"
                onClick={() => setMissingWallsModal(null)}
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] hover:text-[var(--muted)]"
                data-testid="ai-measure-missing-walls-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
